// src/expenseImport.js
// ─────────────────────────────────────────────────────────────
// Expense import engine (10/9/26) — pure, no React, no network.
//
// Reece: "upload large invoice data and sheets and ensure that all of it is
// reflected in the marketing sheet … if it's a duplicate or something like
// that, just flag those for me to confirm or delete after they have been
// input into the system."
//
// The pipeline:
//   file → readSheetFile / parseCSV / readXlsxSheets → rows (string[][]) —
//        an .xlsx: EVERY sheet (sheetCandidates → workbookCandidates)
//        → detectSheet(rows)  "loanflowReady" | "amazonBusiness" | "invoiceTracker"
//          | "invoiceLog" | "itemDetails" | "generic" | "unknown"
//        → amazonCandidates / trackerCandidates (the tracker AND the invoice
//          export's Invoice Log) / genericCandidates / readyCandidates;
//          itemDetailReceipts → joinItemDetails puts item lines (the
//          export's "Itemized Details") on the rows they belong to (10/10/26)
//   receipt file → (src/receiptReader.js, Claude) → receiptCandidates
//   all candidates → planImport(candidates, existingExpenses, {deletedKeys})
//        → rows to show (+ `stale`: rows ALREADY in Expenses to flag)
//   rows Reece leaves ticked → finalizeImport (pair flags re-checked among
//        the ticked rows) → buildExpenses → mergeImported (id guard),
//        applyStale / attachExistingReceipts on the rows already there
//
// Standing rules (Reece):
//   • failed / declined payments are NEVER imported;
//   • cancelled orders are never imported;
//   • rows with no amount (estimates, proofs, reminders) are dropped — EXCEPT
//     a receipt file Claude couldn't read (or the pages of one it read only
//     in part), which comes in at $0, flagged, so nothing he uploaded is
//     silently lost;
//   • the exact same order / email / receipt file already in Expenses (its
//     source key is stamped in that expense's notes) is skipped silently.
//
// One drop, the fewest flags (10/9/26 — Reece: "I just wanted you to be able
// to upload all of these to have it updated in the system. Obviously, things
// like a controller, pet, or grocery can be excluded."). Every OBVIOUS case
// is decided here, and listed (planImport's `excluded`, grouped by
// leftOutGroups) with "Include anyway" (opts.include) — nothing is silently
// lost:
//   • personal items (pet, grocery, health & beauty, toys / games, apparel…)
//     are LEFT OUT — the rest of the order keeps its exact share; so are
//     personal services (PERSONAL_SERVICES: a Kill Tony ticket, streaming, a
//     pet store) in the tracker / a card export, a receipt whose item lines
//     are ALL personal (a mixed one comes in, flagged), and a refund for
//     items that were left out;
//   • an Amazon email (or receipt) whose total is EXACTLY an order / that
//     day's orders in the report, or that names an order in it, is MERGED
//     into the order (its file and source key go on the order's rows);
//   • a receipt that exactly matches a row of this upload (same vendor, to
//     the cent, ±2 days or the same order / transaction number) is MERGED
//     into that row as its receipt;
//   • the same tracker email listed twice is kept once;
//   • an exact copy of an expense already in Expenses (same vendor, to the
//     cent, ±3 days, one-to-one) is skipped — never one that is already
//     ANOTHER row of this (cumulative) upload, or another Amazon order.
//
// His hand-entered rows (no import stamp — Reece's decisions, 10/9/26):
//   • a blank vendor on one is read from its description when matching
//     (matchVendorOf: "USPS stamps" → USPS, "Claude Max plan" → Anthropic) —
//     never written back;
//   • POSTAGE — "Receipts are the record": every USPS / Stamps.com charge
//     (or Postage purchase) comes in, never skipped or flagged against a
//     hand-entered postage row; instead his hand-entered postage rows inside
//     the receipts' dates (±7 days) are listed in plan.replaced and flagged
//     at import ("Replaced by the imported USPS/Stamps.com receipts …"), so
//     Select the replaced rows → Delete selected clears them in one click;
//   • SOFTWARE — "Keep my recurring entries": a Software/Subscriptions row
//     from a vendor he already logs by hand (his own Software rows) is left
//     out ("handLogged", Include anyway) — its receipt file goes on his
//     nearest hand entry of that vendor (±15 days, none stored yet);
//     software vendors he doesn't log yet come in as usual;
//   • TYPED ROUNDED — he types "$60" for a $60.63 receipt, "$450" for a
//     $447.91 order: a hand-entered row within max($5, 1.5%), ±14 days, same
//     vendor (or same item type / subcategory when a side has no vendor)
//     makes the upload row a "Possible duplicate of your hand entry …"
//     (imported, flagged — rule a's ±1¢ never paired them);
//   • the MAILHOUSE (10/9/26, later) — AlphaGraphics is Marketing → Postage,
//     and its receipts replace his hand-entered mailhouse rows in their
//     dates, exactly like USPS / Stamps.com and his postage rows;
//   • …or a software vendor's INVOICES instead of his hand entries (10/10/26,
//     later — his Claude Max invoices from Anthropic): the preview offers,
//     per vendor he logs by hand, "Use these invoices instead of my hand
//     entries for <vendor> (<dates>)" (opts.useInvoices). Chosen, they come
//     in (copies of one invoice kept once — invoiceCopies) and his hand rows
//     of that vendor within 15 days of one are flagged "Replaced by the
//     imported invoices for <vendor> …", like postage. Not chosen: left out.
//
// A receipt whose lines fall in different categories (a Vistaprint order of
// business cards + a mouse pad) is split by category like an Amazon order
// (splitReceipt) and matched WHOLE everywhere it is matched.
//
// The LoanFlow ready file (readyFileCsv / readyCandidates, 10/9/26 later):
// everything an import would add, already decided, as one Excel-friendly
// CSV Reece may edit; read back, its rows keep their keys and come in as he
// left them, the PDFs it names attach by name (attachReadyFiles — never
// read), and only the live-table rules run again. Its manifest line(s) say
// what it was made with, so a row he deletes in Excel stays out — and Add
// remembers it (importDecisions → recordImportDecisions), with the Amazon
// emails left out with their order, so a later drop of the cumulative
// tracker brings them back unticked, never as new. In one upload with the
// original files, the ready file wins.
//
// What was bought (10/9/26, src/expenseItems.js): every row with line items
// gets ONE notes line — "Items: Paper $53.98 (2 · 200 sheets) · Envelopes
// $68.98 (1 · 500 envelopes)" — the parts summing exactly to the row. The
// items: an Amazon order's lines (per split row, only its own), a receipt's
// item lines (also onto the tracker email / sheet row it is merged into), a
// generic sheet's description, a tracker email's subject (never an Amazon
// "Ordered:" email or a store's "Here's your receipt"; never an Amazon
// order confirmation or any receipt whose titles are cut short with "…" —
// "Southworth Z550CK 25% Cotton Line..." is 65 lb coverstock, not paper:
// `trustedLines`). An Amazon refund takes its order's lines (the whole
// order, or exactly one line) — also when the refund was imported before
// (backfill). An expense ALREADY
// in Expenses that this upload matches (imported before, an exact copy, a
// receipt for it) and that has no Items line gets one from the upload's
// line items when their total is its amount to the cent (plan.backfill →
// applyItemBackfill); nothing else on it changes.
// (10/10/26, later) Items lines name the KIND too ("Paper [Cotton (25–100%)]
// $53.98 …", expenseItems VARIETIES). A row imported before kinds existed,
// matched by its import key, gets the upload's line — "product detail" —
// only when it has exactly the same types at exactly the same per-type
// amounts and the upload adds a kind or a count (detailUpgrade →
// {replace: DETAIL_REPLACE, was}); a line he changed is never touched.
// (10/10/26, later still) …and the BRAND of paper / envelopes / cardstock
// ("Paper [Cotton (25–100%) · Southworth]", "[… · Non-Southworth]") — from
// the title, or the Amazon report's Brand column when the title doesn't say
// (two of his Southworth titles don't); a brand alone is product detail.
// Non-Southworth only when a brand is NAMED (the title or the Brand column —
// 10/10/26 review: Staples' FINE LINEN / RESUME PPR short names name none —
// since 10/11/26 they are Southworth, Reece's word: expenseItems isSouthworthText).
// purchaseDetail(ex, list) reads what's behind one purchase for Expenses'
// panel: its stamp lines (the Amazon order + its page on Amazon, the
// invoice email, receipt files, merged copies), the split order's other
// rows, refunds tied to it, its files — read-only.
// Only genuine doubts are imported AND flagged: a near match (a cent off,
// 4–15 days, a missing or different vendor), a bill or notice, a charge
// followed by a "payment failed / due" notice, not charged yet, a refund,
// receipt read problems, a quote.
//
// Flags live IN THE EXPENSE'S NOTES as lines starting with REVIEW_PREFIX
// ("⚑ Review: "), so no DB column / migration is needed; "Keep" in Expenses
// strips just those lines (clearReviewFlags), "Delete" is the normal delete.
// The source stamp is one line "Imported from <source> · <key>"; the old
// tracker stamp ("Imported from invoice tracker · <file_path>") still reads
// as key "tracker:<file_path>", so a re-run never re-imports those rows.
//
// Everything here is linear or n·log n so a sheet with tens of thousands of
// rows doesn't freeze the tab (the Amazon subset match only ever looks at
// the orders of one day ±1).
// ─────────────────────────────────────────────────────────────

import { nextExpenseId, expenseReceiptPath, csvFormulaGuard, safeStorageName } from './lib';
import { itemTypeOf, isPostalService, brandOf, columnBrand, SOUTHWORTH, stampLineFacts, POSTAGE_KINDS, packCountOf, itemParts, mergeItemParts, formatItemsLine, itemsLine, hasItemsLine, withItemsLine, replaceItemsLine, vendorGroupOf, isStoreGroup, canonicalType, parseItemsLine, isMarketingItem, fallbackItemType, expenseItemParts, expenseVendorGroup, subcategoryText, canonicalSubcategory, itemDiscountLine, spreadUntaxedPostage, unitNameOf, NAMED_SOFTWARE, ITEM_OTHER, NO_DETAIL, OTHER_MARKETING } from './expenseItems';

// ── Constants ────────────────────────────────────────────────

var CATEGORIES=["Marketing","Software/Subscriptions","Travel & Meals","Office/Equipment","Licensing & Education","Other"];
var REVIEW_PREFIX="⚑ Review: ";
// The stamp the first importer wrote (rows imported before 10/9 carry it).
var IMPORT_TAG="Imported from invoice tracker · ";
var STAMP_START="Imported from ";
var STAMP_SEP=" · ";
var SOURCE_LABELS={amazon:"Amazon Business report",tracker:"invoice tracker",sheet:"spreadsheet",receipt:"receipt"};
var SOURCE_BADGES={amazon:"Amazon order",tracker:"Tracker email",sheet:"Sheet",receipt:"Receipt PDF"};
var KEY_RE=/^(amazon|tracker|receipt|sheet):/;
var AMAZON_ORDER_RE=/\b(\d{3}-\d{7}-\d{7})\b/g;

// Tracker rules (moved here from InvoiceImport.jsx — the one copy).
var FAILED=/fail|declin|unsuccessful|past due|overdue|payment is due|action required|update required|could not be processed|problem with your payment/i;
// A bill, renewal reminder or statement notice states an amount owed, not an
// amount paid — the actual charge (if it happened) has its own receipt email.
var NOTICE_SOURCE=/amount due|renewal price|balance due/i;
var NOTICE_SUBJECT=/statement available|will renew|renewal (notice|reminder)|upcoming (payment|charge|renewal)|new invoice|invoice (is )?available|payment is due/i;
// An email that says the charge went through (a failure notice after it is
// about another attempt, not this one).
var PAID_SUBJECT=/payment (received|successful|confirmed|confirmation)|\breceipt\b|\bpaid\b|thank you for (your )?(payment|order|purchase)|purchase confirmation|order confirmation/i;

var FLAG={
  unreadable:"Couldn't read this receipt — open it and fill in the amount",
  noTotal:"Couldn't read the amount on this receipt — open it and fill it in",
  refund:"Refund — check it's against a purchase that's in here",
  support:"Support email, not a receipt — may duplicate the refund receipts",
  repeat:"Listed twice in the invoice tracker (the same email) — delete one copy",
  noDate:"No date on the receipt — check the date",
  noSheetDate:"No date in the sheet — check the date",
  quote:"Quote/estimate, not a purchase — delete unless it was paid",
  notReceipt:"Doesn't look like a receipt — delete unless it's an expense",
  bill:"Invoice/bill, not proof of payment — the payment may have its own receipt",
  partial:"Part of this file couldn't be read",
  pageRepeat:"May be the same receipt read twice (the file was read in parts) — delete one copy if so",
  noVendor:"No vendor in this sheet — check the vendor and category",
  noItems:"No item description in this sheet — check the category",
  rereadPrefix:"This file was only partly read when it was imported before",
  deletedPrefix:"You deleted this after an earlier import",
  // (10/9/26 fix pass) remembered at Add — importDecisions
  removedPrefix:"You took this out of the ready file",
  leftBeforePrefix:"Left out at an earlier import",
  // (10/10/26) two rows' receipt files are the same image
  sameImagePrefix:"Its receipt file is the same image as another row's"
};
// Postage: the receipts are the record (Reece, 10/9/26). A hand-entered
// postage row inside the imported receipts' dates is flagged with this (it
// starts every such flag — the dates follow), and Keep on it leaves the
// REPLACED_KEPT line, so a later import never flags it again.
var REPLACED_PREFIX="Replaced by the imported USPS/Stamps.com receipts";
var REPLACED_KEPT="Kept as a postage purchase that isn't among the imported USPS/Stamps.com receipts";
// …and the mailhouse (10/9/26, later — "Can you also include the mailhouse
// fees under postage?"): his hand-entered mailhouse rows inside the
// imported AlphaGraphics receipts' dates, the same way.
var REPLACED_MAILHOUSE_PREFIX="Replaced by the imported AlphaGraphics mailhouse receipts";
var REPLACED_MAILHOUSE_KEPT="Kept as a mailhouse charge that isn't among the imported AlphaGraphics mailhouse receipts";
// …and software he logs by hand (10/10/26, later — his Claude Max invoices
// from Anthropic): when he chooses "Use these invoices instead of my hand
// entries for <vendor>" in the preview, his hand-entered Software /
// Subscriptions rows of that vendor near the invoices' dates get this flag,
// the same way (the vendor's name follows the prefix).
var REPLACED_INVOICES_PREFIX="Replaced by the imported invoices for";
var REPLACED_INVOICES_KEPT="Kept as a hand-logged charge that isn't among the imported invoices";
// The "receipts are the record" families: prefix of the ⚑ flag, the "kept"
// comment Keep leaves, and the end of the flag.
var REPLACED_KINDS=[
  {kind:"postage",prefix:REPLACED_PREFIX,kept:REPLACED_KEPT,tail:"delete this hand entry unless it's a postage purchase that isn't among them"},
  {kind:"mailhouse",prefix:REPLACED_MAILHOUSE_PREFIX,kept:REPLACED_MAILHOUSE_KEPT,tail:"delete this hand entry unless it's a mailhouse charge that isn't among them"},
  {kind:"invoices",prefix:REPLACED_INVOICES_PREFIX,kept:REPLACED_INVOICES_KEPT,tail:"delete this hand entry unless it's a charge that isn't among them"}
];
var REPLACED_BY={};REPLACED_KINDS.forEach(function(k){REPLACED_BY[k.kind]=k;});

// ── Small helpers ────────────────────────────────────────────

function oneLine(s){return String(s==null?"":s).replace(/\s+/g," ").trim();}
function clipWords(s,n){
  var t=oneLine(s);if(t.length<=n)return t;
  var c=t.slice(0,n-1);var sp=c.lastIndexOf(" ");
  if(sp>n*0.6)c=c.slice(0,sp);
  return c.replace(/[\s,;:\-–—(]+$/,"")+"…";
}
function cents(n){return Math.round((Number(n)||0)*100);}
function round2(n){return Math.round((Number(n)||0)*100)/100;}
function fmtMoney(n){
  n=Number(n)||0;
  var s=Math.abs(n).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g,",");
  return(n<0&&Math.abs(n)>=0.005?"-":"")+"$"+s;
}
function baseName(p){return String(p||"").split(/[\\/]/).pop();}
function pad2(n){return String(n).padStart(2,"0");}
function todayIso(){var d=new Date();return d.getFullYear()+"-"+pad2(d.getMonth()+1)+"-"+pad2(d.getDate());}
function dayNum(iso){var m=/^(\d{4})-(\d{2})-(\d{2})$/.exec(iso||"");return m?Math.round(Date.UTC(+m[1],+m[2]-1,+m[3])/86400000):null;}
function dayDiff(a,b){var x=dayNum(a),y=dayNum(b);return x==null||y==null?Infinity:Math.abs(x-y);}
function isoPlus(iso,n){var d=dayNum(iso);if(d==null)return null;var t=new Date((d+n)*86400000);return t.getUTCFullYear()+"-"+pad2(t.getUTCMonth()+1)+"-"+pad2(t.getUTCDate());}
function noonMs(iso){var m=/^(\d{4})-(\d{2})-(\d{2})$/.exec(iso||"");return m?new Date(+m[1],+m[2]-1,+m[3],12).getTime():0;}
// Stable, sync, non-crypto string hash (cyrb53) → 14 hex chars. Used for
// keys of rows that have no id of their own (sheet rows, tracker rows
// without a file path). The receipt FILE hash is SHA-256 (receiptReader).
function hashStr(str){
  var h1=0xdeadbeef,h2=0x41c6ce57;str=String(str);
  for(var i=0;i<str.length;i++){var ch=str.charCodeAt(i);h1=Math.imul(h1^ch,2654435761);h2=Math.imul(h2^ch,1597334677);}
  h1=Math.imul(h1^(h1>>>16),2246822507);h1^=Math.imul(h2^(h2>>>13),3266489909);
  h2=Math.imul(h2^(h2>>>16),2246822507);h2^=Math.imul(h1^(h1>>>13),3266489909);
  return(4294967296*(2097151&h2)+(h1>>>0)).toString(16).padStart(14,"0");
}
function uniq(arr){var s=new Set();var out=[];(arr||[]).forEach(function(x){if(x&&!s.has(x)){s.add(x);out.push(x);}});return out;}
function plural0(n,w){return n+" "+w+(n===1?"":"s");}
function addFlag(row,text){var t=oneLine(text);if(t&&row.flags.indexOf(t)<0)row.flags.push(t);}
function orderIdsIn(){
  var out=[];
  for(var i=0;i<arguments.length;i++){var s=String(arguments[i]||"");var m;AMAZON_ORDER_RE.lastIndex=0;while((m=AMAZON_ORDER_RE.exec(s)))out.push(m[1]);}
  return uniq(out);
}

// ── Notes: source stamp + review flags ───────────────────────

function cleanLabel(s){return clipWords(String(s||"").replace(/\s*·\s*/g," "),90);}
// "Imported from <source> · <key>". `source` is a source code (amazon |
// tracker | sheet | receipt) or any label; `detail` (a file name) is added
// to sheet / receipt labels so the stamp says which file it came from.
// An Amazon stamp carries what the order was when it was imported —
// "(order total $268.51)" or "(order cancelled)" — so a later report that
// shows a different total / a cancellation can be spotted (planImport's
// `stale`) without nagging again once it has been flagged.
function stampLine(source,key,detail){
  var label=SOURCE_LABELS[source]||String(source||"import");
  if(detail&&(source==="sheet"||source==="receipt"))label+=" "+detail;
  else if(detail&&source==="amazon")label+=" ("+detail+")";
  return STAMP_START+cleanLabel(label)+STAMP_SEP+oneLine(key);
}
// → {source, label, key} or null. Old stamps ("Imported from invoice
// tracker · invoices/x.pdf") come back as key "tracker:invoices/x.pdf"; an
// old stamp with no file path is still a stamp (key null).
function parseStampLine(line){
  var s=String(line||"").trim();
  if(s.indexOf(STAMP_START)!==0)return null;
  if(s.indexOf(IMPORT_TAG.trim())===0){
    var rest=s.slice(IMPORT_TAG.trim().length).trim();
    if(!rest)return{source:"tracker",label:SOURCE_LABELS.tracker,key:null};
    var k=KEY_RE.test(rest)?rest:"tracker:"+rest;
    return{source:k.split(":")[0],label:SOURCE_LABELS.tracker,key:k};
  }
  var i=s.lastIndexOf(STAMP_SEP);
  if(i<0)return null;
  var key=s.slice(i+STAMP_SEP.length).trim();
  if(!KEY_RE.test(key))return null;
  return{source:key.split(":")[0],label:s.slice(STAMP_START.length,i).trim(),key:key};
}
function isStampLine(line){return parseStampLine(line)!==null;}
function isReviewLine(line){return String(line||"").indexOf(REVIEW_PREFIX)===0;}
function notesOf(x){return typeof x==="string"?x:(x&&x.notes)||"";}
function importKeysOf(x){
  var out=[];String(notesOf(x)).split("\n").forEach(function(l){var p=parseStampLine(l);if(p&&p.key)out.push(p.key);});
  return out;
}
// The part of a key that identifies the ORDER / FILE: a re-run that splits
// an order differently, or reads one more receipt out of the same PDF, is
// still the same order / file.
// A tracker email split by its item lines (10/10/26 — the invoice export's
// Itemized Details: an Uber delivery's personal snacks left out, a Best Buy
// receipt's Microsoft 365 line under Software) has a part key
// "tracker:<file_path>::<part>" — "::" never occurs in a file path; the email
// is the group.
var TRACKER_PART_SEP="::";
function groupKeyOf(key){
  var k=String(key||"");
  var m=/^(amazon|receipt):([^:]+)/.exec(k);
  if(m)return m[1]+":"+m[2];
  if(k.indexOf("tracker:")===0){var i=k.indexOf(TRACKER_PART_SEP,8);if(i>0)return k.slice(0,i);}
  return k;
}
function reviewFlagsOf(x){
  var out=[];
  String(notesOf(x)).split("\n").forEach(function(l){if(isReviewLine(l)){var t=l.slice(REVIEW_PREFIX.length).trim();if(t)out.push(t);}});
  return out;
}
function hasReviewFlags(x){return reviewFlagsOf(x).length>0;}
function trimBlankLines(lines){
  var a=0,b=lines.length;
  while(a<b&&!lines[a].trim())a++;
  while(b>a&&!lines[b-1].trim())b--;
  return lines.slice(a,b);
}
// Adds flags (one line each, no duplicates) at the TOP of the notes; every
// other line is kept as it was.
function withReviewFlags(notes,flags){
  var lines=String(notes||"").split("\n");
  var have=reviewFlagsOf(notes);
  var add=[];
  (flags||[]).forEach(function(f){var t=oneLine(f);if(t&&have.indexOf(t)<0&&add.indexOf(t)<0)add.push(t);});
  var rest=trimBlankLines(lines.filter(function(l){return!isReviewLine(l);}));
  var all=have.concat(add).map(function(t){return REVIEW_PREFIX+t;});
  return all.concat(rest).join("\n");
}
// Removes ONLY the ⚑ lines; comment and stamp stay exactly as they were.
function clearReviewFlags(notes){
  var lines=String(notes||"").split("\n").filter(function(l){return!isReviewLine(l);});
  return trimBlankLines(lines).join("\n");
}
// {comment, stamp, flags}: comment = everything that isn't a stamp or a ⚑
// line, stamp = the stamp line(s), flags = the ⚑ texts (prefix stripped).
function splitImportNotes(notes){
  var lines=String(notes||"").split("\n");
  return{
    comment:trimBlankLines(lines.filter(function(l){return!isStampLine(l)&&!isReviewLine(l);})).join("\n").trim(),
    stamp:lines.filter(isStampLine).map(function(l){return l.trim();}).join("\n"),
    flags:reviewFlagsOf(notes)
  };
}
// Expense ids named in a flag ("Possible duplicate of EXP-0123 …") — for a
// Compare toggle.
function relatedExpenseIds(flag){
  var out=[];var re=/\bEX[PM]-\d{3,}\b/g;var m;var s=String(flag||"");
  while((m=re.exec(s)))out.push(m[0]);
  return uniq(out);
}
// Keep = the flags are answered: strip the ⚑ lines from that one expense.
// A hand-entered postage row kept against "Replaced by the imported
// USPS/Stamps.com receipts" also gets the REPLACED_KEPT line (ordinary
// comment text), so the next import doesn't flag it again — and a mailhouse
// row kept against the AlphaGraphics one, REPLACED_MAILHOUSE_KEPT; a software
// row kept against "Replaced by the imported invoices for …",
// REPLACED_INVOICES_KEPT (10/10/26, later).
// (10/10/26, later) A flag that says what a refund / return refunds —
// "Refund on Amazon order 113-… (2026-09-04 · $112.18), which is in here —
// keep both…", "Return of Staples receipt 65031 (2026-06-20 · $347.33 — the
// ink), which is in here — keep both…" — stays behind, in its place, as a
// plain line without the advice ("Refund on Amazon order 113-… (2026-09-04 ·
// $112.18)"): it is the only thing that ties the refund to its purchase in
// the purchase panel (purchaseDetail), and Keep used to take it with the flag.
function refundLinkOf(flag){
  var s=oneLine(flag);
  if(!LINK_LINE_RE.test(s))return null;
  s=s.replace(/,?\s+which (?:is|are|was|were)\b.*$/i,"").replace(/\s+(?:is|are) in here\b.*$/i,"").replace(/\s*—\s*keep both\b.*$/i,"").trim();
  return s||null;
}
function keepExpense(expense){
  if(!expense)return expense;
  var raw=String((expense.notes)||"").split("\n");
  var have={};raw.forEach(function(l){if(!isReviewLine(l))have[l.trim()]=true;});
  var kept=[];
  raw.forEach(function(l){
    if(!isReviewLine(l)){kept.push(l);return;}
    var link=refundLinkOf(l.slice(REVIEW_PREFIX.length));
    if(link&&!have[link]){have[link]=true;kept.push(link);}
  });
  var n=trimBlankLines(kept).join("\n");
  var flags=reviewFlagsOf(expense.notes);
  REPLACED_KINDS.forEach(function(k){
    if(flags.some(function(f){return replacedKindOf(f)===k.kind;})&&!hasReplacedKept(n,k.kind))n=n?n+"\n"+k.kept:k.kept;
  });
  return n===(expense.notes||"")?expense:Object.assign({},expense,{notes:n});
}
// "postage" | "mailhouse" | "invoices" | null — which "receipts are the
// record" flag this is.
function replacedKindOf(flag){
  var s=String(flag||"");
  for(var i=0;i<REPLACED_KINDS.length;i++)if(s.indexOf(REPLACED_KINDS[i].prefix)===0)return REPLACED_KINDS[i].kind;
  return null;
}
function isReplacedFlag(flag){return replacedKindOf(flag)!==null;}
// Kept before against that kind's flag (kind omitted: postage, as before).
function hasReplacedKept(x,kind){
  var k=REPLACED_BY[kind||"postage"];var head=k.kept.slice(0,26);
  return String(notesOf(x)).split("\n").some(function(l){return l.trim().indexOf(head)===0;});
}
function flaggedExpenses(list){return(list||[]).filter(function(e){return e&&hasReviewFlags(e);});}
// Rewrite the stamp line(s) `test(parsed)` picks: fn(parsed) → {label, key}.
// Every other line is kept as it was.
function rewriteStamps(notes,test,fn){
  return String(notes||"").split("\n").map(function(l){
    var p=parseStampLine(l);
    if(!p||!p.key||!test(p))return l;
    var n=fn(p);
    return STAMP_START+cleanLabel(n.label)+STAMP_SEP+oneLine(n.key);
  }).join("\n");
}
// groupKey → the expenses carrying a stamp in that group.
function keyIndex(list){
  var m=new Map();
  (list||[]).forEach(function(e){
    if(!e||!e.id)return;
    importKeysOf(e.notes).forEach(function(k){var g=groupKeyOf(k);var l=m.get(g);if(!l){l=[];m.set(g,l);}if(l.indexOf(e)<0)l.push(e);});
  });
  return m;
}

// ── Deleted imports (tombstones) ─────────────────────────────
// Deleting an imported row takes its stamp with it, so the next run of a
// cumulative sheet (the monthly invoice tracker) would bring it back as if
// new. Expenses records the deleted rows' group keys here (this browser,
// per person — no DB column, no migration); planImport (opts.deletedKeys)
// brings such a row back UNTICKED and flagged, so a row he already threw
// out (a personal Uber, an Amazon email copy) isn't silently re-added.
// Another device doesn't know about this device's deletes.
var TOMB_PREFIX="loanflow_import_deleted_";
var TOMB_MAX=5000;
function tombKey(who){return TOMB_PREFIX+(who==="max"?"max":"reece");}
// → {groupKey: "YYYY-MM-DD" deleted}
function readImportTombstones(who){
  try{
    var o=JSON.parse(window.localStorage.getItem(tombKey(who))||"null");
    return o&&typeof o==="object"&&!Array.isArray(o)?o:{};
  }catch(e){return{};}
}
// Record the import keys of expenses that were just deleted → how many keys.
function recordDeletedImports(who,deleted){
  var keys=[];
  (deleted||[]).forEach(function(e){importKeysOf(e&&e.notes).forEach(function(k){var g=groupKeyOf(k);if(keys.indexOf(g)<0)keys.push(g);});});
  if(!keys.length)return 0;
  try{
    var o=readImportTombstones(who);var day=todayIso();
    keys.forEach(function(k){o[k]=day;});
    var all=Object.keys(o);
    if(all.length>TOMB_MAX){all.sort(function(a,b){return String(o[a]).localeCompare(String(o[b]));});all.slice(0,all.length-TOMB_MAX).forEach(function(k){delete o[k];});}
    window.localStorage.setItem(tombKey(who),JSON.stringify(o));
  }catch(e){/* private window / storage blocked: nothing kept */}
  return keys.length;
}
// A tombstone's value → {day, removed, leftOut}: "YYYY-MM-DD" (deleted in
// Expenses), "YYYY-MM-DD|removed" (taken out of a ready file), "YYYY-MM-DD|
// left out: <why>" (left out by an earlier import).
function parseTomb(v){
  var s=String(v||"");var bar=s.indexOf("|");
  var day=bar<0?s:s.slice(0,bar);var what=bar<0?"":s.slice(bar+1);
  var lo=/^left out\s*:?\s*(.*)$/i.exec(what);
  return{day:day,removed:what==="removed",leftOut:lo?(oneLine(lo[1])||"left out"):null};
}
// What Add remembers on this browser besides deletes in Expenses (10/9/26
// fix pass) — so a later drop of a CUMULATIVE file (the monthly tracker,
// the Amazon report) brings these back unticked and flagged, never as new:
//   • the rows Reece took out of a ready file (deleted in Excel — its
//     manifest had them — or their Amount cleared, or unticked before he
//     exported it): "removed";
//   • the Amazon emails left out as already in the order report whose
//     order isn't coming in (left out as personal items, cancelled) — from
//     this plan, and from a ready file's manifest: "left out: …". Without
//     the Amazon report in that later drop nothing else would know them.
// Group keys this import stamps (the rows it builds, and what's merged into
// them) or already stamped in Expenses are never remembered.
// → {groupKey: "removed" | "left out: <why>"}
function importDecisions(candidates,plan,builtRows,existingExpenses){
  var stamped=new Set();
  (builtRows||[]).forEach(function(r){if(r&&r.on!==false)rowKeysOf(r).forEach(function(k){stamped.add(groupKeyOf(k));});});
  keyIndex(existingExpenses).forEach(function(l,g){stamped.add(g);});
  var out={};
  var put=function(g,v){if(g&&!stamped.has(g)&&!(g in out))out[g]=v;};
  var infos=[];
  (candidates||[]).forEach(function(c){var i=c&&c.ready&&c.readyManifest;if(i&&infos.indexOf(i)<0)infos.push(i);});
  infos.forEach(function(i){(i.removed||[]).forEach(function(g){put(g,"removed");});});
  infos.forEach(function(i){(i.leftOut||[]).forEach(function(x){put(x.key,"left out: "+(x.why||"left out"));});});
  ((plan&&plan.excluded)||[]).forEach(function(x){if(x&&x.reason==="inReport")put(groupKeyOf(x.key),"left out: "+LEFT_OUT_BY.inReport.one);});
  return out;
}
// Write importDecisions(…) to this browser's tombstones (loanflow_import_
// deleted_<who>, "YYYY-MM-DD|<what>"; a later delete in Expenses overwrites
// with the plain day) → how many. Never in Practice mode (the import screen
// isn't open there).
function recordImportDecisions(who,decisions){
  var keys=Object.keys(decisions||{});
  if(!keys.length)return 0;
  try{
    var o=readImportTombstones(who);var day=todayIso();
    keys.forEach(function(k){o[k]=day+"|"+decisions[k];});
    var all=Object.keys(o);
    if(all.length>TOMB_MAX){all.sort(function(a,b){return String(o[a]).localeCompare(String(o[b]));});all.slice(0,all.length-TOMB_MAX).forEach(function(k){delete o[k];});}
    window.localStorage.setItem(tombKey(who),JSON.stringify(o));
  }catch(e){/* private window / storage blocked: nothing kept */}
  return keys.length;
}
function deletedMap(x){
  var m=new Map();
  if(!x)return m;
  if(x instanceof Map){x.forEach(function(v,k){m.set(groupKeyOf(k),v||"");});return m;}
  if(Array.isArray(x)||x instanceof Set){Array.from(x).forEach(function(k){m.set(groupKeyOf(k),"");});return m;}
  if(typeof x==="object")Object.keys(x).forEach(function(k){m.set(groupKeyOf(k),x[k]||"");});
  return m;
}

// ── Values: cells, money, dates ──────────────────────────────

// One cell → trimmed string. Excel-style ="6093" (a quoted value that kept
// its leading zeros) → 6093.
function cellValue(v){
  if(v==null)return"";
  var s=String(v);
  var m=/^\s*="([\s\S]*)"\s*$/.exec(s);
  if(m)s=m[1].replace(/""/g,'"');
  else if(/^\s*=\d[\d.\-/]*\s*$/.test(s))s=s.trim().slice(1);
  return s.replace(/^﻿/,"").trim();
}
// "$1,234.56" / "(12.34)" / "-$53.18" / "12.34 USD" / "12.34 CR" → number
// (to the cent). Blank, "N/A" or anything not a number → null.
// Excel's Accounting format (the ribbon's "$" button), saved as CSV, writes
// a negative as "$(74.60)" / " $ (74.60) " and a zero as " $ -   " — the
// currency symbol OUTSIDE the parentheses (10/9/26 fix pass: those read as
// "no amount" and the ready file's refunds were dropped).
function parseMoney(v){
  if(v==null)return null;
  if(typeof v==="number")return isFinite(v)?round2(v):null;
  var s=cellValue(v);if(!s)return null;
  var neg=false;
  s=s.replace(/[−‒–—]/g,"-");
  var acc=/^([$€£]?)\s*\((.*)\)$/.exec(s);
  if(acc){neg=true;s=(acc[1]+acc[2]).trim();}
  if(/^[$€£]?\s*-\s*$/.test(s))return 0;   // Accounting's zero
  if(/\s*\bCR\.?$/i.test(s)){neg=!neg;s=s.replace(/\s*\bCR\.?$/i,"");}
  else if(/\s*\bDR\.?$/i.test(s))s=s.replace(/\s*\bDR\.?$/i,"");
  s=s.replace(/^[A-Z]{3}\s+|\s+[A-Z]{3}$/i,"").replace(/^(USD|US)\$/i,"$");
  s=s.replace(/[$€£\s]/g,"").replace(/,/g,"");
  if(s.charAt(0)==="-"){neg=!neg;s=s.slice(1);}
  else if(s.charAt(0)==="+")s=s.slice(1);
  if(s.charAt(s.length-1)==="-"){neg=!neg;s=s.slice(0,-1);}
  if(s.charAt(0)==="$")s=s.slice(1);
  if(!/^(\d+\.?\d*|\.\d+)$/.test(s))return null;
  var n=round2(parseFloat(s));
  if(!isFinite(n))return null;
  return neg&&n?-n:n;
}
var MONTHS={jan:1,feb:2,mar:3,apr:4,may:5,jun:6,jul:7,aug:8,sep:9,oct:10,nov:11,dec:12};
function isoOf(y,m,d){
  if(!(y>=2000&&y<=2099&&m>=1&&m<=12&&d>=1&&d<=31))return null;
  var t=new Date(Date.UTC(y,m-1,d));
  if(t.getUTCMonth()!==m-1)return null;   // Feb 30
  return y+"-"+pad2(m)+"-"+pad2(d);
}
// Excel's day number (1900 system) → YYYY-MM-DD.
function serialToIso(n){
  n=Number(n);if(!(n>=36526&&n<=73050))return null;   // 2000 … 2099
  var t=new Date(Math.round((Math.floor(n)-25569)*86400000));
  return isoOf(t.getUTCFullYear(),t.getUTCMonth()+1,t.getUTCDate());
}
// Anything a sheet or receipt calls a date → YYYY-MM-DD (or null). ISO
// timestamps keep the day they were written in ("2026-09-30T00:56-07:00"
// is the 30th); US order M/D/Y; "Sep 15, 2026"; "15 Sep 2026"; Excel's
// d-mmm-yy "10-Apr-26" and "Apr-10-26" (a 2-digit year is 20yy, as in
// M/D/YY — 10/9/26 fix pass); an Excel day number (only when opts.serial —
// a sheet's date column).
function parseDate(v,opts){
  if(v==null)return null;
  if(typeof v==="number")return opts&&opts.serial?serialToIso(v):null;
  var s=cellValue(v);if(!s)return null;
  var m;
  var yy=function(y){y=+y;return y<100?y+2000:y;};
  if((m=/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})(?:$|[T\s])/.exec(s)))return isoOf(+m[1],+m[2],+m[3]);
  if((m=/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4}|\d{2})(?:$|[\s,T])/.exec(s)))return isoOf(yy(m[3]),+m[1],+m[2]);
  if((m=/^(?:[A-Za-z]{3,9}\.?,?\s+)?([A-Za-z]{3,9})\.?\s+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{4})\b/.exec(s))){var mo=MONTHS[m[1].toLowerCase().slice(0,3)];if(mo)return isoOf(+m[3],mo,+m[2]);}
  if((m=/^(\d{1,2})[\s-]+([A-Za-z]{3,9})\.?[\s,-]+(\d{4}|\d{2})\b/.exec(s))){var mo2=MONTHS[m[2].toLowerCase().slice(0,3)];if(mo2)return isoOf(yy(m[3]),mo2,+m[1]);}
  if((m=/^([A-Za-z]{3,9})\.?-(\d{1,2})-(\d{4}|\d{2})\b/.exec(s))){var mo3=MONTHS[m[1].toLowerCase().slice(0,3)];if(mo3)return isoOf(yy(m[3]),mo3,+m[2]);}
  if(opts&&opts.serial&&/^\d{5}(\.\d+)?$/.test(s))return serialToIso(parseFloat(s));
  return null;
}

// ── CSV ──────────────────────────────────────────────────────

// The delimiter of the first line (outside quotes): comma, semicolon or tab.
function sniffDelimiter(t){
  var counts={",":0,";":0,"\t":0};var q=false;
  for(var i=0;i<t.length&&i<20000;i++){
    var c=t[i];
    if(c==='"')q=!q;
    else if(!q&&(c==="\n"||c==="\r"))break;
    else if(!q&&counts[c]!==undefined)counts[c]++;
  }
  var best=",";
  if(counts[";"]>counts[best])best=";";
  if(counts["\t"]>counts[best])best="\t";
  return best;
}
// RFC-4180-ish CSV → rows of raw strings. Handles a UTF-8 BOM, quoted fields
// with commas / newlines / doubled quotes, CRLF, and a quote that isn't at
// the start of a field (kept literally — that's how ="6093" survives for
// cellValue to unwrap). Linear: whole fields are sliced, never built one
// character at a time. Blank lines are dropped.
function parseCSV(text,opts){
  var t=String(text==null?"":text);
  if(t.charCodeAt(0)===0xFEFF)t=t.slice(1);
  var d=(opts&&opts.delimiter)||sniffDelimiter(t);
  var rows=[],row=[],i=0,n=t.length;
  var stop=function(k){for(;k<n;k++){var c=t[k];if(c===d||c==="\n"||c==="\r")break;}return k;};
  while(i<n){
    var val;
    if(t[i]==='"'){
      var s=i+1,buf="";
      for(;;){
        var j=t.indexOf('"',s);
        if(j<0){buf+=t.slice(s);i=n;break;}
        if(t[j+1]==='"'){buf+=t.slice(s,j+1);s=j+2;continue;}
        buf+=t.slice(s,j);i=j+1;break;
      }
      var k=stop(i);if(k>i)buf+=t.slice(i,k);i=k;val=buf;
    }else{var k2=stop(i);val=t.slice(i,k2);i=k2;}
    row.push(val);
    if(i>=n)break;
    if(t[i]===d){i++;if(i>=n)row.push("");continue;}
    if(t[i]==="\r"&&t[i+1]==="\n")i+=2;else i++;
    rows.push(row);row=[];
  }
  if(row.length)rows.push(row);
  return rows.filter(function(r){for(var x=0;x<r.length;x++)if(String(r[x]).trim())return true;return false;});
}
// The first importer's shape: [{header: value}] with trimmed values, rows
// of one cell dropped (InvoiceImport.jsx and its tests still use this).
function parseCSVObjects(text){
  var rows=parseCSV(text);
  var h=(rows.shift()||[]).map(function(s){return cellValue(s);});
  return rows.filter(function(x){return x.length>1;}).map(function(x){
    var o={};h.forEach(function(k,i){o[k]=cellValue(x[i]);});return o;
  });
}

// ── XLSX (no dependency: kb.js's ZIP reader + regex over the sheet XML) ──

function xmlDecode(s){
  return String(s).replace(/&(#x[0-9a-f]+|#\d+|amp|lt|gt|quot|apos);/gi,function(_,e){
    var l=e.toLowerCase();
    if(l==="amp")return"&";if(l==="lt")return"<";if(l==="gt")return">";if(l==="quot")return'"';if(l==="apos")return"'";
    var code=l.charAt(1)==="x"?parseInt(l.slice(2),16):parseInt(l.slice(1),10);
    return isFinite(code)?String.fromCodePoint(code):"";
  });
}
function textRuns(inner){
  var src=String(inner||"").replace(/<rPh\b[\s\S]*?<\/rPh>/g,"");
  var s="";var re=/<t\b[^>]*>([\s\S]*?)<\/t>/g;var m;
  while((m=re.exec(src)))s+=xmlDecode(m[1]);
  return s;
}
// The self-closing <si/> is tried FIRST: the open-tag form's [^>]* would
// otherwise eat its "/" and run on to the NEXT </si>, merging two entries
// and shifting every later shared string by one (wrong vendors / amounts).
function sharedStringsOf(xml){
  var out=[];var re=/<si(?:\s[^>]*)?\/>|<si(?:\s[^>]*)?>([\s\S]*?)<\/si>/g;var m;
  while((m=re.exec(String(xml||""))))out.push(m[1]!=null?textRuns(m[1]):"");
  return out;
}
function colIndex(letters){var n=0;var L=String(letters||"").toUpperCase();for(var i=0;i<L.length;i++)n=n*26+(L.charCodeAt(i)-64);return n-1;}
// Worksheet XML → rows of strings (cells placed by their column letter, so
// empty cells keep everything after them in the right column).
function sheetRowsOf(xml,shared){
  var rows=[];var rowRe=/<row\b([^>]*?)(?:\/>|>([\s\S]*?)<\/row>)/g;var m;var next=0;
  while((m=rowRe.exec(String(xml||"")))){
    var ra=/\br="(\d+)"/.exec(m[1]);var ri=ra?+ra[1]-1:next;next=ri+1;
    var cells=[];var cRe=/<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g;var c;var ci=0;var inner=m[2]||"";
    while((c=cRe.exec(inner))){
      var attrs=c[1];var ref=/\br="([A-Z]+)\d*"/i.exec(attrs);var col=ref?colIndex(ref[1]):ci;ci=col+1;
      var t=(/\bt="([^"]*)"/.exec(attrs)||[])[1]||"";var body=c[2]||"";
      var v=/<v\b[^>]*>([\s\S]*?)<\/v>/.exec(body);var raw=v?xmlDecode(v[1]):"";
      var val=raw;
      if(t==="s")val=(shared||[])[parseInt(raw,10)]||"";
      else if(t==="inlineStr"){var is=/<is\b[^>]*>([\s\S]*?)<\/is>/.exec(body);val=is?textRuns(is[1]):"";}
      else if(t==="b")val=raw==="1"?"TRUE":raw==="0"?"FALSE":raw;
      cells[col]=val;
    }
    for(var k=0;k<cells.length;k++)if(cells[k]===undefined)cells[k]="";
    rows[ri]=cells;
  }
  var out=[];
  for(var r=0;r<rows.length;r++){var row=rows[r];if(row&&row.some(function(x){return String(x).trim();}))out.push(row);}
  return out;
}
// Every sheet of the workbook, in the workbook's order, from workbook.xml +
// its rels → [{name, path, hidden}]. A rels Target may be relative
// ("worksheets/sheet1.xml") or absolute ("/xl/worksheets/sheet1.xml" — the
// invoice export writes those).
function workbookSheetsOf(files){
  var wb=String(files["xl/workbook.xml"]||"");
  var rels=String(files["xl/_rels/workbook.xml.rels"]||"");
  var targets={};var rm;var rre=/<Relationship\b([^>]*)\/?>/g;
  while((rm=rre.exec(rels))){
    var id=(/\bId="([^"]*)"/.exec(rm[1])||[])[1];var tg=(/\bTarget="([^"]*)"/.exec(rm[1])||[])[1];
    if(id&&tg)targets[id]=tg.charAt(0)==="/"?tg.slice(1):"xl/"+tg.replace(/^\.\//,"");
  }
  var out=[];var sm;var sre=/<sheet\b([^>]*)\/?>/g;
  while((sm=sre.exec(wb))){
    var rid=(/\b(?:r:)?id="([^"]*)"/i.exec(sm[1].replace(/\bsheetId="[^"]*"/,""))||[])[1];
    var p=rid&&targets[rid];
    if(!p)continue;
    var nm=(/(?:^|\s)name="([^"]*)"/.exec(sm[1])||[])[1];
    out.push({name:nm?xmlDecode(nm):baseName(p).replace(/\.xml$/i,""),path:p,hidden:/\bstate="(hidden|veryHidden)"/.test(sm[1])});
  }
  return out;
}
// .xlsx bytes → EVERY sheet: [{name, hidden, rows}] in the workbook's order
// (10/10/26: the invoice export is three sheets — "Invoice Log", "Itemized
// Details", "Summary" — and inline strings, no sharedStrings.xml). One pass
// over the ZIP for all the sheets. A workbook.xml that names no sheet whose
// part is there falls back to every xl/worksheets/*.xml, by part name.
// opts.unzip / opts.inflate are swappable for tests; the default is kb.js's
// unzipText (DecompressionStream).
async function readXlsxSheets(buf,opts){
  opts=opts||{};
  var unzip=opts.unzip;
  if(!unzip){var kb=await import("./kb");unzip=kb.unzipText;}
  var meta=await unzip(buf,function(n){return n==="xl/workbook.xml"||n==="xl/_rels/workbook.xml.rels"||n==="xl/sharedStrings.xml";},opts.inflate);
  if(!meta["xl/workbook.xml"])throw new Error("This isn't an Excel workbook. Save it as CSV from Excel and drop it again.");
  var list=workbookSheetsOf(meta);
  var want=new Set(list.map(function(s){return s.path;}));
  var parts=await unzip(buf,function(n){return want.has(n)||/^xl\/worksheets\/[^/]+\.xml$/.test(n);},opts.inflate);
  var shared=sharedStringsOf(meta["xl/sharedStrings.xml"]||"");
  var out=[];
  list.forEach(function(s){var xml=parts[s.path];if(xml!=null)out.push({name:s.name,hidden:s.hidden,rows:sheetRowsOf(xml,shared)});});
  if(!out.length)Object.keys(parts).filter(function(n){return/^xl\/worksheets\/[^/]+\.xml$/.test(n);}).sort().forEach(function(n){
    out.push({name:baseName(n).replace(/\.xml$/i,""),hidden:false,rows:sheetRowsOf(parts[n],shared)});
  });
  if(!out.length)throw new Error("This workbook has no sheets to read. Save it as CSV from Excel and drop it again.");
  return out;
}
// .xlsx bytes → rows of the first visible sheet (as before).
async function readXlsxRows(buf,opts){
  var all=await readXlsxSheets(buf,opts);
  for(var i=0;i<all.length;i++)if(!all[i].hidden)return all[i].rows;
  return all[0].rows;
}
var SHEET_ACCEPT=".csv,.tsv,.txt,.xlsx";
function sheetKindOf(name){
  var n=String(name||"").toLowerCase();
  if(/\.(csv|tsv|txt)$/.test(n))return"csv";
  if(/\.xlsx$/.test(n))return"xlsx";
  if(/\.(xls|xlsm|xlsb|numbers|ods)$/.test(n))return"unsupported";
  return null;
}
function readFileText(file){
  if(file&&typeof file.text==="function")return file.text();
  return new Promise(function(resolve,reject){var r=new FileReader();r.onload=function(){resolve(String(r.result||""));};r.onerror=function(){reject(new Error("Couldn't read this file."));};r.readAsText(file);});
}
function readFileBuffer(file){
  if(file&&typeof file.arrayBuffer==="function")return file.arrayBuffer();
  return new Promise(function(resolve,reject){var r=new FileReader();r.onload=function(){resolve(r.result);};r.onerror=function(){reject(new Error("Couldn't read this file."));};r.readAsArrayBuffer(file);});
}
// A dropped sheet file → {name, kind, rows, fingerprint}. Throws an Error
// whose message is written for Reece. fingerprint = a hash of the content,
// so the same sheet dropped twice can be spotted before it's parsed again.
async function readSheetFile(file,opts){
  var name=(file&&file.name)||"sheet";
  var kind=sheetKindOf(name);
  if(kind==="unsupported")throw new Error(name+": that Excel format can't be read here. Save it as CSV (or .xlsx) from Excel and drop it again.");
  if(kind==="xlsx"){
    var buf=await readFileBuffer(file);
    var all;
    try{all=await readXlsxSheets(buf,opts);}
    catch(e){throw new Error(name+": "+((e&&e.message)||"couldn't read it")+(/CSV/.test(String(e&&e.message))?"":" Save it as CSV from Excel and drop it again."));}
    // rows = the first visible sheet (as before); sheets = every visible one
    // (hidden sheets are Excel's scratch space) — sheetCandidates reads them
    // all (workbookCandidates).
    var vis=all.filter(function(s){return!s.hidden;});if(!vis.length)vis=all.slice(0,1);
    var rows=vis[0].rows;
    var fp=vis.length>1?hashStr(JSON.stringify(vis.map(function(s){return[s.name,s.rows.length,s.rows.slice(0,50)];}))):hashStr(JSON.stringify(rows.slice(0,50)));
    return{name:name,kind:"xlsx",rows:rows,sheets:vis.map(function(s){return{name:s.name,rows:s.rows};}),fingerprint:"x"+(buf.byteLength||0)+"-"+fp+"-"+rows.length};
  }
  // UTF-8 — or, when the bytes aren't UTF-8, Windows-1252 (Excel's plain
  // "CSV (Comma delimited)" save of the ready file: its "·" and "—" would
  // otherwise come back as "�").
  var text=null;
  if(typeof TextDecoder!=="undefined"&&file&&typeof file.arrayBuffer==="function"){try{text=decodeSheetBytes(await file.arrayBuffer());}catch(e){text=null;}}
  if(text==null)text=await readFileText(file);
  return{name:name,kind:"csv",rows:parseCSV(text),fingerprint:"c"+text.length+"-"+hashStr(text.length>2000000?text.slice(0,1000000)+text.slice(-1000000):text)};
}
// Bytes → text: UTF-8 when they are valid UTF-8, else Windows-1252. null
// when there's no decoder (the caller falls back to File.text()).
function decodeSheetBytes(buf,Decoder){
  var D=Decoder||(typeof TextDecoder!=="undefined"?TextDecoder:null);
  if(!D||!buf)return null;
  var u8=buf instanceof Uint8Array?buf:new Uint8Array(buf);
  try{return new D("utf-8",{fatal:true}).decode(u8);}catch(e){/* not UTF-8 */}
  try{return new D("windows-1252").decode(u8);}catch(e){return null;}
}

// ── Vendors + categories ─────────────────────────────────────

// Sender / merchant → clean vendor name + its category when the vendor
// alone decides it (a service). Stores (null) are decided by what was
// bought — a Staples or Amazon order of envelopes is Marketing, a USB hub
// is Office/Equipment. Order matters: first match wins.
var VENDORS=[
  [/usps|postal service/i,"USPS","Marketing"],[/stamps\.com|stamps com\b/i,"Stamps.com","Marketing"],
  [/alpha ?graphics/i,"AlphaGraphics","Marketing"],[/propertyshark/i,"PropertyShark","Marketing"],
  [/datazapp/i,"DataZapp","Marketing"],[/prop ?stream/i,"PropStream","Marketing"],[/prop ?radar/i,"PropRadar","Marketing"],
  [/qr\.io/i,"QR.io","Marketing"],[/vistaprint/i,"Vistaprint","Marketing"],
  [/amazon business|prime business/i,"Amazon Business",null],[/amazon|amzn/i,"Amazon",null],
  [/staples/i,"Staples",null],[/office ?depot|officemax|odp business/i,"Office Depot",null],
  [/fedex/i,"FedEx",null],[/(the )?ups store/i,"The UPS Store",null],[/walmart/i,"Walmart",null],
  // (10/10/26 — the invoice export's uploaded receipt photos and Uber
  // deliveries: "Target (uploaded receipt photo)", "Fry's", "Target delivery
  // via Uber")
  [/^\s*target\b|\btarget\.com\b|\btarget (store|t-?\d{3,}|#\s?\d+|chandler|mesa|gilbert|phoenix)\b/i,"Target",null],[/\bfry['’]?s\b(?! (bread|pans?|oil|baskets?))|frysfood/i,"Fry's",null],
  [/costco/i,"Costco",null],[/best ?buy/i,"Best Buy",null],[/home ?depot/i,"Home Depot",null],
  [/ringcentral/i,"RingCentral","Software/Subscriptions"],[/supabase/i,"Supabase","Software/Subscriptions"],
  [/vercel/i,"Vercel","Software/Subscriptions"],[/eleven ?labs/i,"ElevenLabs","Software/Subscriptions"],
  [/wispr/i,"Wispr Flow","Software/Subscriptions"],[/efax/i,"eFax","Software/Subscriptions"],
  [/magicblocks/i,"MagicBlocks","Software/Subscriptions"],
  [/anthropic|claude\.ai/i,"Anthropic","Software/Subscriptions"],[/openai|chatgpt/i,"OpenAI","Software/Subscriptions"],
  [/adobe/i,"Adobe","Software/Subscriptions"],[/microsoft|msft\b/i,"Microsoft","Software/Subscriptions"],
  [/google (workspace|one|\*)|gsuite/i,"Google","Software/Subscriptions"],[/canva\b/i,"Canva","Software/Subscriptions"],
  [/zoom\.us|zoom video/i,"Zoom","Software/Subscriptions"],[/dropbox/i,"Dropbox","Software/Subscriptions"],
  [/godaddy/i,"GoDaddy","Software/Subscriptions"],
  [/oncourse|\bocl\b/i,"OnCourse Learning","Licensing & Education"],[/nrmla/i,"NRMLA","Licensing & Education"],
  [/\bnmls\b/i,"NMLS","Licensing & Education"],[/az\.gov|arizona department/i,"State of Arizona","Licensing & Education"],
  [/\buber\b/i,"Uber","Travel & Meals"],[/\blyft\b/i,"Lyft","Travel & Meals"]
];
function titleCaseIfShouting(s){
  if(!/[a-z]/.test(s)&&/[A-Z]{3,}/.test(s))return s.toLowerCase().replace(/\b[a-z]/g,function(c){return c.toUpperCase();});
  return s;
}
// → {vendor, cat (null for stores / unknown), known}
function vendorOf(text){
  var s=oneLine(text);
  for(var i=0;i<VENDORS.length;i++){var v=VENDORS[i];if(v[0].test(s))return{vendor:v[1],cat:v[2],known:true};}
  var m=s.match(/^"?([^<"]+?)"?\s*</);
  var name=(m?m[1]:s).trim();
  if(!m&&/^[^\s@]+@([^\s>]+)$/.test(name))name=name.replace(/^[^@]+@/,"").replace(/^(mail|email|e|info|noreply|no-reply)\./i,"").replace(/\.[a-z]{2,}$/i,"");
  name=name.replace(/^(sq|tst|pp|paypal|sp)\s*\*\s*/i,"").replace(/\s{2,}/g," ").trim();
  return{vendor:clipWords(titleCaseIfShouting(name),40),cat:null,known:false};
}
// Reece's business: everything for a mailer is Marketing (it counts in
// Marketing spend and cost per response).
var MARKETING_RE=/envelope|\bpapers?\b|card ?stock|cover ?stock|parchment|letterhead|\bstamps?\b|postage|\blabels?\b|\binks?\b|inkjet|\btoner\b|cartridge|\bprint(s|ing|ed)?\b|\bpens?\b|\bmarkers?\b|writing instrument|fountain pen|rollerball|ballpoint|\bmailers?\b|\bmailing\b|post ?cards?|\busps\b|stamps\.com|alpha ?graphics|datazapp|propertyshark|prop ?stream|prop ?radar|skip[- ]?trac|\bqr\b|#10\b|mail ?house|direct mail|presort|letter ?shop|\beddm\b|inserter|neopost|business cards?|\b(plastic|linen|cotton|kraft) cards?\b|vistaprint|brochure|flyers?|door ?hangers?|southworth|hammermill|r[eé]sum[eé]|cotton (business|linen|resume)/i;
var NOT_MARKETING_RE=/toilet (paper|tissue)s?|toilet|paper towels?|\btissues?\b|paper (plates?|cups?|bowls?)|napkins?|\bbaking\b/i;
var NOT_MARKETING_G=new RegExp(NOT_MARKETING_RE.source,"gi");
function withoutNotMarketing(s){return String(s||"").replace(NOT_MARKETING_G," ");}
var SOFTWARE_RE=/software|subscription|\bsaas\b|license key|\bcloud\b|\bapps?\b|microsoft 365|office 365|adobe|vercel|supabase|ringcentral|eleven ?labs|anthropic|openai|chatgpt|\bclaude\b|membership|web ?hosting|domain (name|renewal|registration)|wispr|magicblocks|efax|google workspace/i;
var LICENSING_RE=/\bnmls\b|continuing education|\bce (course|credits?|hours?)\b|\bcourses?\b|nrmla|licen[cs]e (fee|renewal)|licensing|\bexams?\b|education/i;
var TRAVEL_RE=/flight|airline|airfare|\bhotel|marriott|hilton|hyatt|airbnb|\buber\b|\blyft\b|\btaxi\b|rental car|car rental|parking|\btolls?\b|restaurant|\bmeals?\b|\bdining\b|food (&|and) drink|\bgas\b|\bfuel\b|starbucks/i;
// (+ 10/10/26: a desktop computer, a printer protection plan / warranty, an
// extension cord)
var EQUIPMENT_RE=/\bdesktops?\b(?! ?(organi[sz]ers?|files?|sorters?|pads?|mats?|stands?|shel(f|ves)|calendars?|easels?|labels?|trays?))|protection plan|extended (warranty|service)|\bwarranty\b|extension cords?|\btape\b|cable|\bhubs?\b|\busb\b|adapter|charger|monitor|keyboard|\bmouse\b|laptop|computer|printer|scanner|shredder|\bdesk\b|chair|furniture|stapler|binder|folder|clipboard|calculator|headset|webcam|\bphone\b|\blamp\b|whiteboard|organizer|batter(y|ies)/i;
// Amazon's own product groups for things that aren't business supplies.
var PERSONAL_AMAZON=/^(pet products|health and beauty|beauty|grocery|video games|toys?\b|baby|apparel|shoes|jewelry|luggage|sports|wine|personal care|musical instruments|automotive)/i;
var PERSONAL_SEGMENT=/live plant and animal|food beverage and tobacco|apparel and luggage and personal care|toys and games/i;
var PERSONAL_TEXT=/toilet (paper|tissue)|paper towels?|toothpaste|mouthwash|shampoo|\bdog\b|\bpuppy\b|\bpet (food|hair|treats?)|cat litter|groceries|energy drink|sports drink|playstation|xbox|nintendo/i;
// Services that are personal whatever the email / card line / receipt says
// around them (10/9/26: a Kill Tony virtual ticket — sold through Maestro —
// came in as Software/Subscriptions). A vendor-level match, so a "print your
// tickets" line doesn't make it Marketing.
var PERSONAL_SERVICES=[
  [/kill tony/i,"entertainment"],
  [/netflix|\bhulu\b|disney ?\+|disneyplus|hbo ?max|peacock ?tv|paramount ?\+|crunchyroll|youtube (premium|tv)|sirius ?xm|spotify/i,"streaming"],
  [/playstation|xbox|nintendo|steampowered|steam (wallet|games?)|epic games|\btwitch\b|patreon|onlyfans/i,"games / fan membership"],
  [/ticketmaster|stubhub|seatgeek|fandango/i,"event tickets"],
  [/petco|petsmart|chewy\.com|barkbox/i,"pet store"],
  [/instacart/i,"groceries"]
];
// → "entertainment: Kill Tony" or null.
function personalService(text){
  var s=oneLine(text);if(!s)return null;
  for(var i=0;i<PERSONAL_SERVICES.length;i++){var m=PERSONAL_SERVICES[i][0].exec(s);if(m)return PERSONAL_SERVICES[i][1]+": "+titleCaseIfShouting(m[0]).replace(/\b[a-z]/g,function(ch){return ch.toUpperCase();});}
  return null;
}
// → a short reason ("Amazon: Pet Products") or null.
function looksPersonal(o){
  o=o||{};
  var pc=cellValue(o.productCategory);
  if(pc&&PERSONAL_AMAZON.test(pc))return"Amazon: "+pc;
  if(!pc||/^\d+$/.test(pc)){var seg=cellValue(o.segment);if(seg&&PERSONAL_SEGMENT.test(seg))return"Amazon: "+seg;}
  var t=[o.title,o.text].filter(Boolean).join(" ");
  if(t&&PERSONAL_TEXT.test(t)&&!MARKETING_RE.test(withoutNotMarketing(t)))return"looks personal";
  return t?personalService(t):null;
}
// One of CATEGORIES for a purchase. Fields are all optional: vendor, the
// item title, Amazon's UNSPSC family / klass / commodity / segment, its
// product category, and any free text (subject, receipt items, a card
// export's own category column).
function categorize(o){
  o=o||{};
  var v=o.vendor?vendorOf(o.vendor):{vendor:"",cat:null,known:false};
  if(v.cat)return v.cat;
  if(looksPersonal(o))return"Other";
  var structured=[o.commodity,o.klass,o.family].map(cellValue).filter(Boolean).join(" | ");
  var title=oneLine(o.title);
  if(structured){
    if(MARKETING_RE.test(structured)&&!NOT_MARKETING_RE.test(structured+" "+title))return"Marketing";
    if(/software/i.test(structured))return"Software/Subscriptions";
    if(EQUIPMENT_RE.test(structured))return"Office/Equipment";
  }
  var text=[title,oneLine(o.text),structured].filter(Boolean).join(" | ");
  if(text){
    var tv=vendorOf(text);   // a known service named in the text ("USPS eReceipt")
    if(tv.cat&&!v.known)return tv.cat;
    if(LICENSING_RE.test(text))return"Licensing & Education";
    if(MARKETING_RE.test(withoutNotMarketing(text)))return"Marketing";
    // Every Marketing subcategory (Data, Postage, Paper, Envelopes, Cardstock,
    // Ink & Toner, Printing, Mailhouse, Labels, Pens & Markers, Mailing
    // Machine — src/expenseItems.js) lands in Marketing: when what the text
    // names (each " | " piece on its own — receipt lines, the title, the
    // subject) is all Marketing items ("Sealing solution for mailing systems",
    // "Batch leads", "Letter shop presort").
    var pieceTypes=text.split(" | ").map(itemTypeOf).filter(Boolean);
    if(pieceTypes.length&&pieceTypes.every(isMarketingItem))return"Marketing";
    if(SOFTWARE_RE.test(text))return"Software/Subscriptions";
    if(TRAVEL_RE.test(text))return"Travel & Meals";
    if(EQUIPMENT_RE.test(text))return"Office/Equipment";
  }
  return v.known?"Office/Equipment":"Other";
}

// ── Sheet detection ──────────────────────────────────────────

function hasIn(list,name){return list.indexOf(name)>=0;}
function findCol(h,re){for(var i=0;i<h.length;i++)if(re.test(h[i]))return true;return false;}
var AMOUNT_COL_RE=/^amount( \((usd|\$)\))?$/;
// The invoice export's "Invoice Log": a date, the sender / vendor, a
// description (the subject), an amount and the receipt file it was saved
// as (headers normalised: lower case, "_" → " ").
function isInvoiceLogHeader(h){
  return hasIn(h,"date")&&hasIn(h,"vendor")&&hasIn(h,"description")&&findCol(h,AMOUNT_COL_RE)&&findCol(h,/^(receipt file|file path|receipt file path|file)$/);
}
// …and its "Itemized Details": one row per item line of a receipt, the
// receipt named by its date + vendor + total.
function isItemDetailsHeader(h){
  return findCol(h,/^(receipt )?date$/)&&hasIn(h,"vendor")&&findCol(h,/^receipt total( \((usd|\$)\))?$/)&&findCol(h,/^(item|item description|description)$/)&&
    findCol(h,/^(unit price|line total|price|amount)( \((usd|\$)\))?$/);
}
// The ready file's header (normalised): every READY_HEADER column is there.
function isReadyHeader(h){for(var i=0;i<READY_HEADER.length;i++)if(h.indexOf(READY_HEADER[i].toLowerCase())<0)return false;return true;}
function normHeader(h){return cellValue(h).toLowerCase().replace(/[_\s]+/g," ").trim();}
function pickCol(h,exact,loose,avoid){
  var i;
  for(var e=0;e<exact.length;e++){for(i=0;i<h.length;i++)if(exact[e].test(h[i])&&!(avoid&&avoid.test(h[i])))return i;}
  if(loose){for(i=0;i<h.length;i++)if(loose.test(h[i])&&!(avoid&&avoid.test(h[i])))return i;}
  return -1;
}
// Column guesses for a generic sheet (a card export, a Staples order
// history…) → indexes (-1 = none): date, amount, credit, vendor,
// description, status, type, category. The UI lets Reece change them.
function guessMapping(header){
  var h=(header||[]).map(normHeader);
  var date=pickCol(h,[/^(transaction|trans\.?|purchase|order|invoice|receipt|payment|charge) date$/,/^date$/,/^(date of (purchase|transaction|order))$/],/date/,/due|ship|deliver|expir|birth|created|updated|modified|received|post/);
  if(date<0)date=pickCol(h,[/^post(ed|ing)? date$/],null,null);
  var amount=pickCol(h,[/^(order |grand |invoice )?total$/,/^(total |net |charge |transaction |payment )?amount( \(?(usd|\$)\)?)?$/,/^amount paid$/,/^debit( amount)?$/,/^(order )?net total$/,/^total (charged|paid|cost|price)$/,/^price$/,/^cost$/],/amount|total|debit|charge|paid|cost|price/,/tax|shipping|sub ?total|discount|credit|balance|quantity|qty|unit|tip|count|items?$/);
  var credit=-1;
  if(amount>=0&&/debit/.test(h[amount]))credit=pickCol(h,[/^credit( amount)?$/],null,null);
  var vendor=pickCol(h,[/^(vendor|merchant|payee|supplier|seller|store|company|business|merchant name|vendor name|payee name|sold by)$/],/merchant|vendor|payee|seller|supplier/,null);
  var description=pickCol(h,[/^(description|details|memo|item|items|title|product|product name|item description|item name|subject|transaction description|narrative|transaction)$/],/description|memo|item|product|details/,/date|amount|total|price|qty|quantity|id$|number|#/);
  if(description===vendor)description=-1;
  var status=pickCol(h,[/^(order |payment |transaction )?status$/],/status/,null);
  var type=pickCol(h,[/^(transaction )?type$/],null,null);
  var category=pickCol(h,[/^category$/,/^(transaction|merchant) category$/],null,null);
  // How many were bought (10/11/26 — his sister's-account sheets): the
  // item's count ("Envelopes $85 (1,000 envelopes)").
  var units=pickCol(h,[/^(count|units|how many|pieces|sheets|envelopes|cards|stamps)$/],null,null);
  return{date:date,amount:amount,credit:credit,vendor:vendor,description:description,status:status,type:type,category:category,units:units,flipSign:false};
}
// Purchases written as negatives (most bank / some card exports): when
// most of the amounts are negative, flip them so a purchase is positive.
function guessFlipSign(dataRows,mapping){
  if(!mapping||mapping.amount<0||mapping.credit>=0)return false;
  var neg=0,pos=0;
  for(var i=0;i<dataRows.length&&i<1000;i++){var a=parseMoney((dataRows[i]||[])[mapping.amount]);if(a==null||!a)continue;if(a<0)neg++;else pos++;}
  return neg>pos*1.5&&neg>=2;
}
// rows → {type, headerIndex, header, mapping?, noAmountColumn?, reason?, rowCount}
//   loanflowReady   a "LoanFlow ready file" (READY_HEADER — readyFileCsv wrote it)
//   amazonBusiness  Amazon Business "Orders" report (Order ID + Order Net Total + Title)
//   invoiceTracker  the inbox scrape (subject + from; amount may be missing)
//   generic         any sheet with a date column and an amount / total column
//   unknown         nothing recognisable (reason says why)
function detectSheet(rows){
  rows=rows||[];
  var limit=Math.min(rows.length,15);
  var sawDate=false;
  for(var i=0;i<limit;i++){
    var raw=rows[i]||[];
    var h=raw.map(normHeader);
    var has=hasIn.bind(null,h);
    var header=raw.map(cellValue);
    var count=rows.length-i-1;
    if(isReadyHeader(h))return{type:"loanflowReady",headerIndex:i,header:header,rowCount:count};
    if(has("order id")&&has("order net total")&&has("title"))return{type:"amazonBusiness",headerIndex:i,header:header,rowCount:count};
    if(has("subject")&&has("from"))return{type:"invoiceTracker",headerIndex:i,header:header,noAmountColumn:!has("amount"),rowCount:count};
    // (10/10/26) The invoice export's sheets: "Invoice Log" — the tracker
    // with friendlier headers (Date | Vendor | Description | Amount (USD) |
    // Amount Source | Receipt File | Itemized Detail?) — and "Itemized
    // Details", the item lines of its receipts (an item SOURCE, joined to
    // the Invoice Log rows — joinItemDetails).
    if(isInvoiceLogHeader(h))return{type:"invoiceLog",headerIndex:i,header:header,noAmountColumn:!findCol(h,AMOUNT_COL_RE),rowCount:count};
    if(isItemDetailsHeader(h))return{type:"itemDetails",headerIndex:i,header:header,rowCount:count};
    var g=guessMapping(raw);
    if(g.date>=0)sawDate=true;
    if(g.date>=0&&g.amount>=0){
      g.flipSign=guessFlipSign(rows.slice(i+1,i+1001),g);
      return{type:"generic",headerIndex:i,header:header,mapping:g,rowCount:count};
    }
  }
  return{type:"unknown",headerIndex:-1,header:(rows[0]||[]).map(cellValue),rowCount:Math.max(0,rows.length-1),
    reason:sawDate?"It has a date column but no amount or total column, so there's nothing to import.":"Couldn't find a date column and an amount / total column in it."};
}

// ── Candidates ───────────────────────────────────────────────
// A candidate = one possible expense:
//   {key, source, badge, date, at, vendor, desc, amount, cat, flags[],
//    exclude: null|"failed"|"cancelled"|"noAmount"|"cardPayment",
//    orderIds[], fileRef, fileName, …source extras}

function makeCand(o){
  var c={key:o.key,source:o.source,badge:o.badge||SOURCE_BADGES[o.source]||o.source,date:o.date||null,at:o.at||noonMs(o.date),
    vendor:o.vendor||"",desc:o.desc||"",amount:o.amount==null?null:round2(o.amount),cat:o.cat||"Other",flags:(o.flags||[]).map(oneLine).filter(Boolean),
    exclude:o.exclude||null,orderIds:o.orderIds||[],fileRef:o.fileRef||null,fileName:o.fileName||null};
  Object.keys(o).forEach(function(k){if(!(k in c))c[k]=o[k];});
  return c;
}
function headerIndexer(det){
  var h=(det.header||[]).map(normHeader);
  return function(name){return h.indexOf(String(name).toLowerCase());};
}

// Amazon Business "Orders" report: one row per item line, order columns
// repeated. One expense per order — or one per category when its lines fall
// into different categories (amounts split in proportion to each group's
// Item Net Total, the parts summing EXACTLY to the order total). Personal
// lines are a part of their own, exclude "personal" (key
// amazon:<id>:personal, or amazon:<id> when the whole order is personal).
function amazonCandidates(rows,det){
  det=det||detectSheet(rows);
  var col=headerIndexer(det);
  var C={date:col("order date"),id:col("order id"),status:col("order status"),net:col("order net total"),
    payRef:col("payment reference id"),payAmt:col("payment amount"),pc:col("amazon-internal product category"),
    title:col("title"),segment:col("segment"),family:col("family"),klass:col("class"),commodity:col("commodity"),
    qty:col("item quantity"),itemNet:col("item net total"),seller:col("seller name"),brand:col("brand")};
  var get=function(r,i){return i>=0?cellValue(r[i]):"";};
  var orders=new Map();
  for(var i=det.headerIndex+1;i<rows.length;i++){
    var r=rows[i];var id=get(r,C.id);if(!id)continue;
    var o=orders.get(id);if(!o){o={id:id,lines:[]};orders.set(id,o);}
    o.lines.push(r);
  }
  var out=[];
  orders.forEach(function(o){
    var first=o.lines[0];
    var status=get(first,C.status)||"Unknown";
    var date=parseDate(get(first,C.date),{serial:true});
    var lines=o.lines.map(function(l){
      var line={title:get(l,C.title),qty:parseFloat(get(l,C.qty))||null,net:parseMoney(get(l,C.itemNet))||0,
        productCategory:get(l,C.pc),segment:get(l,C.segment),family:get(l,C.family),klass:get(l,C.klass),commodity:get(l,C.commodity),
        // (10/10/26) the report's Brand column says Southworth even where
        // the title doesn't ("100% Cotton Business Paper, White, 32 lbs…" is
        // Southworth JD18C) — the item line carries it (expenseItems brand);
        // (review) any other brand it names is Non-Southworth ("100 Pack #10
        // Business Envelopes…" is Topeen) — never over a title saying Southworth
        brand:columnBrand(get(l,C.brand))};
      line.personal=looksPersonal(line);
      line.cat=line.personal?"Other":categorize({vendor:"Amazon Business",title:line.title,family:line.family,klass:line.klass,commodity:line.commodity,segment:line.segment,productCategory:line.productCategory});
      return line;
    });
    var itemSum=round2(lines.reduce(function(s,l){return s+l.net;},0));
    var orderNet=parseMoney(get(first,C.net));
    var base={source:"amazon",orderId:o.id,orderIds:[o.id],date:date,vendor:"Amazon Business",status:status};
    var firstTitle=clipWords((lines[0]&&lines[0].title)||"order "+o.id,60);
    if(/cancel/i.test(status)){
      out.push(makeCand(Object.assign({},base,{key:"amazon:"+o.id,desc:"Amazon — "+firstTitle,amount:itemSum,orderTotal:itemSum,exclude:"cancelled",cat:lines[0]?lines[0].cat:"Office/Equipment"})));
      return;
    }
    var total=orderNet!=null?orderNet:itemSum;
    if(total==null||Math.abs(total)<0.005){
      out.push(makeCand(Object.assign({},base,{key:"amazon:"+o.id,desc:"Amazon — "+firstTitle,amount:0,orderTotal:0,exclude:"noAmount"})));
      return;
    }
    // What has actually been charged: distinct payment references. A Closed
    // order with no payment detail at all (digital orders such as the Prime
    // Business fee) is taken as paid — the report just doesn't carry it.
    var flags=[];
    var closed=/^closed$/i.test(status);
    var paid=new Map();
    if(C.payAmt>=0)o.lines.forEach(function(l){var ref=get(l,C.payRef);var amt=parseMoney(get(l,C.payAmt));if(amt!=null&&!paid.has(ref))paid.set(ref,amt);});
    var charged=0;paid.forEach(function(a){charged+=a;});charged=round2(charged);
    if(!closed){
      var partial=charged>0.005&&charged<total-0.01?" ("+fmtMoney(charged)+" of "+fmtMoney(total)+" charged so far)":"";
      flags.push("Not charged yet — Amazon shows it as "+status+partial);
    }else if(paid.size&&charged<total-0.01){
      flags.push("Not fully charged yet — Amazon shows "+fmtMoney(charged)+" of "+fmtMoney(total)+" paid");
    }
    // Group lines by category, in first-seen order. Personal lines (pet,
    // grocery, health & beauty, toys / games, apparel…) are a group of their
    // own: that part of the order is LEFT OUT (exclude "personal" — listed,
    // with "Include anyway"); the rest keeps its exact share of the total.
    var groups=[];var byCat={};
    lines.forEach(function(l){var gk=l.personal?"personal":l.cat;var g=byCat[gk];if(!g){g={cat:l.cat,personal:!!l.personal,lines:[],net:0};byCat[gk]=g;groups.push(g);}g.lines.push(l);g.net=round2(g.net+l.net);});
    var totalC=cents(total);var parts;
    if(groups.length===1)parts=[totalC];
    else{
      var sumNet=groups.reduce(function(s,g){return s+Math.max(0,cents(g.net));},0);
      parts=groups.map(function(g){return sumNet>0?Math.round(totalC*Math.max(0,cents(g.net))/sumNet):0;});
      if(sumNet<=0){parts=groups.map(function(){return 0;});parts[0]=totalC;}
      var diff=totalC-parts.reduce(function(s,p){return s+p;},0);
      if(diff){var big=0;parts.forEach(function(p,ix){if(Math.abs(p)>Math.abs(parts[big]))big=ix;});parts[big]+=diff;}
    }
    var split=groups.length>1;
    // The split note names every part; on the kept parts a second version
    // says the personal part was left out (buildExpenses picks the one that
    // is true for the rows actually imported).
    var pIx=-1;groups.forEach(function(g,ix){if(g.personal)pIx=ix;});
    var noteOf=function(leftOut){return"Split from Amazon order "+o.id+" ("+fmtMoney(total)+") by category: "+groups.map(function(g,ix){return(g.personal?"personal items":g.cat)+" "+fmtMoney(parts[ix]/100)+(leftOut&&g.personal?" (left out)":"");}).join(" + ");};
    var splitNote=split?noteOf(false):null;
    var personalKey=pIx<0?null:split?"amazon:"+o.id+":personal":"amazon:"+o.id;
    groups.forEach(function(g,ix){
      var extra={};
      if(g.personal){
        var why=uniq(g.lines.map(function(l){return l.personal;}).filter(Boolean)).join(", ").replace(/, Amazon: /g,", ");
        extra={exclude:"personal",personalWhy:why,personalFlag:"Doesn't look like a business supply ("+why+") — keep it or delete it"};
      }else if(personalKey&&split)extra={personalKey:personalKey,splitNoteKept:noteOf(true)};
      out.push(makeCand(Object.assign({},base,{
        key:split?"amazon:"+o.id+":"+(g.personal?"personal":g.cat):"amazon:"+o.id,
        desc:"Amazon — "+clipWords(g.lines[0].title||"order "+o.id,60)+(g.lines.length>1?" (+"+(g.lines.length-1)+" more)":""),
        amount:parts[ix]/100,orderTotal:total,cat:g.cat,flags:flags.slice(),splitNote:splitNote,
        split:split?{index:ix,count:groups.length}:null,
        items:g.lines.map(function(l){var it={desc:l.title,qty:l.qty,amount:l.net};if(l.brand&&!(l.brand!==SOUTHWORTH&&brandOf(l.title)))it.brand=l.brand;return it;}),itemsFrom:"lines"
      },extra)));
    });
  });
  return out;
}

function rowObject(keys,row){var o={};keys.forEach(function(k,ix){o[k]=cellValue(row[ix]);});return o;}
// What a row with no item lines was for, as ONE item with no count (its
// share is the whole amount): a tracker email's subject — the vendor first
// when it is a service that says it (USPS → postage, even for a
// "Click-N-Ship Label"; PropertyShark → lists & data) — or a sheet's
// description. Never an Amazon email ("Ordered: 2 "Southworth…" and 15 more
// items": the order report itemizes it), and nothing when the text names no
// item ("Here's your Staples receipt"). A SHEET row's description is typed
// like any store's, Amazon too (review RD-2: "Amazon, Copy paper, 45.00,
// 2500" lost its item, so its units column never landed — his
// sister's-account buys come in that way).
function oneItem(c,text,from,vendorFirst){
  if(isAmazonish(c.vendor)&&from!=="description")return c;
  var t=(vendorFirst&&!isStoreGroup(vendorGroupOf(c.vendor))?itemTypeOf(c.vendor):null)||itemTypeOf(text);
  if(t){
    c.items=[{desc:oneLine(text),qty:null,amount:c.amount,type:t,noQty:true}];c.itemsFrom=from;
    // Money put on a Stamps.com balance (10/11/26): its own postage kind.
    if(t==="Postage"&&vendorGroupOf(c.vendor)==="Stamps.com")c.items[0].variety=POSTAGE_KINDS.BALANCE;
  }
  return c;
}
// A postal receipt's postage LINE (10/11/26 — point-in-time cost per
// letter): its kind and letter-stamp count from its price (stampLineFacts:
// a Coil/100 at the letter rate = 100 letter stamps a coil; a 29¢ School
// Bus is an add-on, never a letter). Stamps.com lines are its balance.
function postalLineKind(it,c){
  if(vendorGroupOf(c.vendor)==="Stamps.com"){it.variety=POSTAGE_KINDS.BALANCE;return it;}
  var a=numOrNull(it.amount);if(a==null||a<=0)return it;
  var q=numOrNull(it.qty);if(q==null||q<=0)q=1;
  var f=stampLineFacts(it.desc,q,Math.round(a*100/q),c.date);
  it.variety=f.kind;
  if(f.kind===POSTAGE_KINDS.LETTER&&f.stamps>0)it.units=f.stamps;
  return it;
}
function numOrNull(v){if(v==null||v==="")return null;var n=Number(v);return isFinite(n)?n:null;}
// The invoice tracker (inbox scrape). The first importer's rules, except
// that bills / notices / support replies are now imported AND flagged.
// The invoice export's "Invoice Log" headers → the tracker's own names.
function invoiceLogAlias(n){
  if(n==="vendor")return"from";
  if(n==="description")return"subject";
  if(/^amount(_\((usd|\$)\))?$/.test(n))return"amount";
  if(/^(receipt_file|file|file_path|receipt_file_path)$/.test(n))return"file_path";
  if(/^itemized(_detail)?\??$/.test(n))return"itemized";
  return n;
}
// A receipt Reece uploaded himself (a photo, an Uber delivery receipt) — the
// export marks its "Vendor" "Uber (uploaded receipt)", "Best Buy (uploaded
// receipt photo)".
var UPLOADED_RE=/\s*\(\s*uploaded\s+receipt(\s+photo)?\s*\)\s*$/i;
// "Office Depot delivery via Uber - …" → the store it came from.
var DELIVERY_RE=/^(.+?)\s+delivery\s+(?:via|by|through)\s+(uber(?: eats)?|doordash|instacart|postmates|gopuff)\b/i;
var NOT_VISIBLE_RE=/items? (are |were )?not (visible|shown|legible|readable)|not visible in (the )?(photo|receipt)|illegible|can'?t (be )?read|cut off/i;
var NOT_VISIBLE_FLAG="Items not visible on the receipt — check what it was";
function trackerCandidates(rows,det){
  det=det||detectSheet(rows);
  // The tracker's own column names (file_path, amount_source…) — the
  // Invoice Log's are mapped onto them.
  var h=(det.header||[]).map(function(x){return cellValue(x).toLowerCase().replace(/\s+/g,"_");});
  var isLog=det.type==="invoiceLog";
  if(isLog)h=h.map(invoiceLogAlias);
  var objs=[];
  for(var i=det.headerIndex+1;i<rows.length;i++){
    var r=rows[i];if(!r||r.length<2)continue;
    objs.push(rowObject(h,r));
  }
  var noAmountCol=h.indexOf("amount")<0;
  var out=[];
  // The same email listed twice (the scrape picked it up twice): the same
  // file_path, or the same sender + amount at the identical second. Kept
  // once — the copy is left out ("repeat", Include anyway). Two charges
  // seconds apart with DIFFERENT timestamps are separate purchases
  // (PropertyShark sells one report at a time, $16.19 each, often several in
  // a row) and come in unflagged.
  var firstFp=new Map();var firstTs=new Map();var keyCount=new Map();
  var repeatOf=function(c,o){
    var fp=o.file_path||"";
    // (only a timestamp WITH a time of day: the invoice export's Invoice Log
    // has dates only — PropertyShark's $16.19 reports the same day are
    // separate purchases, not one email listed twice)
    var ms=/\d:\d\d/.test(String(o.date||""))?Date.parse(o.date):NaN;
    var ts=isFinite(ms)?Math.floor(ms/1000)+"|"+cents(c.amount)+"|"+oneLine(o.from).toLowerCase():null;
    var first=(fp&&firstFp.get(fp))||(ts&&firstTs.get(ts))||null;
    if(!first){if(fp)firstFp.set(fp,c);if(ts)firstTs.set(ts,c);}
    return first;
  };
  objs.forEach(function(o){
    var subj=oneLine(o.subject);
    var from=o.from||"";
    // An uploaded receipt (10/10/26): the vendor without "(uploaded receipt
    // photo)"; an Uber / DoorDash delivery is the STORE it came from —
    // "Office Depot (Uber delivery)" (its vendor family is Office Depot's),
    // never Uber's Travel & Meals.
    var up=UPLOADED_RE.exec(from);
    var fromClean=up?oneLine(from.slice(0,up.index)):from;
    var dm=up?DELIVERY_RE.exec(subj):null;
    var v,via=null;
    if(dm){var sv=vendorOf(dm[1]);via=titleCaseIfShouting(dm[2]).replace(/\b[a-z]/g,function(ch){return ch.toUpperCase();});v={vendor:(sv.known?sv.vendor:clipWords(titleCaseIfShouting(oneLine(dm[1])),30))+" ("+via+" delivery)",cat:sv.cat,known:true};}
    else{v=vendorOf(fromClean);if(!v.known){var vs=vendorOf(subj);if(vs.known)v=vs;}}
    var fpRaw=o.file_path||"";
    // The Invoice Log names the file "invoices/…" (the folder the export's
    // receipts/ came from): its key is the tracker's — "tracker:invoices/<file
    // name>" whatever folder it names — so an email already imported from
    // invoice_tracker.csv is the SAME email.
    var fp=isLog&&fpRaw?"invoices/"+baseName(fpRaw):fpRaw;
    var key=fp?"tracker:"+fp:"tracker:email-"+hashStr([o.date,from,subj,o.amount].join("|"));
    var date=parseDate(o.date);
    // (a date with no time of day — the Invoice Log's — is that day's noon,
    // never UTC midnight, which is the evening before in Arizona)
    var at=(/\d:\d\d/.test(String(o.date||""))?Date.parse(o.date):NaN)||noonMs(date);
    var preview=o.preview||"";
    var base={key:key,source:"tracker",date:date,at:at,vendor:v.vendor,from:from,subject:subj,
      desc:clipWords(v.vendor+" — "+subj,120),fileName:fp?baseName(fp):null,filePath:fp||null,
      orderIds:orderIdsIn(subj,preview,fp),cat:categorize({vendor:v.vendor,text:subj+" "+preview})};
    if(up){base.uploaded=true;base.badge=/photo/i.test(up[1]||"")||/\.(jpe?g|png|webp|heic)$/i.test(fp)?"Receipt photo":"Uploaded receipt";if(via)base.deliveryVia=via;}
    if(isLog&&/^(yes|y|true|1)$/i.test(oneLine(o.itemized)))base.itemizedInLog=true;
    if(FAILED.test(subj)){out.push(makeCand(Object.assign(base,{exclude:"failed",amount:parseMoney(o.amount)})));return;}
    var amt=noAmountCol?null:parseMoney(o.amount);
    if(amt==null||Math.abs(amt)<0.005){out.push(makeCand(Object.assign(base,{exclude:"noAmount",amount:amt})));return;}
    // Support replies quoting a refund carry the amount as positive.
    var support=/support\]|^re:/i.test(subj);
    // …but a support reply whose amount was read off a "Total" line (not a
    // refund line) quotes the ORDER's total, not what was refunded
    // (PropertyShark 4/8: two "Re: Refund Processed" replies at the $541.45
    // order total, while the refunds themselves are their own -$54.10
    // receipt emails) — left out ("quoted", Include anyway), never a
    // -$541.45 refund (10/9/26 fix pass).
    var refundSubj=/refund/i.test(subj)&&amt>0;
    var quoted=refundSubj&&support&&!/refund|credit/i.test(o.amount_source||"");
    if(refundSubj)amt=-amt;
    var flags=[];
    if(support)flags.push(FLAG.support);
    // A receipt photo whose items can't be made out (the UPS Store $129.96):
    // in, flagged — what it was is his call.
    if(base.uploaded&&NOT_VISIBLE_RE.test(subj+" "+(o.amount_source||"")))flags.push(NOT_VISIBLE_FLAG);
    var notice=NOTICE_SOURCE.test(o.amount_source||"")||NOTICE_SUBJECT.test(subj);
    var srcLbl=String(o.amount_source||"").replace(/^body:/,"").replace(/'/g,"").trim();
    // (the end of this flag depends on whether its payment email is in the
    // tracker too — worked out below, once every row is read)
    var noticeHead=notice?"Bill or notice"+(NOTICE_SOURCE.test(srcLbl)?" (\""+srcLbl+"\")":"")+", not a payment receipt":null;
    if(notice)flags.push(noticeHead);
    var c=oneItem(makeCand(Object.assign(base,{amount:amt,flags:flags,notice:notice,support:support,noticeHead:noticeHead})),subj,"subject",true);
    if(quoted)Object.assign(c,{exclude:"quoted",excludeWhy:"Support reply quoting the order total ("+fmtMoney(Math.abs(amt))+" from its \""+(srcLbl||"Total")+"\" line), not a refund amount — refunds come in from their own receipt emails"});
    var first=repeatOf(c,o);
    // A personal purchase (a Kill Tony ticket, a streaming plan, a pet
    // store…) is left out like a personal Amazon item — listed, with
    // "Include anyway". An Amazon email naming one item of several ("… and 5
    // more items") isn't: its other items may be supplies. A one-item Amazon
    // email is only a hint (personalHint): when the report is in the upload
    // the email is that order's copy and the report decides item by item
    // (never two "personal items" for one purchase); planImport leaves it out
    // as personal only when no order matches it.
    var per=/\bmore items?\b|\bother items?\b|\d+\W*items?\b/i.test(subj)?null:looksPersonal({text:subj+" "+from});
    if(quoted){
      // (left out above; listed twice, its copy keeps a key of its own)
      if(first){var nq=(keyCount.get(c.key)||1)+1;keyCount.set(c.key,nq);if(c.key===first.key)c.key=c.key+"#"+nq;c.repeatOf=first.key;}
    }else if(first){
      var n=(keyCount.get(c.key)||1)+1;keyCount.set(c.key,n);
      if(c.key===first.key)c.key=c.key+"#"+n;   // the same file_path → the same key
      c.exclude="repeat";c.repeatOf=first.key;
      c.repeatWhy="Same email listed twice in the tracker ("+[first.date,first.vendor,fmtMoney(first.amount)].join(" · ")+")";
    }else if(per&&isAmazonish(v.vendor)){
      Object.assign(c,{personalHint:per,cat:"Other"});
    }else if(per){
      Object.assign(c,{exclude:"personal",cat:"Other",personalWhy:per,personalFlag:"Doesn't look like a business supply ("+per+") — keep it or delete it"});
    }
    out.push(c);
  });
  // A bill and its receipt (or a reminder for the same bill) a few days
  // apart: same vendor + amount within 10 days when either one is a notice —
  // the non-notice side is flagged too.
  var live=out.filter(function(c){return!c.exclude;});
  // flagPartners[flag] = the keys of the other row(s) a pairing flag is
  // about, so finalizeImport can drop it when Reece unticks that row.
  var partner=function(row,flag,key){var m=row.flagPartners||(row.flagPartners={});var l=m[flag]||(m[flag]=[]);if(l.indexOf(key)<0)l.push(key);};
  var groups=new Map();
  live.forEach(function(c){var k=c.vendor+"|"+cents(c.amount);var g=groups.get(k);if(!g){g=[];groups.set(k,g);}g.push(c);});
  var paidBy=new Map();   // notice → its payment emails in the tracker
  var addPaid=function(n,row){var l=paidBy.get(n);if(!l){l=[];paidBy.set(n,l);}if(l.indexOf(row)<0)l.push(row);};
  groups.forEach(function(g){
    if(g.length<2)return;
    var notices=g.filter(function(c){return c.notice;});
    if(!notices.length)return;
    g.forEach(function(row){
      if(row.notice)return;
      var best=null,bestDays=Infinity;
      notices.forEach(function(p){var days=Math.abs(row.at-p.at)/86400000;if(days<=10){addPaid(p,row);if(days<bestDays){best=p;bestDays=days;}}});
      if(best){var d=Math.round(bestDays);var t="Same vendor and amount as a bill/notice "+d+" day"+(d===1?"":"s")+" "+(best.at<row.at?"earlier":"later")+" ("+best.date+") — make sure only one is counted";row.flags.push(t);partner(row,t,best.key);}
    });
  });
  // …or a payment email naming the same invoice number ("#CLDHHC-00003").
  var invNos=function(c){var out=[];var re=/#\s?([A-Z0-9][A-Z0-9-]{3,})/gi;var m;while((m=re.exec(c.subject||"")))out.push(m[1].toUpperCase());return out;};
  live.forEach(function(n){
    if(!n.notice)return;var mine=invNos(n);if(!mine.length)return;
    live.forEach(function(row){if(!row.notice&&!row.support&&row.vendor===n.vendor&&invNos(row).some(function(x){return mine.indexOf(x)>=0;}))addPaid(n,row);});
  });
  // The notice's own flag says whether its payment is in the tracker too —
  // never that "the charge has its own receipt" when no such email is here
  // (Supabase 4/8 "New invoice … Amount Due" $25.00 is the only record of
  // that charge: no "Payment received" email for #CLDHHC-00003). A
  // reminder of a charge still to come is a doubt the other way round. If
  // its payment email is unticked at Add, the "no payment email" wording is
  // used instead (flagAlt).
  out.forEach(function(c){
    if(!c.notice||!c.noticeHead)return;
    var ix=c.flags.indexOf(c.noticeHead);if(ix<0)return;
    var upcoming=/will renew|renewal (notice|reminder)|upcoming (payment|charge|renewal)/i.test(c.subject||"");
    var none=c.noticeHead+(upcoming?" — a reminder of a coming charge, and no payment email for it is in the tracker: keep it only if your card shows the charge"
      :" — no payment email for it is in the tracker; keep it unless your card shows no charge");
    var paid=(paidBy.get(c)||[]).slice().sort(function(a,b){return Math.abs(a.at-c.at)-Math.abs(b.at-c.at);});
    if(!paid.length||c.exclude){c.flags[ix]=none;return;}
    var t=c.noticeHead+" — its payment email ("+paid[0].date+") is in the tracker too; make sure only one is counted";
    c.flags[ix]=t;
    paid.forEach(function(p){partner(c,t,p.key);});
    (c.flagAlt||(c.flagAlt={}))[t]=none;
  });
  // A charge followed by a "payment failed / payment is due" notice for the
  // same amount (eFax 9/17 "Your eFax® invoice" $20.57, then 9/22 "payment
  // is due") may never have been paid: the notice itself is never imported,
  // so the email before it (0–10 days, same vendor, to the cent) is flagged —
  // the nearest one, once. Not when that email says it was paid ("Payment
  // received", a receipt — Supabase 9/08: a failure notice, the payment,
  // another failure notice, all one day).
  var DAY=86400000;
  var fails=out.filter(function(c){return c.exclude==="failed"&&cents(c.amount)>0;}).sort(function(a,b){return a.at-b.at;});
  var sameCharge=function(a,b){return a.vendor===b.vendor&&cents(a.amount)===cents(b.amount);};
  var failFlagged=new Set();
  fails.forEach(function(f){
    var target=null;
    live.forEach(function(row){
      if(row.exclude||failFlagged.has(row)||!sameCharge(row,f))return;
      var d=(f.at-row.at)/DAY;if(d<0||d>10)return;
      if(PAID_SUBJECT.test(row.subject||""))return;
      if(!target||row.at>target.at)target=row;
    });
    if(!target)return;
    failFlagged.add(target);
    var later=null;
    live.forEach(function(row){if(!row.exclude&&row!==target&&!row.notice&&sameCharge(row,f)&&row.at>f.at&&(row.at-f.at)/DAY<=10&&(!later||row.at<later.at))later=row;});
    target.flags.push("A \"payment failed / payment due\" notice for this amount followed on "+f.date+" — check it was paid"+(later?" (the "+later.date+" email may be the real charge — keep only one)":"; delete this if it never was"));
    f.excludeWhy="Follows the "+[target.date,target.vendor,fmtMoney(target.amount)].join(" · ")+" email, which is flagged to check it was paid";
  });
  // A purchase cancelled and refunded the SAME day (10/10/26 — Vistaprint
  // 4/10: the $995.29 order was cancelled and refunded that day, then bought
  // again on 4/11 for $1,190.35): the charge never really happened, so both
  // emails are left out TOGETHER ("refundedSameDay" — Include anyway on
  // either brings both back, flagged). Same vendor, the refund exactly the
  // charge, the same day, a refund / cancellation email; one-to-one; never a
  // bill, notice or support reply.
  var buys=new Map();
  out.forEach(function(c){
    if(c.exclude||c.notice||c.support||!c.date||cents(c.amount)<=0)return;
    var k=vendorFamily(c.vendor)+"|"+c.date+"|"+cents(c.amount);var l=buys.get(k);if(!l){l=[];buys.set(k,l);}l.push(c);
  });
  out.forEach(function(r){
    if(r.exclude||r.notice||r.support||!r.date||cents(r.amount)>=0)return;
    if(!/refund|cancel|credit|return|revers|void/i.test(r.subject||""))return;
    var fam=vendorFamily(r.vendor);if(!fam)return;
    var l=buys.get(fam+"|"+r.date+"|"+(-cents(r.amount)));if(!l||!l.length)return;
    var b=l.shift();
    var why="Cancelled and refunded the same day ("+[r.date,b.vendor].join(" · ")+": "+fmtMoney(b.amount)+" charged, "+fmtMoney(r.amount)+" refunded) — the two cancel out";
    [b,r].forEach(function(c,ix){
      var other=ix?b:r;
      Object.assign(c,{exclude:"refundedSameDay",excludeWhy:why,refundPair:other.key,
        pairFlag:"Cancelled and refunded the same day — this "+(ix?"refund":"charge")+" and its "+(ix?"charge":"refund")+" ("+fmtMoney(other.amount)+") cancel out; keep both or delete both"});
    });
  });
  return out;
}

// ── Item lines from a sheet (10/10/26) ───────────────────────
// The invoice export's "Itemized Details": one row per item line of a
// receipt — Receipt Date | Vendor | Receipt Total (USD) | Qty | Item | Unit
// Price (USD) | Line Total (USD) | Detail Source. It is an item SOURCE, not
// expenses: joinItemDetails puts each receipt's lines on the Invoice Log /
// tracker row it belongs to (date + vendor family + total to the cent).
// → [{date, vendor, family, total, totalC, lines:[{desc, qty, amount}],
// source}] — consecutive rows of the same date + vendor + total are one
// receipt. A line's amount = its Line Total, else qty × unit price, else the
// unit price; none when neither is there.
// A line's UPC / item number → its digits (6–14), else null (review RV-4).
function lineUpc(v){var u=oneLine(v).replace(/^=?"(.*)"$/,"$1").replace(/[\s-]/g,"");return/^\d{6,14}$/.test(u)?u:null;}
function itemSourceFamily(v){
  var s=oneLine(v).replace(UPLOADED_RE,"");
  var d=/\b(uber(?: eats)?|doordash|instacart|postmates|gopuff)\b/i.exec(s);
  return d?"via:"+normVendor(d[1]):vendorFamily(s);
}
function itemDetailReceipts(rows,det){
  det=det||detectSheet(rows);
  var h=(det.header||[]).map(normHeader);
  var at=function(re){for(var i=0;i<h.length;i++)if(re.test(h[i]))return i;return -1;};
  var C={date:at(/^(receipt )?date$/),vendor:at(/^vendor$/),total:at(/^receipt total( \((usd|\$)\))?$/),qty:at(/^(qty|quantity)$/),
    item:at(/^(item|item description|description)$/),unit:at(/^(unit price|price)( \((usd|\$)\))?$/),line:at(/^(line total|amount)( \((usd|\$)\))?$/),src:at(/^(detail )?source$/),
    upc:at(/^(upc|sku|upc ?\/ ?sku|item (number|no\.?|#))$/)};   // (an optional UPC column — a Staples 083514… UPC is Southworth; review RV-4)
  var get=function(r,i){return i>=0?cellValue(r[i]):"";};
  var out=[];var cur=null;
  for(var i=det.headerIndex+1;i<(rows||[]).length;i++){
    var r=rows[i]||[];
    var date=parseDate(C.date>=0?r[C.date]:null,{serial:true});
    var vendor=oneLine(get(r,C.vendor));var total=parseMoney(get(r,C.total));var desc=oneLine(get(r,C.item));
    if(!date&&!vendor&&total==null&&!desc)continue;
    var k=[date||"",vendor.toLowerCase(),total==null?"":cents(total)].join("|");
    if(!cur||cur.k!==k){cur={k:k,date:date,vendor:vendor,family:itemSourceFamily(vendor),total:total,totalC:total==null?null:cents(total),lines:[],source:oneLine(get(r,C.src))};out.push(cur);}
    if(!desc)continue;
    var q=parseFloat(String(get(r,C.qty)).replace(/,/g,""));var qty=isFinite(q)&&q>0?q:null;
    var unit=parseMoney(get(r,C.unit));var line=parseMoney(get(r,C.line));
    var ln={desc:desc,qty:qty,amount:line!=null?line:unit!=null?round2(unit*(qty||1)):null};var upc=lineUpc(get(r,C.upc));if(upc)ln.upc=upc;
    cur.lines.push(ln);
  }
  return out.filter(function(x){return x.lines.length&&x.date&&x.totalC!=null;}).map(function(x){delete x.k;return x;});
}
// A line that isn't an item but part of what was paid for it — a delivery
// / service fee, the tip, the tax, a promotion: folded into the items in
// proportion (the receipt's total already has them in).
var FEE_LINE_RE=/\b(delivery|service|bag|small order|busy|regulatory|checkout|processing|convenience|priority|express|expanded range) fees?\b|^fees?\b|\btip\b|\bgratuity\b|\bsales tax\b|^tax(es)?\b|\bpromotions?\b|\bpromo\b|\bdiscount\b|\bsavings\b|\buber one\b|\bcredits? applied\b/i;
// Store-receipt lines that are personal (10/10/26 — the Uber deliveries'
// snacks and drinks, a dog's treats, the High Noon at Fry's): left out with
// their exact share, the rest of the receipt comes in.
var STORE_PERSONAL_RE=/\b(snacks?|candy|candies|cookies?|crackers?|chips|pretzels?|crawlers|gummy|gummies|chocolates?|jerky|popcorn|cereal|soda|cola|coke|pepsi|sprite|gatorade|body ?armor|electrolytes?|sports drinks?|energy drinks?|juice|sparkling water|seltzers?|beer|wine|vodka|tequila|whiske?y|liquor|alcohol|high noon|white claw|twisted tea|dips?|salsa|queso|tostitos|doritos|cheetos|sun ?chips|trolli|pringles|oreos?|groceries|grocery|diphenhydramine|ibuprofen|acetaminophen|tylenol|advil|aleve|zyrtec|claritin|benadryl|sleep aids?|melatonin|vitamins?|dogs?|cats?|puppy|puppies|kittens?|pets?|treats?|denta ?stix|purina|pedigree|beggin'?)\b|\(personal\)/i;
// …and lines that MIGHT be personal (household things a store run picks up
// — a GE outdoor extension cord at Target): in, flagged.
var MAYBE_PERSONAL_RE=/\boutdoor\b|household|kitchen|\bbath(room)?\b|cleaning|laundry|\bgarden|\bpatio\b|holiday|christmas|halloween|\bdecor\b|light bulbs?|\bpaper towels?\b/i;
function personalLine(desc){
  var t=oneLine(desc);if(!t)return null;
  var p=looksPersonal({text:t});if(p)return p;
  if(STORE_PERSONAL_RE.test(t)&&(/\(personal\)/i.test(t)||!MARKETING_RE.test(withoutNotMarketing(t))))return"looks personal";
  return null;
}
function maybePersonalLine(desc){var t=oneLine(desc);return!!t&&MAYBE_PERSONAL_RE.test(t)&&!MARKETING_RE.test(withoutNotMarketing(t));}
// One receipt's lines on its Invoice Log / tracker row → [the row] or its
// parts. The lines say what was bought (its Items line — "Paper $116.91 (9)",
// postage at a post office counter, …) and its category; a receipt whose
// lines fall in different categories is SPLIT like an Amazon order / a read
// receipt (Best Buy: the desktop under Office/Equipment, Microsoft 365 under
// Software/Subscriptions) — key "tracker:<file>::<category>", `splitOf` the
// row's own key, the parts summing EXACTLY to the total (fees, tip, tax,
// promotions spread in proportion; a "% off" / "Buy 2 get 1" line counts
// against the line before it). Personal lines (snacks, drinks, a dog's
// treats, alcohol) are a part of their own, LEFT OUT (exclude "personal",
// key "…::personal"); all personal → the whole row is left out. A household
// line that might be personal stays in, flagged. Amazon emails keep their
// lines only as a description (cut-short titles — the order report
// itemizes them).
function applyItemLines(c,rec){
  var lines=(rec.lines||[]).map(function(l){var o={desc:l.desc,qty:l.qty,amount:l.amount};if(l.upc)o.upc=l.upc;return o;});
  if(lines.length===1&&lines[0].amount==null)lines[0].amount=c.amount;
  // A $0 line nothing can type (a bundled "Norton 360 (bundle)" at $0) adds
  // nothing but an "Other $0.00" to the Items line.
  lines=lines.filter(function(l){return!(l.amount!=null&&!cents(l.amount)&&!itemTypeOf(l.desc));});
  var base=Object.assign({},c,{itemized:true,itemSource:rec.source||"item lines"});
  var named=function(ls){return uniq(ls.map(function(l){return clipWords(l.desc,40);})).slice(0,3).join(", ")+(ls.length>3?" +"+(ls.length-3)+" more":"");};
  if(isAmazonish(c.vendor)){
    // (the order report decides an Amazon email's category and items; its
    // lines only say when everything on it looks personal)
    base.items=lines;base.itemsFrom=c.itemsFrom||null;
    var biz=lines.filter(function(l){return!personalLine(l.desc);});
    if(lines.length&&!biz.length&&!/\bmore items?\b|\d+\W*items?\b/i.test(c.subject||""))base.personalHint=base.personalHint||("items look personal: "+named(lines));
    return[makeCand(base)];
  }
  if(!lines.length)return[makeCand(base)];
  var postal=!!POSTAGE_FAMILIES[vendorFamily(c.vendor)];
  var groups=[];var byCat={};var last=null;var pers=null;var maybe=[];var anyNull=false;
  lines.forEach(function(l){
    var a=l.amount==null?null:cents(l.amount);
    var t=itemTypeOf(l.desc);
    if(!t&&FEE_LINE_RE.test(l.desc))return;   // a fee / tip / tax line: spread
    if(a!=null&&a<0){if(last&&itemDiscountLine(l.desc)){last.w+=a;last.lines.push(l);}return;}   // a discount on the line before, or spread
    if(a==null)anyNull=true;
    var p=personalLine(l.desc);
    var it={desc:l.desc,qty:l.qty,amount:l.amount};if(l.upc)it.upc=l.upc;
    if(postal&&!t)it.type="Postage";   // a post office counter: the booklets ARE stamps
    if(postal&&(it.type||t)==="Postage"&&!p)postalLineKind(it,c);   // letter stamps vs add-ons, by price (10/11/26)
    var g;
    if(p){if(!pers){pers={cat:"Other",personal:true,w:0,pw:0,lines:[],why:[]};}g=pers;if(pers.why.indexOf(p)<0)pers.why.push(p);}
    else{
      var cat=postal?"Marketing":receiptLineCat(l.desc,c.cat);
      g=byCat[cat];if(!g){g={cat:cat,w:0,pw:0,lines:[]};byCat[cat]=g;groups.push(g);}
      if(maybePersonalLine(l.desc))maybe.push({g:g,l:l});
    }
    g.w+=a||0;g.lines.push(it);last=g;
    if(!p&&(it.type||t)==="Postage")g.pw+=a||0;   // stamps carry no tax (spreadUntaxedPostage)
  });
  var totalC=cents(c.amount);
  // Can't be split honestly (an unpriced line among several groups, a $0 /
  // negative total): its lines describe it, nothing more.
  var parts=groups.slice();if(pers)parts.push(pers);
  if(!parts.length)return[makeCand(base)];
  // A group with nothing priced in it (a $0 bundled item in its own
  // category) rides with the biggest one.
  if(parts.length>1){
    var big=parts.slice().sort(function(a,b){return b.w-a.w;})[0];
    parts=parts.filter(function(g){if(g!==big&&g.w<=0&&!g.personal){big.lines=big.lines.concat(g.lines);return false;}return true;});
  }
  var trusted=!anyNull&&trustedLines(c.vendor,"payment receipt",lines);
  var mf=function(g){var m=maybe.filter(function(x){return x.g===g;}).map(function(x){return x.l;});
    return m.length?"Might be personal: "+named(m)+" — keep it if it was for the business, or take "+(m.length===1?"its":"their")+" cost out of the amount":null;};
  if(totalC<=0||(anyNull&&parts.length>1)||parts.some(function(g){return g.w<0;})){
    base.items=lines;base.itemsFrom=null;return[makeCand(base)];
  }
  if(parts.length===1){
    var g0=parts[0];
    if(g0.personal){
      var w0=g0.why.join(", ");
      return[makeCand(Object.assign(base,{items:g0.lines,itemsFrom:trusted?"lines":null,cat:"Other",exclude:"personal",personalWhy:w0,personalFlag:"Doesn't look like a business supply ("+w0+") — keep it or delete it"}))];
    }
    var f0=mf(g0);
    return[makeCand(Object.assign(base,{items:g0.lines,itemsFrom:trusted?"lines":null,cat:g0.cat,flags:f0?(base.flags||[]).concat([f0]):(base.flags||[]).slice()}))];
  }
  parts.sort(function(a,b){return(a.personal?1:0)-(b.personal?1:0)||a.cat.localeCompare(b.cat);});
  // (the tax / fees / tip go on what was taxed — the stamps keep their face
  // value: Fry's 4/17 = Postage $15.60 + the High Noon $28.15, tax and all)
  var cs=spreadUntaxedPostage(totalC,parts.map(function(g){return Math.max(0,g.w);}),parts.map(function(g){return g.pw||0;}));
  if(!cs){base.items=lines;base.itemsFrom=null;return[makeCand(base)];}
  var what=(c.fileName||"receipt");
  var label=function(g){return g.personal?"personal items":g.cat;};
  var noteOf=function(leftOut){return"Split from "+(c.vendor||"the")+" receipt "+what+" ("+fmtMoney(c.amount)+") by category: "+parts.map(function(g,i){return label(g)+" "+fmtMoney(cs[i]/100)+(leftOut&&g.personal?" (left out)":"");}).join(" + ");};
  var personalKey=pers?c.key+TRACKER_PART_SEP+"personal":null;
  return parts.map(function(g,i){
    var first=clipWords(g.lines[0].desc,60);var n=g.lines.filter(function(l){return cents(l.amount)>=0;}).length;
    var o=Object.assign({},base,{key:c.key+TRACKER_PART_SEP+(g.personal?"personal":g.cat),cat:g.cat,amount:cs[i]/100,items:g.lines.slice(),itemsFrom:trusted?"lines":null,
      desc:clipWords(c.vendor+" — "+first+(n>1?" (+"+(n-1)+" more)":""),120),flags:(c.flags||[]).slice(),
      split:{index:i,count:parts.length},splitOf:c.key,splitTotal:c.amount,splitNote:noteOf(false),splitDesc:c.desc});
    if(g.personal){var w=g.why.join(", ");Object.assign(o,{cat:"Other",exclude:"personal",personalWhy:w,personalFlag:"Doesn't look like a business supply ("+w+") — keep it or delete it"});}
    else{if(personalKey)Object.assign(o,{personalKey:personalKey,splitNoteKept:noteOf(true)});var f=mf(g);if(f)o.flags.push(f);}
    return makeCand(o);
  });
}
// Item receipts (itemDetailReceipts) onto the rows they belong to: an
// Invoice Log / tracker row of the same vendor family (an Uber delivery by
// "Uber", Amazon = Amazon.com = return@amazon.com, StaplesReceipt@… =
// Staples), the same total to the cent (±1¢) and the same day (±1) — best
// pair first, one-to-one. → {candidates (the rows with their lines, a split
// row as its parts), joined: how many receipts found their row, unmatched:
// the receipts that didn't}. A row already itemized is never joined again.
function joinItemDetails(candidates,receipts){
  var cands=candidates||[];var recs=(receipts||[]).filter(function(r){return r&&r.lines&&r.lines.length&&r.date&&r.totalC!=null;});
  if(!recs.length)return{candidates:cands,joined:0,unmatched:[]};
  var byFam=new Map();
  cands.forEach(function(c,ix){
    if(!c||c.source!=="tracker"||c.ready||c.itemized||c.exclude||!c.date||c.amount==null||String(c.key).indexOf(TRACKER_PART_SEP)>0)return;
    var f=c.deliveryVia?"via:"+normVendor(c.deliveryVia):vendorFamily(c.vendor);if(!f)return;
    var l=byFam.get(f);if(!l){l=[];byFam.set(f,l);}l.push(ix);
  });
  var pairs=[];
  recs.forEach(function(r,ri){
    (byFam.get(r.family)||[]).forEach(function(ix){
      var c=cands[ix];var d=Math.abs(cents(c.amount)-r.totalC);var days=dayDiff(c.date,r.date);
      if(d<=1&&days<=1)pairs.push({ri:ri,ix:ix,score:days*10+d});
    });
  });
  pairs.sort(function(a,b){return a.score-b.score||a.ri-b.ri||a.ix-b.ix;});
  var usedR=new Set(),usedC=new Map();
  pairs.forEach(function(p){if(usedR.has(p.ri)||usedC.has(p.ix))return;usedR.add(p.ri);usedC.set(p.ix,recs[p.ri]);});
  var out=[];
  cands.forEach(function(c,ix){var r=usedC.get(ix);if(r)applyItemLines(c,r).forEach(function(x){out.push(x);});else out.push(c);});
  return{candidates:out,joined:usedR.size,unmatched:recs.filter(function(r,ri){return!usedR.has(ri);})};
}

var CARD_PAYMENT_RE=/\b(payment|autopay|auto pay|thank you|ach pmt|online pmt|pymt)\b/i;
// Any other sheet, through a column mapping (guessMapping / the UI). Purchases
// positive; a negative is a refund / credit — except a card payment, which
// is money moving between accounts (excluded, counted).
function genericCandidates(rows,det,mapping,opts){
  det=det||detectSheet(rows);opts=opts||{};
  var m=Object.assign({},det.mapping||{},mapping||{});
  var get=function(r,i){return i!=null&&i>=0?cellValue(r[i]):"";};
  var fileName=opts.fileName||"";
  var fallbackDate=opts.fallbackDate||todayIso();
  // The vendor for rows whose own cells don't name one: what Reece typed in
  // the column picker ("Vendor for every row"), else — only for a sheet with
  // no vendor AND no description column (a store order history: date,
  // order #, total, status) — a known vendor in the file name ("Staples
  // order history.csv"), else none ("Unknown", flagged).
  var fixed=oneLine(m.fixedVendor||"");
  var fv=fixed?vendorOf(fixed):null;
  var noWho=!(m.vendor>=0)&&!(m.description>=0);
  var nv=noWho?vendorOf(baseName(fileName).replace(/\.[a-z0-9]+$/i,"").replace(/[_\-.\d]+/g," ")):null;
  var sheetVendor=fixed?(fv.known?fv.vendor:clipWords(titleCaseIfShouting(fixed),40)):(nv&&nv.known?nv.vendor:"");
  var noWhat=!(m.description>=0)&&!(m.category>=0);
  var seen=new Map();
  var out=[];
  for(var i=det.headerIndex+1;i<rows.length;i++){
    var r=rows[i]||[];
    var rawDate=m.date>=0?r[m.date]:null;
    var date=parseDate(rawDate,{serial:true});
    var amt=parseMoney(get(r,m.amount));
    if(m.credit>=0){var cr=parseMoney(get(r,m.credit));if(cr!=null&&cr){amt=(amt||0)-Math.abs(cr);}}
    if(amt!=null&&m.flipSign)amt=-amt;
    var vendorCell=get(r,m.vendor);var descCell=get(r,m.description);var catCell=get(r,m.category);
    var status=get(r,m.status);var type=get(r,m.type);
    // The row's own cells first: the vendor cell, else the description (a
    // card export's "STAPLES 00123"). The sheet-level vendor only fills in
    // a row whose cells name nobody (or, typed by Reece, an unknown one).
    var v=vendorCell?vendorOf(vendorCell):vendorOf(descCell);
    var cellVendor=v.vendor||clipWords(descCell,40)||"";
    var fromSheet=!!sheetVendor&&(!cellVendor||(!!fixed&&!vendorCell&&!v.known));
    var vendor=fromSheet?sheetVendor:(cellVendor||"Unknown");
    var what=descCell&&oneLine(descCell)!==oneLine(vendorCell)?descCell:"";
    var desc=clipWords(vendor+(what?" — "+what:""),120);
    // The key comes from the row's own cells, so setting the sheet-level
    // vendor in the picker doesn't make the same rows look new.
    var sig=[date||"",cents(amt),(cellVendor||"unknown").toLowerCase(),oneLine(what).toLowerCase()].join("|");
    var occ=(seen.get(sig)||0)+1;seen.set(sig,occ);
    var key="sheet:"+hashStr(sig)+(occ>1?"#"+occ:"");
    var base={key:key,source:"sheet",date:date||fallbackDate,vendor:vendor,desc:desc,sheetName:fileName,
      orderIds:orderIdsIn(descCell,vendorCell),cat:categorize({vendor:vendor,text:[what,catCell].filter(Boolean).join(" ")})};
    if(!date&&!amt&&!vendorCell&&!descCell)continue;   // a blank-ish row
    if(!date&&/^(grand )?total|sub ?total|^balance|^summary/i.test(descCell||vendorCell)){out.push(makeCand(Object.assign(base,{exclude:"noAmount",amount:amt,summaryRow:true})));continue;}
    if(/cancel/i.test(status)){out.push(makeCand(Object.assign(base,{exclude:"cancelled",amount:amt})));continue;}
    if(/declin|fail|reject|void/i.test(status)||FAILED.test(type)){out.push(makeCand(Object.assign(base,{exclude:"failed",amount:amt})));continue;}
    if(amt==null||Math.abs(amt)<0.005){out.push(makeCand(Object.assign(base,{exclude:"noAmount",amount:amt})));continue;}
    if(amt<0&&(/^payment$/i.test(type)||CARD_PAYMENT_RE.test(descCell+" "+vendorCell))&&!/refund|return|credit adj/i.test(descCell+" "+type)){
      out.push(makeCand(Object.assign(base,{exclude:"cardPayment",amount:amt})));continue;
    }
    var flags=[];
    if(!date)flags.push(FLAG.noSheetDate);
    if(vendor==="Unknown")flags.push(FLAG.noVendor);
    else if(fromSheet&&noWhat)flags.push(FLAG.noItems);
    // A personal service on a card line (streaming, a ticket, a pet store) is
    // left out like a personal Amazon item — listed, with "Include anyway".
    var ps=personalService([vendorCell,descCell].join(" "));
    if(ps){out.push(makeCand(Object.assign(base,{amount:amt,flags:flags,cat:"Other",exclude:"personal",personalWhy:ps,personalFlag:"Doesn't look like a business supply ("+ps+") — keep it or delete it"})));continue;}
    var made=what?oneItem(makeCand(Object.assign(base,{amount:amt,flags:flags})),what,"description",false):makeCand(Object.assign(base,{amount:amt,flags:flags}));
    // Its count (10/11/26): the units column, else one the description
    // names ("500 envelopes", "Box of 500").
    var it0=made.items&&made.items.length===1?made.items[0]:null;
    var un=it0?unitNameOf(it0.type||itemTypeOf(it0.desc)):null;
    if(it0&&un){
      var uc=m.units!=null&&m.units>=0?parseInt(String(get(r,m.units)).replace(/[,\s]/g,""),10):NaN;
      var cnt=isFinite(uc)&&uc>0?uc:packCountOf(what,un);
      if(cnt)it0.units=cnt;
    }
    out.push(made);
  }
  return out;
}

// ── The "LoanFlow ready file" (10/9/26, later) ───────────────
// Reece: the easiest "pick and play". One CSV of everything an import would
// add — already decided (personal items out, Amazon emails merged into their
// orders, receipts attached, flags worked out) — that he can open in Excel
// to delete rows or change a category / subcategory, then drop together
// with the receipt PDFs it names into Expenses → Import and click Add.
// UTF-8 with a BOM, every field quoted, CRLF (Excel opens it cleanly):
//   Date        YYYY-MM-DD (Excel's own M/D/YYYY reads back too)
//   Vendor, Description, Category
//   Subcategory a Marketing row's subcategories, biggest first ("Paper +
//               Envelopes"); blank outside Marketing
//   Amount      a plain number
//   Items       the "Items:" line without "Items: "
//   Review      the ⚑ reasons, joined " | " (blank when clean)
//   Receipt file  the PDF it attaches to ("Staples 620.pdf", "VP1.pdf")
//   Source      Amazon order (order total $X) / Tracker email / Receipt PDF
//               (receipt total $X — a part of a receipt split by category) /
//               Sheet; "· included anyway" on a row Reece brought back with
//               Include anyway (it stays in when the file is read back)
//   Source key  the engine's key, + the keys merged into it (" + "), so the
//               ready file again — or the ORIGINAL files later — are
//               recognised as already imported.
// The last line(s) are the MANIFEST (Source "Manifest — rows" / "Manifest —
// left out: …", Source key = group keys): every row the file was made with
// (and the rows unticked before it was exported), and the Amazon emails left
// out as already in the order report whose order isn't coming in — so a row
// he DELETES in Excel is known when the file is read back.
// Read back (detectSheet → "loanflowReady" → readyCandidates) each row is a
// candidate with its own key(s), category, Items line and flags, marked
// `ready`: planImport doesn't merge / pair / refund-flag those again (the
// file was made with them), but every LIVE-table rule still runs (already
// imported keys, exact copies of what's in Expenses, near-duplicates of his
// hand entries, the postage / mailhouse "replaced" flags, software he logs
// by hand, deleted-before). His Excel edits win: a changed Category is used;
// a changed Subcategory (Marketing) rewrites a one-part Items line's type,
// or replaces a several-part line with one part of it; a changed Amount
// scales the Items line to it (to the cent) and flags "Amount changed in
// the ready file — check"; a row he added with no Source key comes in as a
// sheet row. A row he deletes (or whose Amount he clears) stays out, and Add
// remembers it on this computer (importDecisions), so a later drop of the
// cumulative tracker / Amazon report brings it back unticked and flagged,
// never as new. In ONE upload with the original files, the ready file wins
// (planImport: their rows are "in the ready file"). Receipt files attach by
// NAME — or by the SHA-256 in the row's receipt key, for a renamed copy — to
// the PDFs dropped with it (attachReadyFiles) — never read.
var READY_HEADER=["Date","Vendor","Description","Category","Subcategory","Amount","Items","Review","Receipt file","Source","Source key"];
var READY_FLAG={
  amountChanged:"Amount changed in the ready file — check",
  itemsDropped:"The Items in the ready file don't fit the amount — check what it went on",
  badAmountPrefix:"Couldn't read the Amount in the ready file",
  twice:"This row appears twice in the ready file — keep or delete one"
};
var READY_INCLUDED="included anyway";
// (10/10/26, later) a software invoice he chose over his hand entries for
// that vendor ("Use these invoices instead of my hand entries") — read back,
// the row stands in for them again (planImport's replacesHand).
var READY_REPLACES="replaces my hand entries";
var READY_MANIFEST_RE=/^manifest\b\s*(?:[—–-]\s*)?(.*)$/i;
var READY_TOTAL_RE=/\b(grand )?total\b|\bsub ?total\b|\bbalance\b|\bsummary\b|\bsum\b/i;
var READY_CHUNK=30000;   // Excel keeps 32,767 characters in a cell
// A text cell Excel would read as a formula (an email subject "=…", a
// tracker sender named "-1+cmd|…") gets a leading apostrophe in the file;
// reading it back takes it off. The rule is lib.js csvFormulaGuard — the
// one every CSV export uses: a "-" is left alone only when the WHOLE cell
// is a plain number (before, "-1+cmd|' /C calc'!A0" went out as it was).
function readyGuard(s){return csvFormulaGuard(s);}
function readyUnguard(s){s=String(s==null?"":s);return/^'[=+@\t\r-]/.test(s)?s.slice(1):s;}
function csvQuote(v){return'"'+String(v==null?"":v).replace(/"/g,'""')+'"';}
function normCell(s){return oneLine(s).toLowerCase();}
function rowKeysOf(r){return[r&&r.key].concat(((r&&r.mergedFrom)||[]).map(function(m){return m.key;})).filter(Boolean);}
// Rows (planImport / finalizeImport rows — the ticked ones) → the ready
// file's data rows (strings, READY_HEADER order).
function readyFileRows(rows){
  return(rows||[]).filter(function(r){return r&&r.on!==false&&!r.exclude;}).map(function(r){
    var line=candidateItemsLine(r);
    var src=r.badge||SOURCE_BADGES[r.source]||r.source||"";
    if(r.source==="amazon"&&r.orderTotal!=null)src+=" (order total "+fmtMoney(r.orderTotal)+")";
    else if((r.source==="receipt"||r.source==="tracker")&&r.splitOf&&r.splitTotal!=null)src+=" (receipt total "+fmtMoney(r.splitTotal)+")";
    if(r.forced)src+=" · "+READY_INCLUDED;
    if(r.replacesHand)src+=" · "+READY_REPLACES;
    return[r.date||"",r.vendor||"",r.desc||"",r.cat||"",subcategoryText({cat:r.cat,amount:r.amount,vendor:r.vendor,desc:r.desc,notes:line}),
      (cents(r.amount)/100).toFixed(2),line.replace(/^\s*items?\s*:\s*/i,""),(r.flags||[]).join(" | "),r.fileRef?(r.fileName||""):"",src,uniq(rowKeysOf(r)).join(" + ")];
  });
}
// The manifest line(s) (READY_HEADER order). rows: as readyFileRows takes
// them (an unticked one — on false — counts as taken out); opts: {omitted:
// more rows taken out (unticked in the preview), leftOut: [{key, why}] —
// the plan's "Amazon email already in the order report" entries}. Group
// keys, " + "-joined, a line per 30,000 characters.
function readyManifestRows(rows,opts){
  opts=opts||{};
  var made=[];var add=function(k){var g=groupKeyOf(k);if(g&&made.indexOf(g)<0)made.push(g);};
  var all=(rows||[]).concat(opts.omitted||[]);
  all.forEach(function(r){if(r&&!r.exclude)rowKeysOf(r).forEach(add);});
  var have=new Set(made);
  var left=new Map();   // why → group keys
  (opts.leftOut||[]).forEach(function(x){
    var g=x&&groupKeyOf(x.key);if(!g||have.has(g))return;have.add(g);
    // a plan.excluded entry: its reason's label (one line per reason, not per email)
    var why=(x.reason&&LEFT_OUT_BY[x.reason]?LEFT_OUT_BY[x.reason].one:oneLine(x.why))||"left out";
    var l=left.get(why);if(!l){l=[];left.set(why,l);}l.push(g);
  });
  var out=[];
  var lines=function(keys,source,desc){
    var chunks=[];var cur=[];var len=0;
    keys.forEach(function(k){if(cur.length&&len+k.length+3>READY_CHUNK){chunks.push(cur);cur=[];len=0;}cur.push(k);len+=k.length+3;});
    if(cur.length)chunks.push(cur);
    chunks.forEach(function(c,i){out.push(["","",desc,"","","","","","",source+(chunks.length>1?" ("+(i+1)+")":""),c.join(" + ")]);});
  };
  if(made.length)lines(made,"Manifest — rows","LoanFlow ready file: the rows it was made with. Keep this line: it's how the import knows which rows you deleted (they stay out, and a later drop of the original files won't bring them back as new)");
  left.forEach(function(keys,why){lines(keys,"Manifest — left out: "+why,"LoanFlow ready file: left out when it was made ("+why+"). Keep this line: a later drop of the original files won't bring them back as new");});
  return out;
}
// → the file's text: BOM + quoted header + rows + manifest, CRLF.
function readyFileCsv(rows,opts){
  var lines=[READY_HEADER].concat(readyFileRows(rows),readyManifestRows(rows,opts)).map(function(cells){return cells.map(function(c,i){return csvQuote(i===5?c:readyGuard(c));}).join(",");});
  return"﻿"+lines.join("\r\n")+"\r\n";
}
function splitReadyKeys(cell){return uniq(String(cell||"").split(/\s+\+\s+/).map(oneLine).filter(function(k){return KEY_RE.test(k);}));}
// The ready file's rows → {candidates (ready: true), info}. info (also on
// every candidate as `readyManifest` — the same object): {removed: group
// keys taken out of the file (deleted in Excel — the manifest says it had
// them — or its Amount cleared), leftOut: [{key, why}] (the manifest's
// left-out emails), covers: Set of every group key the file decides (its
// rows, the removed and left-out ones — planImport drops the ORIGINAL
// files' rows for those), manifest: has one, deleted / cleared: counts}.
function readySheet(rows,det,opts){
  det=det||detectSheet(rows);opts=opts||{};
  var col=headerIndexer(det);
  var C={date:col("date"),vendor:col("vendor"),desc:col("description"),cat:col("category"),sub:col("subcategory"),amount:col("amount"),items:col("items"),
    review:col("review"),file:col("receipt file"),source:col("source"),key:col("source key")};
  var fileName=opts.fileName||"ready file";
  var get=function(r,i){return i>=0?readyUnguard(cellValue(r[i])):"";};
  var catOf={};CATEGORIES.forEach(function(c){catOf[c.toLowerCase()]=c;});
  var out=[];var seen=new Map();var keyUse=new Map();
  var man={rows:[],leftOut:[]};var hasManifest=false;
  var placeholderRe=new RegExp("^("+[FLAG.unreadable,FLAG.noTotal,FLAG.partial].map(function(t){return t.replace(/[.*+?^${}()|[\]\\]/g,"\\$&");}).join("|")+")");
  for(var i=det.headerIndex+1;i<rows.length;i++){
    var r=rows[i]||[];
    var vendor=oneLine(get(r,C.vendor)),descCell=oneLine(get(r,C.desc)),desc=descCell||vendor,catCell=oneLine(get(r,C.cat)),keyCell=get(r,C.key);
    var srcCell=oneLine(get(r,C.source));
    // The manifest: not an expense — what the file was made with.
    var mm=READY_MANIFEST_RE.exec(srcCell);
    if(mm&&!vendor&&!oneLine(get(r,C.amount))){
      hasManifest=true;
      var mk=splitReadyKeys(keyCell).map(groupKeyOf);
      var lo=/\bleft out\b\s*:?\s*(.*?)(?:\s*\(\d+\))?$/i.exec(mm[1]);   // (an Excel save may mangle the dash before it)
      var loWhy=lo?oneLine(lo[1])||"left out":null;
      for(var mi=0;mi<mk.length;mi++){if(loWhy)man.leftOut.push({key:mk[mi],why:loWhy});else man.rows.push(mk[mi]);}
      continue;
    }
    var amountText=oneLine(get(r,C.amount));
    var amount=parseMoney(amountText);
    var rawDate=C.date>=0?r[C.date]:null;var date=parseDate(rawDate,{serial:true});
    var keys=splitReadyKeys(keyCell);
    if(!date&&!amountText&&!vendor&&!desc&&!keys.length&&!oneLine(keyCell))continue;   // a blank line
    // A total / sum line he added at the bottom to check the file (no key,
    // no date, and "Total" somewhere or nothing but an amount) — not an
    // expense, like a generic sheet's undated Total row.
    if(!keys.length&&!date&&(READY_TOTAL_RE.test([cellValue(rawDate),vendor,descCell,catCell].join(" "))||(!vendor&&!descCell))){
      out.push(makeCand({key:"sheet:"+hashStr("ready-total|"+fileName+"|"+i+"|"+amountText),source:"sheet",badge:SOURCE_BADGES.sheet,date:null,vendor:vendor,desc:descCell||"Total line ("+(amountText||"no amount")+")",
        amount:amount,cat:"Other",exclude:"noAmount",summaryRow:true,sheetName:fileName,excludeWhy:"A total / summary line in the ready file, not an expense"}));
      continue;
    }
    var flags=String(get(r,C.review)||"").split(/\s+\|\s+|\r?\n/).map(function(f){return oneLine(f).replace(/^⚑\s*/,"").replace(/^review:\s*/i,"");}).filter(Boolean);
    var cat=catOf[catCell.toLowerCase()];
    if(!cat){cat="Other";if(catCell)flags.push("Category \""+clipWords(catCell,40)+"\" in the ready file isn't one of Expenses' categories — it's filed under Other; check it");}
    if(!date){date=opts.fallbackDate||todayIso();flags.push(FLAG.noSheetDate);}
    // Its key(s): the engine's key first, then the ones merged into it.
    var key=keys[0];var copyOf=null;
    if(!key){   // a row he added in Excel: a sheet row of its own
      var sig=[date,cents(amount),vendor.toLowerCase(),desc.toLowerCase()].join("|");
      var occ=(seen.get(sig)||0)+1;seen.set(sig,occ);
      key="sheet:"+hashStr("ready|"+sig)+(occ>1?"#"+occ:"");keys=[key];
    }else{
      // A row copied in Excel (to split a purchase: change the two amounts
      // and categories) keeps the Source key: the copy gets a key of its own
      // (<key>#2 — the merged keys stay on the first) and is never counted
      // as "imported before".
      var n=(keyUse.get(key)||0)+1;keyUse.set(key,n);
      if(n>1){copyOf=key;key=key+"#"+n;keys=[key];}
    }
    var baseKey=copyOf||key;
    var source=key.split(":")[0];
    // (an uploaded receipt in the invoice export is a tracker row whose
    // Source says "Receipt photo" / "Uploaded receipt")
    var upB=source==="tracker"?/^(receipt photo|uploaded receipt)\b/i.exec(srcCell):null;
    var badge=source==="receipt"&&/photo/i.test(srcCell)?"Receipt photo":upB?(/photo/i.test(upB[1])?"Receipt photo":"Uploaded receipt"):SOURCE_BADGES[source];
    var receiptName=oneLine(get(r,C.file));
    var c={key:key,source:source,badge:badge,date:date,vendor:vendor,desc:desc,amount:amount,cat:cat,flags:flags,ready:true,readyFile:fileName,
      readyReceipt:receiptName||null,orderIds:[],
      mergedFrom:readyMerged(keys.slice(1),receiptName)};
    if(copyOf)c.readyCopyOf=copyOf;
    // "· included anyway": Reece brought it back with Include anyway before
    // the file was made — the live-table rules flag it instead of leaving it
    // out again.
    if(new RegExp("·\\s*"+READY_INCLUDED+"\\b","i").test(srcCell))c.forced=true;
    // "· replaces my hand entries": a software invoice he chose over his hand
    // entries for that vendor — it stands in for them again (unless he
    // unticks the choice in this preview).
    if(new RegExp("·\\s*"+READY_REPLACES+"\\b","i").test(srcCell)){var rf=vendorFamily(vendor);if(rf)c.replacesHand=rf;}
    if(source==="amazon"){
      var oid=(/^amazon:([^:#]+)/.exec(baseKey)||[])[1];
      var ot=/order total (-?\$[\d,]+\.\d{2})/i.exec(srcCell);
      Object.assign(c,{orderId:oid,orderIds:oid?[oid]:[],orderTotal:ot?parseMoney(ot[1]):null,status:"Closed",splitPart:baseKey.split(":").length>2});
    }else if(source==="tracker"){
      var tg=groupKeyOf(baseKey);
      var fp=tg.slice("tracker:".length).replace(/#\d+$/,"");
      if(!/^email-/.test(fp))c.filePath=fp;
      // a part of an email split by its item lines ("…::Marketing")
      if(tg!==baseKey){
        c.splitOf=tg;
        var tt=/receipt total (-?\$[\d,]+\.\d{2})/i.exec(srcCell);
        if(tt)c.readySplitTotal=parseMoney(tt[1]);
      }
      if(upB)c.uploaded=true;
    }else if(source==="receipt"){
      var seg=baseKey.split(":");
      if(seg.length>2&&catOf[String(seg[seg.length-1]).toLowerCase()])c.splitOf=seg.slice(0,-1).join(":");
      c.fileHash=seg[1];
      var rt=/receipt total (-?\$[\d,]+\.\d{2})/i.exec(srcCell);
      if(rt&&c.splitOf)c.readySplitTotal=parseMoney(rt[1]);
    }else if(source==="sheet")c.sheetName=fileName;
    // The file it attaches to: the Receipt file named, else (a tracker
    // email) its own PDF by the tracker's name — matched to what's dropped.
    c.fileName=receiptName||(c.filePath?baseName(c.filePath):null);
    if(flags.some(function(f){return/^Bill or notice/.test(f);}))c.notice=true;
    // An Amount that isn't a number ("$12.5O", "see receipt"): in at $0,
    // flagged — never dropped. A cleared Amount (blank, or 0 on a row that
    // isn't a $0 "couldn't read" placeholder): taken out of the file.
    if(amount==null&&amountText){amount=0;c.amount=0;flags.push(READY_FLAG.badAmountPrefix+" (\""+clipWords(amountText,30)+"\") — it came in at $0; fill it in");}
    else if(amount==null||(Math.abs(amount)<0.005&&!flags.some(function(f){return placeholderRe.test(f);}))){
      var isCleared=!copyOf&&source!=="sheet";
      out.push(makeCand(Object.assign(c,{exclude:"noAmount",readyCleared:isCleared,excludeWhy:isCleared?"Amount cleared in the ready file — taken out (Add remembers it, so a later drop of the original files doesn't bring it back as new)":""})));
      continue;
    }
    // Items: as he left them, split to the amount (a changed amount scales
    // them to the cent — and is flagged).
    var parts=null;var itemsCell=oneLine(get(r,C.items)).replace(/\s[?�]\s/g," · ");
    if(itemsCell&&Math.abs(amount)>=0.005){
      var line=ITEMS_PREFIX_RE.test(itemsCell)?itemsCell:"Items: "+itemsCell;
      var typed=parseItemsLine(line);
      if(typed.length){
        var got=expenseItemParts({amount:amount,vendor:vendor,desc:desc,cat:cat,notes:line,imported:true});
        if(got.length&&got[0].fromLine){
          parts=got.map(function(p){return{type:p.type,variety:p.variety||null,brand:p.brand||null,cents:p.cents,amount:p.amount,qty:p.qty,units:p.units,unitName:p.unitName};});
          if(typed.every(function(p){return p.amount!=null;})&&typed.reduce(function(s,p){return s+cents(p.amount);},0)!==cents(amount))flags.push(READY_FLAG.amountChanged);
        }else flags.push(READY_FLAG.itemsDropped);
      }
    }
    // Subcategory: his pick (Marketing rows) — a one-part line changes its
    // type, a several-part line (or none) becomes one part of it.
    var subCell=oneLine(get(r,C.sub));
    if(subCell&&Math.abs(amount)>=0.005){
      var now=subcategoryText({cat:"Marketing",amount:amount,vendor:vendor,desc:desc,notes:parts?formatItemsLine(parts):""});
      if(normCell(subCell)!==normCell(now)){
        var S=canonicalSubcategory(subCell);
        if(!S)flags.push("Subcategory \""+clipWords(subCell,40)+"\" in the ready file isn't one of Marketing's — check it");
        else if(cat!=="Marketing")flags.push("Subcategory \""+S+"\" is set but the category is "+cat+" — subcategories are Marketing's; check the category");
        else if(parts&&parts.length===1){
          var p0=parts[0];var un=unitNameOf(S);var keepUnits=!!un&&un===p0.unitName;
          parts=[{type:S,variety:S===p0.type?p0.variety||null:null,brand:S===p0.type?p0.brand||null:null,cents:p0.cents,amount:p0.amount,qty:p0.qty,units:keepUnits?p0.units:null,unitName:keepUnits?p0.unitName:null}];
        }else parts=[{type:S,variety:null,brand:null,cents:cents(amount),amount:round2(amount),qty:null,units:null,unitName:null}];
      }
    }
    if(parts){
      c.readyParts=parts;
      c.items=parts.map(function(p){return{desc:p.type,type:p.type,variety:p.variety||null,brand:p.brand||null,qty:p.qty,amount:p.amount,noQty:p.qty==null};});
      // (a count, the kind of paper / envelope — "Paper [Cotton]" — or its
      // brand — "[… · Southworth]" — is detail the row's description doesn't
      // carry: an item source for the backfill)
      c.itemsFrom=parts.some(function(p){return p.qty!=null||p.units!=null||!!p.variety||!!p.brand;})?"ready":"readyOne";
    }
    out.push(makeCand(c));
  }
  // A copy with the SAME amount as the row it was copied from is most likely
  // a slip (a split changes the amounts): both flagged.
  var byKey=new Map();out.forEach(function(c){if(!c.readyCopyOf&&!c.exclude)byKey.set(c.key,c);});
  out.forEach(function(c){
    if(!c.readyCopyOf||c.exclude)return;var o=byKey.get(c.readyCopyOf);
    if(o&&cents(o.amount)===cents(c.amount)){addFlag(o,READY_FLAG.twice);addFlag(c,READY_FLAG.twice);}
  });
  // Split Amazon orders / receipts: the whole (an order's / receipt's total
  // from its Source cell, else the sum of its rows here), and a split note.
  var groups=new Map();
  out.forEach(function(c){
    if(c.exclude||!c.ready)return;
    var g=c.source==="amazon"&&c.orderId?"amazon:"+c.orderId:c.splitOf?"split:"+c.splitOf:null;
    if(!g)return;var l=groups.get(g);if(!l){l=[];groups.set(g,l);}l.push(c);
  });
  groups.forEach(function(list,g){
    var sumC=list.reduce(function(s,c){return s+cents(c.amount);},0);
    var lead=list[0];var isAmz=g.indexOf("amazon:")===0;
    var totalC=(isAmz?list.map(function(c){return c.orderTotal;}):list.map(function(c){return c.readySplitTotal;})).filter(function(t){return t!=null;}).map(cents)[0];
    if(totalC==null)totalC=sumC;
    list.forEach(function(c){if(isAmz)c.orderTotal=totalC/100;});
    if(!(list.length>1||(isAmz?lead.splitPart:!!lead.splitOf)))return;
    var parts=list.slice().sort(function(a,b){return String(a.cat).localeCompare(String(b.cat));});
    var left=totalC-sumC;
    var note="Split from "+(isAmz?"Amazon order "+lead.orderId:(lead.vendor||"the")+" receipt "+(lead.readyReceipt||"receipt"))+" ("+fmtMoney(totalC/100)+") by category: "+
      parts.map(function(c){return c.cat+" "+fmtMoney(c.amount);}).join(" + ")+(left?" + "+fmtMoney(left/100)+" left out":"");
    list.forEach(function(c,ix){c.split={index:ix,count:list.length};c.splitNote=note;if(!isAmz)c.splitTotal=totalC/100;});
  });
  out.forEach(function(c){delete c.splitPart;delete c.readySplitTotal;});
  // What the file decides: its rows, the rows taken out of it (deleted —
  // the manifest had them — or their Amount cleared), the emails it left out.
  var present=new Set(),liveG=new Set();
  out.forEach(function(c){if(!c.ready)return;rowKeysOf(c).forEach(function(k){var g=groupKeyOf(k);present.add(g);if(!c.exclude)liveG.add(g);});});
  var removed=[];var deleted=0,cleared=0;
  man.rows.forEach(function(g){if(!present.has(g)&&removed.indexOf(g)<0){removed.push(g);deleted++;}});
  out.forEach(function(c){
    if(!c.readyCleared)return;var any=false;
    rowKeysOf(c).forEach(function(k){var g=groupKeyOf(k);if(!liveG.has(g)&&removed.indexOf(g)<0){removed.push(g);any=true;}});
    if(any)cleared++;
  });
  var leftOut=[];var lseen=new Set();
  man.leftOut.forEach(function(x){if(!liveG.has(x.key)&&!lseen.has(x.key)){lseen.add(x.key);leftOut.push(x);}});
  var covers=new Set();
  present.forEach(function(g){covers.add(g);});removed.forEach(function(g){covers.add(g);});
  man.rows.forEach(function(g){covers.add(g);});leftOut.forEach(function(x){covers.add(x.key);});
  var info={removed:removed,leftOut:leftOut,covers:covers,manifest:hasManifest,deleted:deleted,cleared:cleared,fileName:fileName};
  out.forEach(function(c){if(c.ready)c.readyManifest=info;});
  return{candidates:out,info:info};
}
function readyCandidates(rows,det,opts){return readySheet(rows,det,opts).candidates;}
var ITEMS_PREFIX_RE=/^\s*items?\s*:/i;
// The keys merged into a ready row → mergedFrom (a receipt's: its file name).
function readyMerged(keys,receiptName){return keys.map(function(k){var s=k.split(":")[0];return{source:s,key:k,detail:s==="receipt"&&receiptName?receiptName:null};});}
// The ready file's Receipt file names → the PDFs dropped with it, by name —
// or, a renamed copy ("VP1 (1).pdf", the same bytes), by the SHA-256 in the
// row's receipt key — never read. files: [{name, fileRef, hash}];
// existingExpenses (optional): a file whose rows are ALL already in
// Expenses (the ready file was imported before, without this PDF) goes to
// those expenses instead (`toExisting`, by "ready" — uploaded and attached
// where none is stored), never onto rows that are skipped.
// → {candidates (copies, fileRef / fileName set on the rows taking a file),
// claimed: the files the rows took, missing: names no dropped file matched
// (those rows come in without them — the PDFs can be dropped later: a
// receipt whose key is already stamped attaches to that expense,
// matchImportedReceipts), toExisting}.
function attachReadyFiles(candidates,files,existingExpenses){
  var byName=new Map(),byHash=new Map();
  (files||[]).forEach(function(f){
    var k=String((f&&f.name)||"").toLowerCase();if(k&&!byName.has(k))byName.set(k,f);
    var h=f&&(f.hash||f.fileRef);if(h&&!byHash.has(String(h)))byHash.set(String(h),f);
  });
  var hashesOf=function(c){return rowKeysOf(c).map(function(k){return(/^receipt:([^:#]+)/.exec(k)||[])[1];}).filter(Boolean);};
  var pick=new Map();var named=new Map();var gotName=new Set();
  (candidates||[]).forEach(function(c){
    if(!c||!c.ready)return;
    var f=null;var k=c.readyReceipt?c.readyReceipt.toLowerCase():null;
    if(k){if(!c.exclude&&!named.has(k))named.set(k,c.readyReceipt);f=byName.get(k)||null;}
    if(!f){var hs=hashesOf(c);for(var i=0;i<hs.length&&!f;i++)f=byHash.get(hs[i])||null;}
    // No Receipt file named: a tracker row's own PDF, by its file name.
    if(!f&&!k&&c.source==="tracker"&&c.fileName)f=byName.get(String(c.fileName).toLowerCase())||null;
    if(!f)return;
    pick.set(c,f);
    if(k&&!c.exclude)gotName.add(k);
  });
  var stamped=existingExpenses?keyIndex(existingExpenses):null;
  var rowsOf=new Map();pick.forEach(function(f,c){var l=rowsOf.get(f);if(!l){l=[];rowsOf.set(f,l);}l.push(c);});
  var toExisting=[];var goExisting=new Set();
  if(stamped&&stamped.size)rowsOf.forEach(function(list,f){
    if(list.some(function(c){return!stamped.has(groupKeyOf(c.key));}))return;
    var exps=[];list.forEach(function(c){(stamped.get(groupKeyOf(c.key))||[]).forEach(function(e){if(exps.indexOf(e)<0)exps.push(e);});});
    toExisting.push({file:f,expenses:exps,needs:exps.filter(function(e){return!e.receiptPath;}),by:"ready"});goExisting.add(f);
  });
  var claimed=[];
  var out=(candidates||[]).map(function(c){
    var f=pick.get(c);if(!f||goExisting.has(f))return c;
    if(claimed.indexOf(f)<0)claimed.push(f);
    return Object.assign({},c,{fileRef:f.fileRef||f.name,fileName:f.name});
  });
  var missing=[];named.forEach(function(n,k){if(!gotName.has(k))missing.push(n);});
  // (10/10/26) A dropped file the ready file KNOWS but has no row for: the
  // PDF of an email it left out (failed, no amount, personal, merged into an
  // Amazon order that has its own…) — its key is in the manifest or a row's
  // Source key — or a receipt merged into a row that carries its Outlook PDF
  // instead (its SHA-256 is in that row's keys). Not read (no Claude cost,
  // Import isn't held up), not uploaded: `unneeded` [{file, why}].
  var knownName=new Map(),knownHash=new Map();
  var learn=function(k,why){
    var g=groupKeyOf(k);var m;
    if((m=/^tracker:(.+)$/.exec(g))&&!/^email-/.test(m[1])){var b=baseName(m[1].replace(/#\d+$/,"")).toLowerCase();if(b&&!knownName.has(b))knownName.set(b,why);}
    else if((m=/^receipt:([^:#]+)/.exec(g))&&!knownHash.has(m[1]))knownHash.set(m[1],why);
  };
  var infos=[];
  (candidates||[]).forEach(function(c){
    if(!c||!c.ready)return;
    rowKeysOf(c).forEach(function(k){learn(k,"its row has its own file");});
    var i=c.readyManifest;if(i&&infos.indexOf(i)<0)infos.push(i);
  });
  infos.forEach(function(i){
    (i.leftOut||[]).forEach(function(x){learn(x.key,"left out when the ready file was made ("+(x.why||"left out")+")");});
    (i.removed||[]).forEach(function(g){learn(g,"taken out of the ready file");});
    if(i.covers)i.covers.forEach(function(g){learn(g,"the ready file doesn't use it");});
  });
  var taken=new Set();pick.forEach(function(f){taken.add(f);});
  var unneeded=[];
  (files||[]).forEach(function(f){
    if(!f||taken.has(f))return;
    var why=knownName.get(String(f.name||"").toLowerCase())||knownHash.get(String(f.hash||f.fileRef||""));
    if(why)unneeded.push({file:f,why:why});
  });
  return{candidates:out,claimed:claimed,missing:missing,toExisting:toExisting,unneeded:unneeded};
}
// Receipt files whose SHA-256 is already stamped in Expenses (imported
// before — from the ready file, or read before) → never read again; the
// file goes on those expenses that have no stored receipt (shaped like
// matchTrackerFiles' toExisting). A file read only in part before (its
// "…:unread" placeholder is there) is read again, as always.
// files: [{name, fileRef, hash}] → {toExisting: [{file, expenses, needs, by}], rest}
function matchImportedReceipts(files,existingExpenses){
  var idx=keyIndex(existingExpenses);
  var unread=new Set();
  (existingExpenses||[]).forEach(function(e){importKeysOf(e&&e.notes).forEach(function(k){if(/^receipt:[^:]+:unread$/.test(k))unread.add(groupKeyOf(k));});});
  var toExisting=[],rest=[];
  (files||[]).forEach(function(f){
    var h=f&&(f.hash||f.fileRef);var g=h?"receipt:"+h:null;
    var exps=g&&!unread.has(g)?idx.get(g):null;
    if(exps&&exps.length)toExisting.push({file:f,expenses:exps.slice(),needs:exps.filter(function(e){return!e.receiptPath;}),by:"hash"});
    else rest.push(f);
  });
  return{toExisting:toExisting,rest:rest};
}

// One sheet (already read) → {type, detection, candidates, mapping, message}.
// Generic sheets use `mapping` when given (the UI's column picker), else
// the guess.
function sheetCandidates(sheet,mapping){
  // A workbook of several sheets (readSheetFile's `sheets`): every one is read.
  if(sheet&&Array.isArray(sheet.sheets)&&sheet.sheets.length>1)return workbookCandidates(sheet,mapping);
  var rows=(sheet&&sheet.rows)||[];
  var det=detectSheet(rows);
  var name=(sheet&&sheet.name)||"sheet";
  if(det.type==="invoiceLog"){
    var lc=trackerCandidates(rows,det);
    var up=lc.filter(function(c){return c.uploaded;}).length;
    return{type:det.type,detection:det,candidates:lc,message:name+": invoice log ("+lc.length+" rows"+(up?", "+up+" of them receipts you uploaded":"")+")"};
  }
  if(det.type==="itemDetails"){
    var ir=itemDetailReceipts(rows,det);
    var nl=ir.reduce(function(s,r){return s+r.lines.length;},0);
    return{type:det.type,detection:det,candidates:[],itemReceipts:ir,message:name+": item lines ("+plural0(nl,"line")+" on "+plural0(ir.length,"receipt")+") — they go on the matching invoice log / tracker rows"};
  }
  if(det.type==="loanflowReady"){
    var rs=readySheet(rows,det,{fileName:name});var rc=rs.candidates;
    var nr=rc.filter(function(c){return!c.summaryRow;}).length;
    var out=rs.info.removed.length?" — "+plural0(rs.info.deleted+rs.info.cleared,"row")+" taken out of it":"";
    return{type:det.type,detection:det,candidates:rc,ready:rs.info,message:name+": LoanFlow ready file ("+nr+" row"+(nr===1?"":"s")+out+")"};
  }
  if(det.type==="amazonBusiness")return{type:det.type,detection:det,candidates:amazonCandidates(rows,det),message:name+": Amazon Business orders report"};
  if(det.type==="invoiceTracker"){
    var c=trackerCandidates(rows,det);
    return{type:det.type,detection:det,candidates:c,message:det.noAmountColumn?name+": email list with no amount column — "+c.length+" emails, nothing to import (no amounts)":name+": invoice tracker ("+c.length+" emails)"};
  }
  if(det.type==="generic"){
    var mp=Object.assign({},det.mapping,mapping||{});
    return{type:det.type,detection:det,mapping:mp,candidates:genericCandidates(rows,det,mp,{fileName:name}),message:name+": spreadsheet — check the columns"};
  }
  return{type:"unknown",detection:det,candidates:[],message:name+": "+det.reason};
}
// A workbook's sheets → one result (10/10/26 — the invoice export:
// "Invoice Log" + "Itemized Details" + "Summary"). Every sheet is detected
// on its own. The expense sheets (a ready file, the Amazon report, the
// tracker, an invoice log) all come in; item-line sheets are joined onto
// their rows (joinItemDetails — receipts that match nothing here are
// returned in `itemReceipts`, for a tracker dropped separately); with an
// expense sheet in the workbook, any other sheet (a summary, a pivot) is
// left alone and named in the message. With none, the FIRST generic sheet
// is the sheet (its column picker works as for a CSV). → sheetCandidates'
// shape + `rows` (the main sheet's rows), `workbook: [{name, type, count}]`.
var MAIN_TYPES={loanflowReady:1,amazonBusiness:1,invoiceTracker:1,invoiceLog:1};
function workbookCandidates(book,mapping){
  var name=(book&&book.name)||"workbook";
  var per=(book.sheets||[]).map(function(s){
    var sc=sheetCandidates({name:name+" › "+(s.name||"sheet"),rows:s.rows||[]});
    return{name:s.name||"sheet",rows:s.rows||[],sc:sc};
  });
  var main=per.filter(function(p){return MAIN_TYPES[p.sc.type];});
  var items=per.filter(function(p){return p.sc.type==="itemDetails";});
  var gen=main.length?[]:per.filter(function(p){return p.sc.type==="generic";}).slice(0,1);
  var used=main.concat(gen);
  var cands=[].concat.apply([],used.map(function(p){
    if(p.sc.type==="generic"&&mapping)return sheetCandidates({name:name,rows:p.rows},mapping).candidates;
    return p.sc.candidates;
  }));
  var recs=[].concat.apply([],items.map(function(p){return p.sc.itemReceipts||[];}));
  var j=joinItemDetails(cands,recs);
  var lead=used[0]||items[0]||per[0]||{name:"sheet",rows:[],sc:{type:"unknown",detection:detectSheet([]),candidates:[]}};
  var type=used.length?lead.sc.type:items.length?"itemDetails":"unknown";
  var desc=used.map(function(p){
    var n=p.sc.candidates.length;
    return"“"+p.name+"” — "+(p.sc.type==="loanflowReady"?"LoanFlow ready file":p.sc.type==="amazonBusiness"?"Amazon Business orders":p.sc.type==="invoiceLog"?"invoice log":p.sc.type==="invoiceTracker"?"invoice tracker":"spreadsheet")+" ("+plural0(n,"row")+")";
  });
  if(recs.length){
    var nl=recs.reduce(function(s,r){return s+r.lines.length;},0);
    desc.push(items.map(function(p){return"“"+p.name+"”";}).join(", ")+" — item lines for "+plural0(recs.length,"receipt")+" ("+plural0(nl,"line")+"): "+j.joined+" matched "+(j.joined===1?"its row":"their rows")+(j.unmatched.length?", "+j.unmatched.length+" matched nothing":""));
  }
  var skippedS=per.filter(function(p){return used.indexOf(p)<0&&items.indexOf(p)<0;}).map(function(p){return"“"+p.name+"”";});
  if(skippedS.length)desc.push(skippedS.join(", ")+" not read ("+(main.length?"not expenses — a summary or other data":"nothing to import in it")+")");
  var out={type:type,detection:lead.sc.detection,candidates:j.candidates,rows:lead.rows,itemReceipts:j.unmatched,
    message:name+": "+(desc.join("; ")||"no sheet in it can be imported"),
    workbook:per.map(function(p){return{name:p.name,type:p.sc.type,count:p.sc.type==="itemDetails"?(p.sc.itemReceipts||[]).length:p.sc.candidates.length};})};
  if(lead.sc.type==="generic")out.mapping=mapping?Object.assign({},lead.sc.mapping,mapping):lead.sc.mapping;
  var rd=used.filter(function(p){return p.sc.ready;})[0];if(rd)out.ready=rd.sc.ready;
  return out;
}

// ── Receipts (read by Claude — src/receiptReader.js) ─────────

var RECEIPT_KINDS=["payment receipt","order confirmation","invoice/bill","refund","failed/declined","quote/estimate","not a receipt"];
// Can a receipt's item lines say WHAT was bought (its Items line, and the
// Items line of the row it's merged into / backfills)? Not when a title is
// cut short ("Southworth Z550CK 25% Cotton Line..." reads as paper; the
// full title says 65 lb coverstock), and not an Amazon order confirmation —
// it prints every title truncated, like the "Ordered:" email. Its lines
// still describe it (category, personal check, the left-out list); the row
// is "No item detail" until the order report itemizes it.
function trustedLines(vendor,kind,items){
  if(!items||!items.length)return false;
  if(items.some(function(it){return/(\.\.\.|…)\s*["')\]]?\s*$/.test(oneLine(it&&it.desc));}))return false;
  if(isAmazonish(vendor)&&kind==="order confirmation")return false;
  return true;
}
function isPdfInfo(info){return/pdf/i.test((info&&info.type)||"")||/\.pdf$/i.test((info&&info.name)||"");}
// One receipt line's category, judged on its own (10/9/26: a Vistaprint
// order of business cards AND a mouse pad): what it is (a Marketing item →
// Marketing), else what categorize says about the line, else office
// supplies / equipment → Office/Equipment, else the receipt's own category.
function receiptLineCat(desc,wholeCat){
  var t=itemTypeOf(desc);
  if(t&&isMarketingItem(t))return"Marketing";
  // An office supply / a piece of equipment the taxonomy is sure of is
  // Office/Equipment — categorize's keywords would read "Paper Clips" as
  // paper (10/10/26).
  if(t==="Office Supplies"||t==="Equipment")return"Office/Equipment";
  var c=categorize({text:desc});
  if(c!=="Other")return c;
  if(t==="Office Supplies"||t==="Equipment")return"Office/Equipment";
  return wholeCat;
}
// A receipt whose lines fall in different categories is split per category
// like an Amazon order: amounts in proportion to each category's line
// totals (a "Buy 2 get 1" / "% off" line counts against the line before
// it; points, savings, shipping and tax are spread), the parts summing
// EXACTLY to the receipt (pennies on the largest). Key <receipt key>:<cat>,
// `split`, `splitOf` (the receipt's own key), `splitTotal`, `splitNote`,
// `wholeCat` + `allItems` (what a row it is merged into takes — planImport
// matches the receipt WHOLE). Only a payment receipt / order confirmation /
// invoice with trusted, priced lines and no personal line, never Amazon's
// (the order report splits those) → otherwise [c] as it was.
function splitReceipt(c,fileName){
  if(!c||c.exclude||!MERGE_KINDS[c.kind]||c.itemsFrom!=="lines"||c.personalItemsFlag||isAmazonish(c.vendor))return[c];
  var totalC=cents(c.amount);if(totalC<=0)return[c];
  var lines=(c.items||[]).filter(function(it){return it&&oneLine(it.desc);});
  if(lines.length<2||lines.some(function(it){return it.amount==null||!isFinite(Number(it.amount));}))return[c];
  var groups=[];var byCat={};var last=null;
  lines.forEach(function(it){
    var a=cents(it.amount);
    if(a<0){if(last&&itemDiscountLine(it.desc)){last.w+=a;last.lines.push(it);}return;}   // a discount on the line before, or spread
    var cat=receiptLineCat(it.desc,c.cat);
    var g=byCat[cat];if(!g){g={cat:cat,w:0,pw:0,lines:[]};byCat[cat]=g;groups.push(g);}
    g.w+=a;g.lines.push(it);last=g;
    if(itemTypeOf(it.desc)==="Postage")g.pw+=a;   // stamps carry no tax (spreadUntaxedPostage)
  });
  if(groups.length<2||groups.some(function(g){return g.w<=0;}))return[c];
  groups.sort(function(a,b){return a.cat.localeCompare(b.cat);});   // (the order a ready file's rows come back in)
  var cs=spreadUntaxedPostage(totalC,groups.map(function(g){return g.w;}),groups.map(function(g){return g.pw;}));
  if(!cs)return[c];
  var note="Split from "+(c.vendor||"the")+" receipt "+(fileName||"receipt")+" ("+fmtMoney(c.amount)+") by category: "+groups.map(function(g,i){return g.cat+" "+fmtMoney(cs[i]/100);}).join(" + ");
  return groups.map(function(g,i){
    var first=clipWords(g.lines[0].desc,60);
    var n=g.lines.filter(function(it){return cents(it.amount)>=0;}).length;
    return makeCand(Object.assign({},c,{key:c.key+":"+g.cat,cat:g.cat,amount:cs[i]/100,items:g.lines.slice(),
      desc:clipWords(c.vendor+" — "+first+(n>1?" (+"+(n-1)+" more)":""),120),flags:(c.flags||[]).slice(),
      split:{index:i,count:groups.length},splitOf:c.key,splitTotal:c.amount,splitNote:note,wholeCat:c.cat,allItems:(c.items||[]).slice(),splitDesc:c.desc}));
  });
}
function localDayOf(ms){if(!ms)return null;var d=new Date(ms);if(isNaN(d.getTime()))return null;return d.getFullYear()+"-"+pad2(d.getMonth()+1)+"-"+pad2(d.getDate());}
// fileInfo: {name, hash (sha-256 hex), type, lastModified, fileRef}
// result:   {receipts:[…]} from readReceiptFile, or {error}
function receiptCandidates(fileInfo,result,opts){
  var info=fileInfo||{};opts=opts||{};
  var name=info.name||"receipt";
  var hash=info.hash||hashStr(name+"|"+(info.size||0));
  var baseKey="receipt:"+hash;
  var badge=isPdfInfo(info)?"Receipt PDF":"Receipt photo";
  var fallbackDate=localDayOf(info.lastModified)||opts.fallbackDate||todayIso();
  var fileRef=info.fileRef||hash;
  var common={source:"receipt",badge:badge,fileRef:fileRef,fileName:name,fileHash:hash};
  var list=result&&!result.error&&Array.isArray(result.receipts)?result.receipts.filter(Boolean):[];
  var partial=result&&!result.error&&result.partial?oneLine(result.partial):"";
  var gv=vendorOf(name.replace(/\.[a-z0-9]+$/i,"").replace(/[_\d]+/g," "));
  if(!list.length){
    var detail=result&&result.error?" ("+clipWords(result.error,80)+")":partial?" ("+clipWords(partial,80)+")":"";
    return[makeCand(Object.assign({},common,{key:baseKey,date:fallbackDate,vendor:gv.known?gv.vendor:"",desc:clipWords((gv.known?gv.vendor+" — ":"")+"receipt "+name,120),
      amount:0,cat:categorize({vendor:gv.known?gv.vendor:"",text:name}),flags:[FLAG.unreadable+detail],unreadable:true}))];
  }
  var out=[];
  list.forEach(function(rc,i){
    var key=list.length>1?baseKey+":"+(i+1):baseKey;
    var kind=RECEIPT_KINDS.indexOf(String(rc.kind||"").toLowerCase())>=0?String(rc.kind).toLowerCase():"payment receipt";
    var rv=oneLine(rc.vendor);var v=vendorOf(rv||name);
    var vendor=v.known?v.vendor:clipWords(titleCaseIfShouting(rv),40)||vendorOf(name).vendor;
    var items=Array.isArray(rc.items)?rc.items.filter(Boolean):[];
    var itemText=items.map(function(it){return oneLine(it.desc);}).filter(Boolean).join(" | ");
    var date=parseDate(rc.date);
    var total=typeof rc.total==="number"?round2(rc.total):parseMoney(rc.total);
    var txn=oneLine(rc.orderOrTransactionId);
    var flags=[];
    var conf=Number(rc.confidence);
    var firstItem=items.length?clipWords(items[0].desc,60):"";
    var desc=clipWords(vendor+" — "+(firstItem?firstItem+(items.length>1?" (+"+(items.length-1)+" more)":""):(kind==="payment receipt"?"receipt":kind)),120);
    var c=Object.assign({},common,{key:key,date:date||fallbackDate,vendor:vendor,desc:desc,kind:kind,transactionId:txn||null,
      orderIds:orderIdsIn(txn,rc.note),cat:categorize({vendor:vendor,text:[itemText,rc.note].filter(Boolean).join(" | ")}),
      // (a line's UPC rides along when the reader saw one — a Staples 083514… UPC is Southworth; review RV-4)
      items:items.map(function(it){var o={desc:oneLine(it.desc),qty:it.qty==null?null:Number(it.qty),amount:it.amount==null?null:Number(it.amount)};var u=lineUpc(it.upc);if(u)o.upc=u;return o;}),
      itemsFrom:trustedLines(vendor,kind,items)?"lines":null,
      confidence:isFinite(conf)?conf:null,receiptIndex:i,receiptCount:list.length});
    if(kind==="failed/declined"){out.push(makeCand(Object.assign(c,{exclude:"failed",amount:total})));return;}
    if(total==null||Math.abs(total)<0.005){
      if(kind==="payment receipt"||kind==="order confirmation"||kind==="refund"){out.push(makeCand(Object.assign(c,{amount:0,flags:[FLAG.noTotal],unreadable:true})));}
      else out.push(makeCand(Object.assign(c,{exclude:"noAmount",amount:total})));
      return;
    }
    var amount=kind==="refund"?-Math.abs(total):total;
    if(kind==="invoice/bill")flags.push(FLAG.bill);
    if(kind==="quote/estimate")flags.push(FLAG.quote);
    if(kind==="not a receipt")flags.push(FLAG.notReceipt);
    if(!date)flags.push(FLAG.noDate);
    if(isFinite(conf)&&conf<0.6)flags.push("Low-confidence reading ("+Math.round(conf*100)+"%) — check the amount against the receipt");
    // Do the parts add up? subtotal (or the item lines) + tax + shipping —
    // only when a subtotal or a tax line was actually read: an Amazon order
    // confirmation lists items and a Grand Total with no tax line, and its
    // items never add up to the total (the tax is hidden in it).
    // A printed subtotal is often BEFORE the promotions (Vistaprint: list
    // prices, then "Savings −$107.60"), so the net item lines (savings lines
    // included) adding up is just as good: flagged only when neither does.
    var sub=typeof rc.subtotal==="number"?rc.subtotal:parseMoney(rc.subtotal);
    var taxRead=typeof rc.tax==="number"?rc.tax:parseMoney(rc.tax);
    var itemsSum=null;
    if(items.length&&items.every(function(it){return it.amount!=null&&isFinite(Number(it.amount));}))itemsSum=round2(items.reduce(function(s,it){return s+Number(it.amount);},0));
    var base=sub!=null?sub:itemsSum;
    if(base!=null&&(sub!=null||taxRead!=null)){
      var tax=taxRead||0;
      var ship=typeof rc.shipping==="number"?rc.shipping:(parseMoney(rc.shipping)||0);
      var expect=round2(base+tax+ship);
      var byLines=itemsSum!=null&&Math.abs(round2(itemsSum+tax+ship)-Math.abs(total))<=0.05;
      if(Math.abs(expect-Math.abs(total))>0.05&&!byLines)flags.push("Items + tax ("+fmtMoney(expect)+") don't add up to the total ("+fmtMoney(Math.abs(total))+") — check the amount");
    }
    if(rc.possibleRepeat)flags.push(FLAG.pageRepeat);
    // A receipt whose item lines are ALL personal (each line on its own) is
    // left out (listed, with "Include anyway"); one with no item lines only
    // when the vendor itself is a personal service. A MIXED one (paper towels
    // + a stapler + a monitor) comes in — in the business items' category —
    // flagged, naming the personal lines.
    var described=items.filter(function(it){return oneLine(it.desc);});
    var perLine=described.map(function(it){return looksPersonal({text:oneLine(it.desc)});});
    var nPer=perLine.filter(Boolean).length;
    var p=described.length?(nPer===described.length?uniq(perLine).join(", "):null):personalService([rv,rc.note].filter(Boolean).join(" "));
    if(p)Object.assign(c,{cat:"Other",exclude:"personal",personalWhy:p,personalFlag:"Doesn't look like a business supply ("+p+") — keep it or delete it"});
    else if(nPer){
      var biz=described.filter(function(it,ix){return!perLine[ix];}).map(function(it){return oneLine(it.desc);}).join(" | ");
      c.cat=categorize({vendor:vendor,text:[biz,rc.note].filter(Boolean).join(" | ")});
      var names=described.filter(function(it,ix){return perLine[ix];}).map(function(it){return clipWords(it.desc,40);});
      c.personalItemsFlag="Some items don't look like business supplies ("+names.slice(0,3).join(", ")+(names.length>3?" +"+(names.length-3)+" more":"")+") — keep it, or take their cost out of the amount";
      flags.push(c.personalItemsFlag);
    }
    splitReceipt(makeCand(Object.assign(c,{amount:amount,flags:flags})),name).forEach(function(x){out.push(x);});
  });
  // Some pages of the file couldn't be read (a page group failed, or the
  // file is past the page limit): whatever is on them comes in as one $0
  // row, flagged, with the file attached — never silently dropped.
  if(partial){
    out.push(makeCand(Object.assign({},common,{key:baseKey+":unread",date:fallbackDate,vendor:gv.known?gv.vendor:"",
      desc:clipWords((gv.known?gv.vendor+" — ":"")+"unread pages of "+name,120),amount:0,cat:categorize({vendor:gv.known?gv.vendor:"",text:name}),
      flags:[FLAG.partial+" ("+clipWords(partial,100)+") — open it and add any receipts on those pages, then delete this row"],unreadable:true,partialRead:true})));
  }
  return out;
}

// Receipt PDFs from the tracker's invoices folder attach to their tracker
// row by file name (as the first importer did) instead of being read again.
// A file whose tracker email is ALREADY in Expenses (its key stamped there —
// the old 9/9 stamp included, and with or without the tracker sheet in this
// upload) goes to that expense instead: `toExisting`, with `needs` = the
// ones that have no stored receipt yet (the UI uploads it and points them
// at it); one that already has its receipt needs nothing.
// files: [{name, fileRef}] → {candidates (copies, with fileRef/fileName set
// on the matched tracker rows), unmatched: files nothing claims, toExisting:
// [{file, expenses, needs}]}
function matchTrackerFiles(candidates,files,existingExpenses){
  var byName=new Map();
  (candidates||[]).forEach(function(c){if(c&&c.source==="tracker"&&c.fileName){var k=c.fileName.toLowerCase();if(!byName.has(k))byName.set(k,[]);byName.get(k).push(c);}});
  var stamped=keyIndex(existingExpenses);
  var byBase=new Map();
  (existingExpenses||[]).forEach(function(e){
    if(!e||!e.id)return;
    importKeysOf(e.notes).forEach(function(k){
      var m=/^tracker:(.+)$/.exec(groupKeyOf(k));if(!m||/^email-/.test(m[1]))return;
      var b=baseName(m[1]).toLowerCase();var l=byBase.get(b);if(!l){l=[];byBase.set(b,l);}if(l.indexOf(e)<0)l.push(e);
    });
  });
  var claimed=new Map();var unmatched=[];var toExisting=[];
  (files||[]).forEach(function(f){
    var k=String((f&&f.name)||"").toLowerCase();
    var cands=byName.get(k)||[];
    if(cands.some(function(c){return!stamped.has(groupKeyOf(c.key));})){claimed.set(k,f);return;}
    var exps=[];
    cands.forEach(function(c){(stamped.get(groupKeyOf(c.key))||[]).forEach(function(e){if(exps.indexOf(e)<0)exps.push(e);});});
    if(!exps.length)(byBase.get(k)||[]).forEach(function(e){if(exps.indexOf(e)<0)exps.push(e);});
    if(exps.length){toExisting.push({file:f,expenses:exps,needs:exps.filter(function(e){return!e.receiptPath;})});return;}
    unmatched.push(f);
  });
  var out=(candidates||[]).map(function(c){
    if(!c||c.source!=="tracker"||!c.fileName)return c;
    var f=claimed.get(c.fileName.toLowerCase());
    return f?Object.assign({},c,{fileRef:f.fileRef||f.name,fileName:f.name}):c;
  });
  return{candidates:out,unmatched:unmatched,toExisting:toExisting};
}
// toExisting (above) → upload entries shaped like buildExpenses' uploads:
// one per file, stored under the first expense it goes to, pointed at by
// every expense in `needs`. Files whose expenses all have a receipt: none.
function existingAttachUploads(toExisting){
  var out=[];
  (toExisting||[]).forEach(function(t){
    if(!t||!t.needs||!t.needs.length||!t.file)return;
    var name=receiptDisplayName(t.file.name);
    out.push({fileRef:t.file.fileRef||t.file.name,name:name,path:expenseReceiptPath(t.needs[0].id,safeFileName(t.file.name)),ids:[],existingIds:t.needs.map(function(e){return e.id;})});
  });
  return out;
}
// After the uploads: point these expenses already in the sheet at their
// stored receipt. done: Map(id → {path, name}). Only rows still without a
// stored receipt change; every other row is returned as it was.
function attachExistingReceipts(expenses,done){
  if(!done||!done.size)return expenses;
  return(expenses||[]).map(function(e){
    var d=e&&done.get(e.id);
    return d&&!e.receiptPath?Object.assign({},e,{receiptPath:d.path,receiptName:d.name}):e;
  });
}

// ── planImport ───────────────────────────────────────────────

function normVendor(s){return oneLine(s).toLowerCase().replace(/[^a-z0-9]+/g,"");}
function sameVendor(a,b){var x=normVendor(a),y=normVendor(b);if(!x||!y)return false;return x===y||(x.length>=4&&y.indexOf(x)>=0)||(y.length>=4&&x.indexOf(y)>=0);}
function describeExp(e){return"("+[e.date||"no date",oneLine(e.vendor)||clipWords(e.desc,40),fmtMoney(e.amount)].filter(Boolean).join(" · ")+")";}
function describeRow(r){return"("+[r.badge,r.date,r.vendor,fmtMoney(r.amount)].filter(Boolean).join(" · ")+")";}
function isAmazonish(s){return/amazon|amzn/i.test(String(s||""));}
// Orders whose totals add up to `target` within `tol` cents (default 1; 0 =
// to the cent). Small sets only (one day ±1); bigger pools try the largest
// orders first and stop early.
function subsetSum(pool,target,tol){
  var t=cents(target);if(t<=0)return null;
  var e=tol==null?1:tol;
  var items=pool.filter(function(o){return cents(o.total)>0;}).sort(function(a,b){return cents(b.total)-cents(a.total);});
  if(items.length>24)items=items.slice(0,24);
  var best=null;var steps=0;
  var suffix=[];var acc=0;for(var i=items.length-1;i>=0;i--){acc+=cents(items[i].total);suffix[i]=acc;}
  (function dfs(i,sum,pick){
    if(best||++steps>200000)return;
    if(Math.abs(sum-t)<=e&&pick.length){best=pick.slice();return;}
    if(i>=items.length||sum>t+e||sum+suffix[i]<t-e)return;
    pick.push(items[i]);dfs(i+1,sum+cents(items[i].total),pick);pick.pop();
    dfs(i+1,sum,pick);
  })(0,0,[]);
  return best;
}
// The vendor a name stands for, for the CERTAIN matches (merges and skips):
// a VENDORS name ("STAPLES 00123" / "Staples Inc" → staples; Amazon and
// Amazon Business are one: amazon), else the name itself normalised. ""
// when there is no vendor to go on (blank / "Unknown") — never certain then.
function vendorFamily(v){
  var s=oneLine(v);if(!s)return"";
  var k=vendorOf(s);
  if(k.known)return k.vendor==="Amazon Business"?"amazon":normVendor(k.vendor);
  var n=normVendor(s);
  return n==="unknown"?"":n;
}

// ── Hand-entered rows (10/9/26) ──────────────────────────────
// "Hand-entered" = an expense with NO import stamp line: typed in Expenses
// (or logged by one of his recurring series).
function isHandEntered(e){return!!e&&!String(e.notes||"").split("\n").some(isStampLine);}
var NAMED_VENDOR={Claude:"Anthropic"};   // a named software item whose VENDORS name differs
// The vendor a description names: a VENDORS name ("USPS stamps" → USPS,
// "Amazon - Envelopes" → Amazon) or a named software item ("Claude Max plan"
// → Anthropic, "Ring Central" → RingCentral). "" when it names none.
function vendorInText(text){
  var s=oneLine(text);if(!s)return"";
  var v=vendorOf(s);if(v.known)return v.vendor;
  var l=" "+s.toLowerCase()+" ";
  for(var i=0;i<NAMED_SOFTWARE.length;i++)if(NAMED_SOFTWARE[i][0].test(l)){var t=NAMED_SOFTWARE[i][1];return NAMED_VENDOR[t]||t;}
  return"";
}
// The vendor an expense ALREADY in the sheet is matched by: its own; a
// hand-entered row with none (86 of his 121 on 10/9) is read from its
// description. For matching only — never written back.
function matchVendorOf(e){
  var v=oneLine(e&&e.vendor);
  if(v&&!/^unknown$/i.test(v))return v;
  return isHandEntered(e)?vendorInText(e&&e.desc):"";
}
// Postage (Reece, 10/9/26: the receipts are the record). A postage purchase
// in the upload: USPS / Stamps.com, or what it bought is Postage.
var POSTAGE_FAMILIES={usps:true,stampscom:true};
// The mailhouse (10/9/26, later): AlphaGraphics of Gilbert, or a charge that
// says mail house / letter shop / presort / EDDM. Its item type is Postage
// too (the mailhouse folds into Postage), but it is its OWN "receipts are
// the record" family: AlphaGraphics receipts replace his hand-entered
// mailhouse rows, USPS / Stamps.com receipts his hand-entered postage.
var MAILHOUSE_FAMILIES={alphagraphics:true};
var MAILHOUSE_TEXT_RE=/alpha ?graphics|mail ?house|letter ?shop|presort|\beddm\b|every door direct|mailing service|mail processing/i;
function isMailhouseRow(r){
  if(!r)return false;
  if(MAILHOUSE_FAMILIES[vendorFamily(r.vendor)])return true;
  return MAILHOUSE_TEXT_RE.test(oneLine((r.vendor||"")+" "+(r.desc||"")));
}
function isPostageRow(r){
  if(!r||isMailhouseRow(r))return false;
  if(POSTAGE_FAMILIES[vendorFamily(r.vendor)])return true;
  var types=(r.items||[]).map(function(it){return it?(it.type?canonicalType(it.type):itemTypeOf(it.desc)):null;}).filter(Boolean);
  if(types.length)return types.every(function(t){return t==="Postage";});
  return itemTypeOf(r.desc)==="Postage";
}
// …an imported one already in Expenses (its Items line, else its description).
function isMailhouseExpense(e){return isMailhouseRow(e);}
function isPostageExpense(e){
  if(!e||isMailhouseExpense(e))return false;
  if(POSTAGE_FAMILIES[vendorFamily(e.vendor)])return true;
  var parsed=parseItemsLine(e.notes);
  if(parsed.length)return parsed.every(function(p){return p.type==="Postage";});
  return itemTypeOf(e.desc)==="Postage";
}
// …and a hand-entered postage row: its vendor / description says USPS,
// postage, stamps, post office or Stamps.com (any category) — but not a
// rubber / self-inking / address stamp or a stamp pad (Office Supplies), a
// postage meter's ink (Mailing Machine)….
var HAND_POSTAGE_RE=/\busps\b|postage|\bstamps?\b|post office|stamps\.com/i;
// Nor what the post office sells that isn't a mailing (isPostalService: a
// PO box, a passport, a notary, a money order — 10/10/26).
function isHandPostage(e){
  if(!isHandEntered(e))return false;
  var text=oneLine((e.vendor||"")+" "+(e.desc||""));
  if(!HAND_POSTAGE_RE.test(text)||isPostalService(text))return false;
  var t=itemTypeOf(text);
  return t===null||t==="Postage";
}
// …and a hand-entered MAILHOUSE row: its vendor / description says
// AlphaGraphics, mail house, letter shop, presort, EDDM or "mailing" — and
// no postage words (those stay postage, as before) — and nothing else it
// could be (a mailing LIST is Data, mailing labels are Labels).
var HAND_MAILING_RE=/\bmailings?\b/i;
function isHandMailhouse(e){
  if(!isHandEntered(e))return false;
  var text=oneLine((e.vendor||"")+" "+(e.desc||""));
  if(HAND_POSTAGE_RE.test(text)||!(MAILHOUSE_TEXT_RE.test(text)||HAND_MAILING_RE.test(text)))return false;
  var t=itemTypeOf(text);
  return t===null||t==="Postage";
}
// ── Software: his hand entries, or the vendor's invoices (10/10/26, later) ──
// "Keep my recurring entries" (10/9/26): an upload row from a vendor he logs
// by hand in Software/Subscriptions is left out (handLogged). Reece then
// dropped ten Anthropic invoices for Claude Max — the vendor's own record of
// what he paid — so the preview offers, per such vendor, "Use these invoices
// instead of my hand entries for <vendor> (<dates>)". Chosen, the invoices
// come in and his hand rows of that vendor near their dates are flagged
// "Replaced by the imported invoices for <vendor> …" — the postage /
// mailhouse "receipts are the record" mechanism (Keep strips it, Select the
// replaced rows → Delete selected). Not chosen: left out, as before.
// The vendor families of his HAND-ENTERED Software/Subscriptions rows
// (matchVendorOf — the vendor, or the one the description names) → Map(family
// → those rows). A row already flagged as replaced by imported invoices, or
// kept against that flag, doesn't count: he has switched that vendor to its
// invoices, so its next invoices come in as usual.
function handSoftwareFamilies(existing){
  var m=new Map();
  (existing||[]).forEach(function(e){
    if(!e||!e.id||e.cat!=="Software/Subscriptions"||!isHandEntered(e))return;
    if(hasReplacedKept(e,"invoices")||reviewFlagsOf(e).some(function(f){return replacedKindOf(f)==="invoices";}))return;
    var f=vendorFamily(matchVendorOf(e));if(!f)return;
    var l=m.get(f);if(!l){l=[];m.set(f,l);}l.push(e);
  });
  return m;
}
// planImport opts.useInvoices → Map(vendor family → true | false): families
// (array / Set — chosen), or {family: true | false} (false = not chosen, even
// for a ready-file row that chose them before).
function invoiceChoices(x){
  var m=new Map();if(!x)return m;
  if(x instanceof Map){x.forEach(function(v,k){if(k)m.set(k,!!v);});return m;}
  if(Array.isArray(x)||x instanceof Set){Array.from(x).forEach(function(k){if(k)m.set(k,true);});return m;}
  if(typeof x==="object")Object.keys(x).forEach(function(k){m.set(k,!!x[k]);});
  return m;
}
// His hand rows of one vendor family that its invoices replace: every one
// dated within INVOICE_NEAR_DAYS of an invoice coming in (or of that vendor's
// rows already imported) — a monthly bill's own month, so a month with no
// invoice keeps its hand entry. Only when this upload brings one. Kept before
// (REPLACED_INVOICES_KEPT) → never again; flagged before → on its way out,
// not listed twice. rows: the upload rows standing in for them.
// → {span: {lo, hi, loN, hiN} | null, replaced: [{id, date, vendor, desc,
// amount, flag}], replacing: [ids flagged now or before], hand: [ids of his
// hand rows of that vendor]}
var INVOICE_NEAR_DAYS=15;
function invoiceFamilyReplaced(fam,vendor,rows,existing){
  var k=REPLACED_BY.invoices;var span=null;var days=[];var incoming=0;
  var addDay=function(d){var n=dayNum(d);if(n==null)return false;days.push(n);if(!span)span={lo:d,hi:d,loN:n,hiN:n};else{if(n<span.loN){span.lo=d;span.loN=n;}if(n>span.hiN){span.hi=d;span.hiN=n;}}return true;};
  (rows||[]).forEach(function(r){if(r&&cents(r.amount)&&addDay(r.date))incoming++;});
  var hand=(existing||[]).filter(function(e){return e&&e.id&&e.cat==="Software/Subscriptions"&&isHandEntered(e)&&vendorFamily(matchVendorOf(e))===fam;});
  var flaggedBefore=function(e){return reviewFlagsOf(e).some(function(x){return replacedKindOf(x)==="invoices";});};
  var out={span:null,replaced:[],replacing:hand.filter(flaggedBefore).map(function(e){return e.id;}),hand:hand.map(function(e){return e.id;})};
  if(!incoming)return out;
  (existing||[]).forEach(function(e){if(e&&e.id&&!isHandEntered(e)&&e.cat==="Software/Subscriptions"&&cents(e.amount)&&vendorFamily(e.vendor)===fam)addDay(e.date);});
  out.span=span;
  days.sort(function(a,b){return a-b;});
  var near=function(n){var lo=0,hi=days.length;while(lo<hi){var mid=(lo+hi)>>1;if(days[mid]<n-INVOICE_NEAR_DAYS)lo=mid+1;else hi=mid;}return lo<days.length&&days[lo]<=n+INVOICE_NEAR_DAYS;};
  var flag=k.prefix+" "+(oneLine(vendor)||"this vendor")+" ("+(span.lo===span.hi?span.lo:span.lo+" to "+span.hi)+") — "+k.tail;
  hand.forEach(function(e){
    if(hasReplacedKept(e,"invoices"))return;   // kept before: a charge that isn't among them
    var n=dayNum(e.date);if(n==null||!near(n))return;
    if(out.replacing.indexOf(e.id)<0)out.replacing.push(e.id);
    if(flaggedBefore(e))return;
    out.replaced.push({id:e.id,date:e.date,vendor:e.vendor||"",desc:e.desc||"",amount:round2(e.amount),flag:flag});
  });
  return out;
}
// The vendor an invoices "replaced" flag names ("Replaced by the imported
// invoices for Anthropic (2026-02-17 to …" → "Anthropic"), else "".
function invoiceVendorOf(flag){
  var s=String(flag||"");if(s.indexOf(REPLACED_INVOICES_PREFIX)!==0)return"";
  var m=/^\s*(.*?)\s+\(\d{4}-\d{2}-\d{2}/.exec(s.slice(REPLACED_INVOICES_PREFIX.length));
  return m?m[1]:"";
}
// Copies of one invoice in an upload (his Anthropic invoices were saved more
// than once: "Invoice-GBH80TOH-0005.pdf", "… (1).pdf", "… (2).pdf" —
// byte-different files, so three receipt keys): receipt rows of one vendor
// family with the same total to the cent, the same date and the same
// invoice / transaction number (or none read on either). The plain file
// name is the one kept. The same file twice (one key) is not a copy here —
// it has its own flag. → [{copy, of}]
var COPY_NAME_RE=/(\s*\(\d+\)|\s*-\s*copy(\s*\(\d+\))?|\s+copy)\s*(\.[a-z0-9]+)?$/i;
function invoiceNoOf(r){return String((r&&r.transactionId)||"").toLowerCase().replace(/[^a-z0-9]+/g,"");}
function invoiceCopies(rows){
  var groups=new Map();
  (rows||[]).forEach(function(r){
    if(!r||r.source!=="receipt"||r.split)return;
    var f=vendorFamily(r.vendor);var c=cents(r.amount);if(!f||!c||!r.date)return;
    var k=f+"|"+c+"|"+r.date;var l=groups.get(k);if(!l){l=[];groups.set(k,l);}l.push(r);
  });
  var nameOf=function(r){return String((r&&r.fileName)||"");};
  var order=function(a,b){
    return(COPY_NAME_RE.test(nameOf(a))?1:0)-(COPY_NAME_RE.test(nameOf(b))?1:0)||nameOf(a).length-nameOf(b).length||nameOf(a).localeCompare(nameOf(b))||String(a.key).localeCompare(String(b.key));
  };
  var out=[];
  groups.forEach(function(l){
    if(l.length<2)return;
    var kept=[];
    l.slice().sort(order).forEach(function(r){
      var n=invoiceNoOf(r);var of=null;
      kept.some(function(t){var m=invoiceNoOf(t);if(t.key!==r.key&&(!n||!m||n===m)){of=t;return true;}return false;});
      if(of)out.push({copy:r,of:of});else kept.push(r);
    });
  });
  return out;
}
// The hand-entered postage rows a set of rows replaces — planImport's
// `replaced`, and the preview's (InvoiceImport works it out again from the
// TICKED rows, exactly as Add does: untick every USPS row and nothing is
// replaced). rows: the upload rows that are coming in; existing: the
// expenses already in the sheet. Only an upload that BRINGS receipts of the
// family replaces anything (a Staples-only sheet never flags his stamps),
// and a hand row is flagged only when a receipt of the family — coming in
// or already imported — is dated within 7 days of it (not merely somewhere
// inside the first … last receipt: a month with no receipts is left alone).
// The flag names the receipts' span (earliest … latest).
// → {replaced: [{id, date, vendor, desc, amount, flag}], replacing: Set of
// ids (flagged now or at an earlier import — matched to nothing else),
// recordRow: Set of the rows that are postage or mailhouse, handRecord: Set
// of the hand-entered postage / mailhouse ids, span / mailSpan: {lo, hi,
// loN, hiN} | null}. Two families, each with its own span and flag
// (REPLACED_KINDS): USPS / Stamps.com receipts → his hand-entered postage;
// AlphaGraphics mailhouse receipts → his hand-entered mailhouse rows.
var RECORD_FAMILIES=[
  {kind:"postage",isRow:isPostageRow,isExp:isPostageExpense,isHand:isHandPostage},
  {kind:"mailhouse",isRow:isMailhouseRow,isExp:isMailhouseExpense,isHand:isHandMailhouse}
];
function postageReplaced(rows,existing){
  var recordRow=new Set(),handRecord=new Set(),replacing=new Set();var replaced=[];var spans={};
  RECORD_FAMILIES.forEach(function(f){
    var k=REPLACED_BY[f.kind];var span=null;var days=[];var incoming=0;
    var addDay=function(d){var n=dayNum(d);if(n==null)return false;days.push(n);if(!span)span={lo:d,hi:d,loN:n,hiN:n};else{if(n<span.loN){span.lo=d;span.loN=n;}if(n>span.hiN){span.hi=d;span.hiN=n;}}return true;};
    (rows||[]).forEach(function(r){if(r&&r.on!==false&&f.isRow(r)){recordRow.add(r);if(cents(r.amount)&&addDay(r.date))incoming++;}});
    var hand=new Set();(existing||[]).forEach(function(e){if(e&&e.id&&f.isHand(e)){hand.add(e.id);handRecord.add(e.id);}});
    var flaggedBefore=function(e){return reviewFlagsOf(e).some(function(x){return replacedKindOf(x)===f.kind;});};
    // A row flagged at an earlier import is on its way out: it takes part in
    // no other match, whatever this upload holds.
    (existing||[]).forEach(function(e){if(e&&hand.has(e.id)&&flaggedBefore(e))replacing.add(e.id);});
    // No receipt of this family in THIS upload: nothing is replaced (the
    // ones already imported only widen the dates when new ones come in).
    if(!incoming){spans[f.kind]=null;return;}
    (existing||[]).forEach(function(e){if(e&&e.id&&!isHandEntered(e)&&cents(e.amount)&&f.isExp(e))addDay(e.date);});
    spans[f.kind]=span;
    if(!hand.size)return;
    days.sort(function(a,b){return a-b;});
    // A receipt within 7 days of day n?
    var near=function(n){var lo=0,hi=days.length;while(lo<hi){var mid=(lo+hi)>>1;if(days[mid]<n-7)lo=mid+1;else hi=mid;}return lo<days.length&&days[lo]<=n+7;};
    var spanTxt=span.lo===span.hi?span.lo:span.lo+" to "+span.hi;
    var flag=k.prefix+" ("+spanTxt+") — "+k.tail;
    existing.forEach(function(e){
      if(!e||!hand.has(e.id)||hasReplacedKept(e,f.kind))return;   // kept before: a purchase that isn't among them
      var n=dayNum(e.date);if(n==null||!near(n))return;
      replacing.add(e.id);
      if(flaggedBefore(e))return;     // flagged at an earlier import: once is enough
      replaced.push({id:e.id,date:e.date,vendor:e.vendor||"",desc:e.desc||"",amount:round2(e.amount),flag:flag});
    });
  });
  // …and the software invoices he chose over his hand entries (rows marked
  // `replacesHand` = the vendor family — planImport; 10/10/26, later): per
  // vendor, its own span and flag (invoiceFamilyReplaced, ±15 days). His
  // hand rows of a chosen vendor are matched to nothing else, like hand
  // postage; one flagged at an earlier import is on its way out.
  var invFams=new Map();
  (rows||[]).forEach(function(r){if(r&&r.on!==false&&r.replacesHand){recordRow.add(r);var l=invFams.get(r.replacesHand);if(!l){l=[];invFams.set(r.replacesHand,l);}l.push(r);}});
  (existing||[]).forEach(function(e){if(e&&e.id&&isHandEntered(e)&&reviewFlagsOf(e).some(function(x){return replacedKindOf(x)==="invoices";}))replacing.add(e.id);});
  var invSpans=[];
  invFams.forEach(function(list,fam){
    var vendor=oneLine(list[0].vendor);
    var res=invoiceFamilyReplaced(fam,vendor,list,existing);
    res.hand.forEach(function(id){handRecord.add(id);});
    res.replacing.forEach(function(id){replacing.add(id);});
    res.replaced.forEach(function(x){replaced.push(x);});
    if(res.span)invSpans.push({family:fam,vendor:vendor,from:res.span.lo,to:res.span.hi});
  });
  replaced.sort(function(a,b){return String(a.date||"").localeCompare(String(b.date||""))||String(a.id).localeCompare(String(b.id));});
  return{replaced:replaced,replacing:replacing,recordRow:recordRow,handRecord:handRecord,span:spans.postage,mailSpan:spans.mailhouse,invoiceSpans:invSpans};
}
function spanOut(s){return s?{from:s.lo,to:s.hi}:null;}
// The preview's list: the rows Reece has ticked (on !== false) → what Add
// will flag as replaced, and the receipts' dates. Same answer as
// finalizeImport(…).replaced for those rows.
function replacedFor(rows,existingExpenses){
  var p=postageReplaced((rows||[]).filter(function(r){return r&&r.on!==false;}),(existingExpenses||[]).filter(function(e){return e&&e.id;}));
  return{replaced:p.replaced,postageSpan:spanOut(p.span),mailhouseSpan:spanOut(p.mailSpan)};
}
// replaced (plan / replacedFor / finalizeImport) → {count, total, mailhouse:
// {count, total}} for importOutcome and the flash — + invoices: {count,
// total, vendors} when software hand rows are among them (10/10/26, later).
function replacedSummary(replaced){
  var all={count:0,total:0,mailhouse:{count:0,total:0}};
  (replaced||[]).forEach(function(x){
    var c=cents(x&&x.amount);all.count++;all.total+=c;
    var kind=replacedKindOf(x&&x.flag);
    if(kind==="mailhouse"){all.mailhouse.count++;all.mailhouse.total+=c;}
    else if(kind==="invoices"){
      var iv=all.invoices||(all.invoices={count:0,total:0,vendors:[]});
      iv.count++;iv.total+=c;
      var vn=invoiceVendorOf(x.flag);if(vn&&iv.vendors.indexOf(vn)<0)iv.vendors.push(vn);
    }
  });
  all.total=all.total/100;all.mailhouse.total=all.mailhouse.total/100;
  if(all.invoices)all.invoices.total=all.invoices.total/100;
  return all;
}

// ── Hand entries typed in whole dollars (10/9/26) ────────────
// 83 of his 86 remaining hand-entered rows are whole dollars — "Envelopes
// $60" for a Staples receipt of $60.63. Rule (a)'s ±1¢ never pairs them, so
// a receipt for the same purchase came in unflagged and the spend counted
// twice. A HAND-ENTERED row near an upload row (not ±1¢ — that is rule (a)
// as before) — within max($5, 1.5%) and ±14 days (was a dollar and ±5 days
// until his $450.00 business cards vs the $447.91 Vistaprint order) — of the
// same vendor (vendorFamily of the vendor its description names, for a
// blank one) — or, when either side has no vendor to go on, the same item
// type / subcategory — makes that upload row a "Possible duplicate of your
// hand entry …": imported and flagged, never skipped. Two different known
// vendors are two purchases.
var NEAR_DAYS=14;
// How far apart a hand entry may be and still be the same purchase typed
// rounded: max($5, 1.5% of the amount), in cents (10/9/26, later — was a
// dollar and ±5 days; his $450.00 business cards vs the $447.91 order).
function nearTolCents(c){return Math.max(500,Math.round(Math.abs(c)*0.015));}
function shareType(a,b){for(var i=0;i<a.length;i++)if(b.indexOf(a[i])>=0)return true;return false;}
function byNearScore(a,b){return a.score-b.score||String(a.e.id).localeCompare(String(b.e.id));}
function realItemType(t){return!!t&&t!==ITEM_OTHER&&t!==NO_DETAIL&&t!==OTHER_MARKETING;}
// What an upload row bought: its line items' types, else what its vendor +
// description say (never an Amazon email subject).
function rowItemTypes(r){
  var ts=((r&&r.items)||[]).map(function(it){return it?(it.type?canonicalType(it.type):itemTypeOf(it.desc)):null;}).filter(realItemType);
  if(ts.length)return uniq(ts);
  var t=fallbackItemType({vendor:r&&r.vendor,desc:r&&r.desc});
  return realItemType(t)?[t]:[];
}
// …and an expense already in the sheet: its Items line, else vendor + description.
function expenseItemTypes(e){
  var ts=parseItemsLine(e&&e.notes).map(function(p){return p.type;}).filter(realItemType);
  if(ts.length)return uniq(ts);
  var t=fallbackItemType(e||{});
  return realItemType(t)?[t]:[];
}
function centsWords(c){c=Math.abs(c);return c<100?c+"¢":fmtMoney(c/100);}
// Why a row was left out. `auto` = decided by planImport / the sheet readers
// this run — listed with "Include anyway"; the rest are Reece's standing
// rules (never imported). Order = the order the preview lists them in.
var LEFT_OUT=[
  {reason:"personal",label:"Personal items",one:"personal item",many:"personal items",auto:true,money:true},
  {reason:"inReport",label:"Amazon emails already in the order report",one:"Amazon email already in the order report",many:"Amazon emails already in the order report",auto:true},
  {reason:"receipt",label:"Receipts attached to their matching rows",one:"receipt attached to its matching row",many:"receipts attached to their matching rows",auto:true},
  {reason:"repeat",label:"Emails listed twice in the tracker",one:"email listed twice in the tracker",many:"emails listed twice in the tracker",auto:true},
  // (10/10/26, later) the same invoice saved twice — invoiceCopies
  {reason:"sameInvoice",label:"Copies of the same invoice",one:"copy of an invoice already in this upload",many:"copies of invoices already in this upload",auto:true},
  {reason:"quoted",label:"Support replies quoting an order total",one:"support reply quoting an order total",many:"support replies quoting an order total",auto:true},
  {reason:"refundedSameDay",label:"Cancelled and refunded the same day",one:"row cancelled and refunded the same day",many:"rows cancelled and refunded the same day (a charge and its refund)",auto:true},
  {reason:"existing",label:"Already in Expenses",one:"already in Expenses",many:"already in Expenses",auto:true},
  {reason:"handLogged",label:"You already log these by hand",one:"software charge you already log by hand",many:"software charges you already log by hand",auto:true,money:true},
  {reason:"failed",label:"Failed / declined — never imported",one:"failed/declined",many:"failed/declined"},
  {reason:"cancelled",label:"Cancelled orders — never imported",one:"cancelled",many:"cancelled"},
  {reason:"noAmount",label:"No amount",one:"with no amount",many:"with no amount"},
  {reason:"cardPayment",label:"Card payments (money between accounts)",one:"card payment",many:"card payments"}
];
var LEFT_OUT_BY={};LEFT_OUT.forEach(function(d){LEFT_OUT_BY[d.reason]=d;});
// Receipt kinds that can stand in for a row's receipt (a quote or "not a
// receipt" never can).
var MERGE_KINDS={"payment receipt":true,"order confirmation":true,"invoice/bill":true};
function leftOutEntry(c,reason,why,isIncluded){
  var e={key:c.key,source:c.source,badge:c.badge,date:c.date,vendor:c.vendor,desc:c.desc,amount:c.amount,reason:reason,why:why||"",
    canInclude:!!(LEFT_OUT_BY[reason]&&LEFT_OUT_BY[reason].auto),included:!!isIncluded,fileName:c.fileName||null,orderId:c.orderId||null};
  if(reason==="personal"&&c.items)e.items=c.items.map(function(it){return{desc:it.desc,qty:it.qty,amount:it.amount};});
  if(c.refundPair)e.pair=c.refundPair;   // (a charge and its same-day refund come and go together)
  return e;
}
function keySetOf(x){
  if(!x)return new Set();
  if(x instanceof Set)return x;
  if(Array.isArray(x))return new Set(x);
  if(typeof x==="object")return new Set(Object.keys(x).filter(function(k){return x[k];}));
  return new Set();
}
function dominantCat(entries){
  var m={};entries.forEach(function(e){m[e.cat]=(m[e.cat]||0)+Math.abs(Number(e.amount)||0);});
  var best=null;Object.keys(m).forEach(function(k){if(best===null||m[k]>m[best])best=k;});
  return best;
}
// Two rows that can't be the same purchase whatever their amounts say.
function distinctRows(a,b){
  if(a.source===b.source&&(a.source==="amazon"||a.source==="tracker"))return true;   // different orders / different emails (the same email listed twice is the tracker reader's "repeat")
  if(a.splitOf&&a.splitOf===b.splitOf)return true;   // two parts of one receipt
  if(a.ready&&b.ready)return true;   // a ready file's rows were checked against each other when it was made
  if(a.key===b.key)return true;   // same file twice — its own flag
  if(a.source==="receipt"&&b.source==="receipt"&&a.transactionId&&b.transactionId&&a.transactionId!==b.transactionId)return true;
  if((a.source==="amazon"&&b._amazonMatched)||(b.source==="amazon"&&a._amazonMatched))return true;
  if(a._amazonMatched&&b._amazonMatched)return true;   // each is already explained against its Amazon order
  return false;
}
// Where a row came from, finer than `source`: two sheets (a card export and
// a store history), or two receipt files, are different origins.
function originOf(r){return r.source==="sheet"?"sheet:"+(r.sheetName||""):r.source==="receipt"?"receipt:"+(r.fileHash||groupKeyOf(r.key)):r.source;}
function blankVendor(v){var s=normVendor(v);return!s||s==="unknown";}
function isAmazonKeyOf(key,orderId){var k=String(key||"");return k==="amazon:"+orderId||k.indexOf("amazon:"+orderId+":")===0;}
// What an Amazon order was when it was imported: the stamp's "(order total
// $X)" / "(order cancelled)", else a split note's "($X)", else the sum of
// its expenses. → {total, cancelled}
function importedAmazonInfo(exps,orderId){
  var total=null,cancelled=false;
  exps.forEach(function(e){
    String(e.notes||"").split("\n").forEach(function(l){
      var p=parseStampLine(l);
      if(p&&p.key&&isAmazonKeyOf(p.key,orderId)){
        if(/order cancelled/i.test(p.label))cancelled=true;
        var m=/order total (-?\$[\d,]+\.\d{2})/i.exec(p.label);if(m&&total==null)total=parseMoney(m[1]);
        return;
      }
      var s=/^Split from Amazon order (\S+) \((-?\$[\d,]+\.\d{2})\)/.exec(String(l).trim());
      if(s&&s[1]===orderId&&total==null)total=parseMoney(s[2]);
    });
  });
  if(total==null&&!cancelled)total=round2(exps.reduce(function(s,e){return s+(Number(e.amount)||0);},0));
  return{total:total,cancelled:cancelled};
}
function summarizeRows(rows){
  var s={count:0,total:0,flagged:0,byCategory:{},bySource:{}};
  (rows||[]).forEach(function(r){
    if(!r||r.on===false)return;
    s.count++;s.total=round2(s.total+(Number(r.amount)||0));
    if(r.flags&&r.flags.length)s.flagged++;
    s.byCategory[r.cat]=round2((s.byCategory[r.cat]||0)+(Number(r.amount)||0));
    s.bySource[r.source]=(s.bySource[r.source]||0)+1;
  });
  s.marketing=s.byCategory.Marketing||0;
  return s;
}

// candidates (every source together) + the expenses already in the sheet →
// {rows, skipped, excluded, included, attachExisting, stats, stale, deletedBefore, backfill}
//   rows      everything that will be imported, each {…candidate, flags[], baseFlags[], on}
//             — flagged rows are ticked too (Reece wants them in, flagged);
//             only a row he DELETED after an earlier import (opts.deletedKeys)
//             comes back unticked, flagged. A row that absorbed a merged one
//             carries `mergedFrom` [{source, key, detail}] (stamped too, so
//             a re-run knows them) and its file (fileRef / fileName)
//   skipped   counts: failed, cancelled, noAmount, alreadyImported,
//             cardPayment, personal, inReport, receipt, repeat, existing
//   excluded  one entry per left-out candidate (not "already imported"):
//             {key, …, reason, why, canInclude} — leftOutGroups(plan) groups them
//   included  the left-out ones Reece chose to "Include anyway"
//             (opts.include): in `rows` too, flagged as they used to be
//   attachExisting  [{fileRef, name, ids}] — a left-out row's receipt file
//             goes on expenses ALREADY in the sheet that have none
//   stale     expenses ALREADY in the sheet that this upload says are wrong
//             now — an Amazon order since cancelled or re-totalled, the $0
//             placeholder of a receipt file that's now been read again:
//             [{kind, ids, flag, orderId?, detail?, …}] → applyStale at import
//   stats     summarizeRows(rows) + candidates
//   backfill  expenses ALREADY in the sheet with no Items line that this
//             upload itemizes to the cent: [{id, line}] → applyItemBackfill
//             (one whose line is a picked one-part subcategory with no
//             count, when the upload says the same type: {id, line,
//             replace: type} — the line gains its qty / count)
//   replaced  HAND-ENTERED postage rows inside the span of the USPS /
//             Stamps.com receipts coming in or already imported (±7 days):
//             [{id, date, vendor, desc, amount, flag}] → applyReplaced flags
//             them at import (postageSpan = {from, to} of those receipts)
// Hand-entered rows (no import stamp): a blank vendor is read from the
// description for matching (matchVendorOf); Software/Subscriptions rows from
// a vendor he logs by hand are left out ("handLogged") — unless he chooses
// that vendor's invoices instead (opts.useInvoices; 10/10/26, later): then
// they come in (`replacesHand`, copies of one invoice kept once —
// "sameInvoice") and his hand rows of it are in `replaced`.
//   invoiceOffers  one per vendor he logs by hand whose charges are in this
//             upload: [{family, vendor, on, count, total, from, to, copies,
//             hand: {count, total, ids}, handLogged, keys}] — the preview's
//             "Use these invoices instead of my hand entries for <vendor>"
// opts.deletedKeys: readImportTombstones(who) (or any list of keys).
// opts.useInvoices: vendor families (array / Set) whose invoices stand in
//   for his hand entries, or {family: true | false}.
// opts.include: keys (Set / array / {key:true}) of left-out rows to bring in
//   anyway. opts.noMerge: nothing is left out or merged by the engine — a
//   would-be merge is flagged instead (finalizeImport: the ticked rows are
//   exactly what was shown).
function planImport(candidates,existingExpenses,opts){
  opts=opts||{};
  var noMerge=!!opts.noMerge;
  var include=keySetOf(opts.include);
  var existing=(existingExpenses||[]).filter(function(e){return e&&e.id;});
  var skipped={failed:0,cancelled:0,noAmount:0,alreadyImported:0,inReady:0,cardPayment:0,personal:0,inReport:0,receipt:0,repeat:0,sameInvoice:0,quoted:0,refundedSameDay:0,existing:0,handLogged:0};
  var excluded=[];var included=[];var attachExisting=[];
  var addAttach=function(fileRef,name,ids){
    if(!fileRef||!ids||!ids.length)return;
    var a=null;attachExisting.forEach(function(x){if(x.fileRef===fileRef)a=x;});
    if(!a){a={fileRef:fileRef,name:name||"receipt",ids:[]};attachExisting.push(a);}
    ids.forEach(function(id){if(a.ids.indexOf(id)<0)a.ids.push(id);});
  };
  var byGroup=keyIndex(existing);
  // A receipt file read only in part before (its "receipt:<hash>:unread"
  // $0 row is in Expenses) isn't "already imported": a new read of it comes
  // in, and what was imported from it the first time is flagged against the
  // new rows by rule (a).
  var partialPrev=new Map();
  existing.forEach(function(e){importKeysOf(e.notes).forEach(function(k){
    if(!/^receipt:[^:]+:unread$/.test(k))return;
    var g=groupKeyOf(k);var l=partialPrev.get(g);if(!l){l=[];partialPrev.set(g,l);}if(l.indexOf(e)<0)l.push(e);
  });});
  var have=new Set();
  byGroup.forEach(function(l,g){if(!partialPrev.has(g))have.add(g);});
  // The sheets are cumulative (the monthly tracker, the Amazon report): an
  // expense that IS a row of this upload (its key — skipped as already
  // imported) is that row's, and can't also be the copy of ANOTHER row of the
  // same kind — two tracker emails, two Amazon orders are two purchases
  // (PropertyShark reports bought seconds apart at $16.19). keyMatched: id →
  // the kinds (amazon / tracker / receipt / sheet) that matched it by key.
  // stampGroups: id → kind → the group keys stamped on it.
  var keyMatched=new Map();
  var stampGroups=new Map();
  existing.forEach(function(e){importKeysOf(e.notes).forEach(function(k){
    var kind=k.split(":")[0];var m=stampGroups.get(e.id);if(!m){m=new Map();stampGroups.set(e.id,m);}
    var s=m.get(kind);if(!s){s=new Set();m.set(kind,s);}s.add(groupKeyOf(k));
  });});
  var deleted=deletedMap(opts.deletedKeys);
  var deletedBefore=0,leftOutBefore=0;
  // A LoanFlow ready file in the upload decides every order / email / file
  // it covers (its rows — as Reece left them in Excel — the rows he took out
  // of it, the emails it left out): a row of the ORIGINAL files (the Amazon
  // report, the tracker, a receipt read again) for one of those is dropped,
  // whatever order the files were added in, and counted "in the ready file"
  // — never "imported before" (10/9/26 fix pass: the originals still in the
  // modal used to win, so his edits and deletions were lost).
  var readyCover=new Set();var coverSeen=new Set();
  (candidates||[]).forEach(function(c){
    if(!c||!c.ready)return;
    rowKeysOf(c).forEach(function(k){readyCover.add(groupKeyOf(k));});
    var info=c.readyManifest;
    if(info&&info.covers&&!coverSeen.has(info)){coverSeen.add(info);info.covers.forEach(function(g){readyCover.add(g);});}
  });
  // Amazon orders already imported that this report shows differently.
  var stale=[];var staleSeen=new Set();
  var noteStale=function(c,kind){
    var oid=c.orderId;if(!oid||staleSeen.has(oid))return;
    var exps=byGroup.get(groupKeyOf(c.key));if(!exps||!exps.length)return;
    staleSeen.add(oid);
    var info=importedAmazonInfo(exps,oid);
    var ids=exps.map(function(e){return e.id;});
    var st=oneLine(c.status)||"Cancelled";
    if(kind==="cancelled"){
      if(info.cancelled)return;   // flagged as cancelled once already
      stale.push({kind:"cancelled",orderId:oid,ids:ids,status:st,oldTotal:info.total,newTotal:null,detail:"order cancelled",
        flag:"Amazon now shows order "+oid+" as "+st+" — delete this unless your card shows the charge"});
      return;
    }
    var now=c.orderTotal;
    if(now==null)return;
    if(!info.cancelled&&info.total!=null&&Math.abs(cents(now)-cents(info.total))<=1)return;
    stale.push({kind:"changed",orderId:oid,ids:ids,status:st,oldTotal:info.total,newTotal:now,detail:"order total "+fmtMoney(now),
      flag:info.cancelled?"Amazon shows order "+oid+" as "+st+" again ("+fmtMoney(now)+") after it was cancelled — check the amount":
        "Amazon now shows order "+oid+" at "+fmtMoney(now)+" ("+st+"); it was imported at "+fmtMoney(info.total)+" — update the amount"+(exps.length>1?"s (it's split by category)":"")});
  };
  var seen=new Map();var live=[];var cancelled=[];var rereads=new Map();var personalLeft=[];
  // Item detail for expenses ALREADY in the sheet (plan.backfill): the
  // candidates of each already-imported group, and the rows this plan
  // decides are copies of / receipts for an expense that's there.
  var haveCands=new Map();
  var bfSrc=new Map();
  var addBackfill=function(id,r){var l=bfSrc.get(id);if(!l){l=[];bfSrc.set(id,l);}if(l.indexOf(r)<0)l.push(r);};
  (candidates||[]).forEach(function(c){
    if(!c||!c.key)return;
    var gk=groupKeyOf(c.key);
    // personal / repeat: decided by the sheet readers this run — left out
    // unless Reece includes it anyway. Every other exclusion is a standing
    // rule (failed, cancelled, no amount, card payment): never imported.
    var auto=c.exclude==="personal"||c.exclude==="repeat"||c.exclude==="quoted"||c.exclude==="refundedSameDay";
    if(c.exclude&&!auto){
      skipped[c.exclude]=(skipped[c.exclude]||0)+1;
      excluded.push(leftOutEntry(c,c.exclude,c.excludeWhy||""));
      if(c.exclude==="cancelled"&&c.source==="amazon"){cancelled.push(c);noteStale(c,"cancelled");}
      return;
    }
    if(have.has(gk)){
      skipped.alreadyImported++;
      var hl=haveCands.get(gk);if(!hl){hl=[];haveCands.set(gk,hl);}hl.push(c);
      (byGroup.get(gk)||[]).forEach(function(e){var s=keyMatched.get(e.id);if(!s){s=new Set();keyMatched.set(e.id,s);}s.add(c.source);});
      if(c.source==="amazon")noteStale(c,"changed");
      return;
    }
    if(!c.ready&&readyCover.size&&readyCover.has(gk)){skipped.inReady++;return;}
    // (a charge and its same-day refund come back together)
    var forced=!!(c.forced||include.has(c.key)||(c.splitOf&&include.has(c.splitOf))||(c.refundPair&&include.has(c.refundPair)));
    var autoWhy=auto?(c.exclude==="personal"?"Doesn't look like a business supply ("+(c.personalWhy||"personal")+")":c.exclude==="quoted"?c.excludeWhy||"Support reply quoting an order total":c.exclude==="refundedSameDay"?c.excludeWhy||"Cancelled and refunded the same day":c.repeatWhy||"Listed twice"):"";
    if(auto&&!forced){
      skipped[c.exclude]++;
      excluded.push(leftOutEntry(c,c.exclude,autoWhy));
      // An order left out as personal still explains its Amazon email.
      if(c.exclude==="personal"&&c.source==="amazon")personalLeft.push(c);
      return;
    }
    var prior=seen.get(c.key);
    if(prior&&c.source!=="receipt"){skipped.alreadyImported++;return;}   // the same sheet dropped twice
    var own=(c.flags||[]).slice();
    if(auto&&c.exclude==="personal"&&c.personalFlag&&own.indexOf(c.personalFlag)<0)own.push(c.personalFlag);
    if(auto&&c.exclude==="refundedSameDay"&&c.pairFlag&&own.indexOf(c.pairFlag)<0)own.push(c.pairFlag);
    var row=Object.assign({},c,{exclude:null,flags:own.slice(),baseFlags:own.slice(),on:true});
    if(c.mergedFrom)row.mergedFrom=c.mergedFrom.slice();
    if(forced)row.forced=true;
    if(auto)included.push(leftOutEntry(c,c.exclude,autoWhy,true));
    if(prior){
      var t="Same receipt file uploaded twice ("+prior.fileName+" and "+row.fileName+") — keep or delete";
      addFlag(prior,t);addFlag(row,t);
    }else seen.set(c.key,row);
    if(partialPrev.has(gk)){
      var pp=partialPrev.get(gk);
      addFlag(row,FLAG.rereadPrefix+" ("+pp.map(function(e){return e.id;}).join(", ")+") — check for receipts that are now in twice");
      if(!rereads.has(gk))rereads.set(gk,pp);
    }
    if(deleted.has(gk)){
      // "YYYY-MM-DD" (deleted in Expenses), or "YYYY-MM-DD|removed" (taken
      // out of a ready file) / "YYYY-MM-DD|left out: …" (left out by an
      // earlier import — importDecisions).
      // A "left out" one goes through the rules first (with the Amazon
      // report in this drop it's left out as before) and is held back only
      // if nothing else explains it — below.
      var tv=parseTomb(deleted.get(gk));
      if(tv.leftOut)row._leftBefore=tv;
      else{row.on=false;deletedBefore++;addFlag(row,(tv.removed?FLAG.removedPrefix:FLAG.deletedPrefix)+(tv.day?" ("+tv.day+")":"")+" — it's unticked; tick it only if "+(tv.removed?"that":"deleting it")+" was a mistake");}
    }
    live.push(row);
  });
  rereads.forEach(function(pp){
    stale.push({kind:"reread",ids:pp.map(function(e){return e.id;}),
      flag:"This file has been read again and its receipts imported — delete this placeholder once nothing is missing"});
  });
  // A tracker email listed twice that Reece chose to include anyway: both
  // copies are flagged (one of them is to be deleted).
  live.forEach(function(r){
    if(!r.repeatOf||r.on===false)return;
    var o=seen.get(r.repeatOf);if(!o||o===r||o.on===false)return;
    addFlag(r,FLAG.repeat);addFlag(o,FLAG.repeat);
  });
  // Rows held back (deleted before) take no part in the pairing below:
  // nothing should be flagged against a row that isn't coming in.
  var act=live.filter(function(r){return r.on!==false;});

  // ── Postage: the receipts are the record (Reece, 10/9/26) ──
  // Every USPS / Stamps.com charge (or Postage purchase) in the upload comes
  // in, and is never skipped or flagged against a HAND-ENTERED postage row
  // (his lump-sum "stamps" entries don't line up 1:1 with the receipts).
  // Instead every hand-entered postage row dated inside the receipts' span
  // (the earliest − 7 days … the latest + 7 days of the postage rows coming
  // in or already imported) is listed in `replaced` and flagged at import —
  // one targeted notes update each; nothing is deleted until he clicks.
  // Those rows take no part in any other match either (they're on their way
  // out). Still as before: already-imported keys, duplicates inside the
  // upload, and matches against IMPORTED rows.
  // (postageReplaced — the preview works the same thing out from the ticked
  // rows with replacedFor, so what it promises is what Add flags.)
  // ── Software he logs by hand — or its invoices instead (10/10/26, later) ──
  // "Keep my recurring entries" (below) leaves a Software/Subscriptions row
  // from a vendor he logs by hand out — unless he chooses, in the preview,
  // "Use these invoices instead of my hand entries for <vendor>"
  // (opts.useInvoices; a ready-file row that chose them before says so —
  // "· replaces my hand entries" — unless this preview says no). Then that
  // vendor's rows come in as the record (`replacesHand` = its family; no
  // "Invoice/bill, not proof of payment" flag — the invoice IS the record
  // he chose), copies of one invoice are kept once (below), and his hand rows
  // of it near the invoices' dates go in `replaced` (postageReplaced). At Add
  // (noMerge) the rows carry the plan's mark as it is. (Every row, the ones
  // held back unticked too — he may tick one in the preview.)
  var handSoft=handSoftwareFamilies(existing);
  var invChoice=invoiceChoices(opts.useInvoices);
  if(!noMerge)live.forEach(function(r){
    var fam=r.cat==="Software/Subscriptions"?vendorFamily(r.vendor):"";
    var on=!!fam&&handSoft.has(fam)&&(invChoice.has(fam)?invChoice.get(fam):r.replacesHand===fam);
    if(!on){if(r.replacesHand)delete r.replacesHand;return;}
    r.replacesHand=fam;
    var notBill=function(f){return f!==FLAG.bill;};
    r.flags=r.flags.filter(notBill);
    if(r.baseFlags)r.baseFlags=r.baseFlags.filter(notBill);
  });
  var pr=postageReplaced(act,existing);
  var recordRow=pr.recordRow,handRecord=pr.handRecord,replacing=pr.replacing,replaced=pr.replaced;

  // ── Left out / merged by this plan ──
  // `_drop` = {reason, why, into: the rows it was merged into, existIds: the
  // expenses it is a copy of}. A row Reece chose to "Include anyway"
  // (`forced`) is never dropped: it gets the flag it always had and is
  // listed as included. noMerge (finalizeImport) drops nothing.
  var dropRow=function(r,reason,why,into,existIds){r._drop={reason:reason,why:why,into:into||null,existIds:existIds||null};};
  var dropped=function(r){return!!r._drop;};
  // r merged into t: t carries r's source key (stamped at import, so a
  // re-run knows r is in) and r's file when t has none of its own.
  var mergeInto=function(t,r){
    var m=t.mergedFrom||(t.mergedFrom=[]);
    var add=function(x){if(!m.some(function(y){return y.key===x.key;}))m.push(x);};
    add({source:r.source,key:r.key,detail:r.source==="receipt"?r.fileName:r.source==="sheet"?r.sheetName:null});
    (r.mergedFrom||[]).forEach(add);
    if(r.fileRef&&!t.fileRef){t.fileRef=r.fileRef;t.fileName=r.fileName;}
  };
  var rowWords=function(t){return t.badge+" ("+[t.date,t.vendor,fmtMoney(t.amount)].filter(Boolean).join(" · ")+")";};

  // ── The same invoice saved twice (10/10/26, later) ──
  // Among the invoices he chose over his hand entries (replacesHand): copies
  // of one invoice (invoiceCopies — byte-different files, so different keys:
  // the same vendor, total, date and invoice number) are kept once. A copy is
  // merged into the one kept — its key stamped there, so a later drop of
  // that file is "imported before" — and listed under "Copies of the same
  // invoice" (Include anyway brings it back; rule c then flags the pair).
  if(!noMerge){
    invoiceCopies(act.filter(function(r){return!!r.replacesHand&&!dropped(r);})).forEach(function(p){
      var t=p.of;
      var why="Copy of "+(t.transactionId?"invoice "+oneLine(t.transactionId)+" ("+[t.fileName,t.date,fmtMoney(t.amount)].filter(Boolean).join(" · ")+")":(t.fileName||"the invoice")+" ("+[t.date,fmtMoney(t.amount)].filter(Boolean).join(" · ")+")")+" — the same invoice, kept once";
      if(p.copy.forced){included.push(leftOutEntry(p.copy,"sameInvoice",why,true));return;}
      mergeInto(t,p.copy);
      dropRow(p.copy,"sameInvoice",why,[t]);
    });
  }

  // ── Amazon orders: this upload, already in Expenses, and cancelled ──
  var orders=new Map();
  act.forEach(function(r){
    if(r.source!=="amazon")return;
    var o=orders.get(r.orderId);
    if(!o){o={id:r.orderId,date:r.date,total:r.orderTotal,rows:[],exp:[],cancelled:false,claimed:{}};orders.set(r.orderId,o);}
    o.rows.push(r);
  });
  existing.forEach(function(e){
    importKeysOf(e.notes).forEach(function(k){
      var m=/^amazon:([^:]+)/.exec(k);if(!m)return;
      var o=orders.get(m[1]);
      if(!o){o={id:m[1],date:e.date,total:0,rows:[],exp:[],cancelled:false,claimed:{}};orders.set(m[1],o);}
      if(o.rows.length)return;
      if(o.exp.indexOf(e)<0){o.exp.push(e);o.total=round2(o.total+(Number(e.amount)||0));}
      // Its email / receipt was merged into it at an earlier import (stamped):
      // another email or receipt of the same total is another purchase (or
      // at most a possible copy — rule a), never certainly this order.
      var sg=stampGroups.get(e.id);
      if(sg)["tracker","receipt"].forEach(function(kind){if(sg.has(kind))o.claimed[kind]=true;});
    });
  });
  // An order already in Expenses counts at the total it was imported at
  // (its stamp) — a part left out as personal isn't in the amounts.
  orders.forEach(function(o){if(!o.rows.length&&o.exp.length){var info=importedAmazonInfo(o.exp,o.id);if(info.total!=null)o.total=info.total;}});
  cancelled.forEach(function(c){if(!orders.has(c.orderId))orders.set(c.orderId,{id:c.orderId,date:c.date,total:c.orderTotal||c.amount||0,rows:[],exp:[],cancelled:true,claimed:{}});});
  // Orders left out entirely as personal items: nothing of theirs comes in,
  // but their Amazon email must not come in either.
  personalLeft.forEach(function(c){
    var o=orders.get(c.orderId);
    if(!o){o={id:c.orderId,date:c.date,total:c.orderTotal||c.amount||0,rows:[],exp:[],cancelled:false,claimed:{}};orders.set(c.orderId,o);}
    o.personal=true;o.personalAmt=round2((o.personalAmt||0)+(Number(c.amount)||0));
  });
  var personalOnly=function(o){return!o.cancelled&&!o.rows.length&&!o.exp.length&&!!o.personal;};
  // What an Amazon refund refunded, when that's certain: the whole order (to
  // the cent, none of it cancelled or left out as personal) or exactly one
  // of its lines — so a returned ream nets out of Paper, sheets and all.
  // The order's lines: its rows in this upload, or — an order imported
  // before — this upload's candidates for it (the report lists it again).
  // (`cats` = each line's category — the order's part it is on — so a
  // refund of the split order's office-supply line nets out of
  // Office/Equipment, not the order's biggest part.)
  var linesWithCats=function(list){var lines=[],cats=[];list.forEach(function(x){(x.items||[]).forEach(function(l){lines.push(l);cats.push(x.cat);});});return{lines:lines,cats:cats};};
  var orderLinesOf=function(o){
    if(!o||o.cancelled||o.personal)return null;
    if(o.rows.length)return Object.assign(linesWithCats(o.rows),{total:o.total});
    var hc=haveCands.get("amazon:"+o.id);
    if(!hc||!hc.length||hc.some(function(c){return c.source!=="amazon"||!!c.exclude||c.itemsFrom!=="lines";}))return null;
    return Object.assign(linesWithCats(hc),{total:hc[0].orderTotal});
  };
  var refundLinesOf=function(r,hit){
    if(r.itemsFrom==="lines"||!hit||hit.length!==1||(Number(r.amount)||0)>=0)return null;
    var ol=orderLinesOf(hit[0]);
    if(!ol||!ol.lines.length||ol.total==null)return null;
    var refC=Math.abs(cents(r.amount));
    if(refC===cents(ol.total))return{items:ol.lines.slice(),cats:ol.cats.slice()};
    var one=[];ol.lines.forEach(function(l,i){if(cents(l.amount)===refC)one.push(i);});
    return one.length===1?{items:[ol.lines[one[0]]],cats:[ol.cats[one[0]]]}:null;
  };
  var refundItemsOf=function(r,hit){var x=refundLinesOf(r,hit);return x?x.items:null;};
  // A refund imported before (its tracker email is in Expenses) whose order
  // is in this upload: the same lines, as a copy — the candidate itself is
  // the caller's and isn't changed — for the backfill below.
  var haveResolved=new Map();
  haveCands.forEach(function(cs){cs.forEach(function(c){
    if(c.source==="amazon"||!c.orderIds||!c.orderIds.length)return;
    var hit=[];c.orderIds.forEach(function(id){var o=orders.get(id);if(o)hit.push(o);});
    var items=refundItemsOf(c,hit);
    if(items)haveResolved.set(c,Object.assign({},c,{items:items,itemsFrom:"lines"}));
  });});
  var byDate=new Map();
  orders.forEach(function(o){if(!o.date)return;var l=byDate.get(o.date);if(!l){l=[];byDate.set(o.date,l);}l.push(o);});
  var usedExisting=new Set();
  var idsOf=function(os){return os.map(function(o){return o.id;}).join(", ");};
  var expIdsOf=function(os){var ids=[];os.forEach(function(o){o.exp.forEach(function(e){ids.push(e.id);});});return uniq(ids).join(", ");};
  var sumOf=function(os){return round2(os.reduce(function(s,o){return s+(Number(o.total)||0);},0));};
  // `claimed[kind]` = a row of that kind (tracker email / receipt / sheet
  // line; "existing" = an expense already in the sheet) was matched to the
  // orders by their TOTAL — two emails can't both be "the" email for one
  // order, but the email and a receipt of the same purchase both match it.
  // An order matched by its ORDER ID (a receipt or refund showing it) stays
  // open to the total match: the tracker email for it is a copy too.
  var flagMatch=function(r,hit,how){
    if(how==="sum")hit.forEach(function(o){o.claimed[r.source]=true;});
    r._amazonMatched=true;
    var total=sumOf(hit);
    var when=hit[0].date;
    var liveO=hit.filter(function(o){return o.rows.length;});
    var expO=hit.filter(function(o){return!o.rows.length&&o.exp.length;});
    var canO=hit.filter(function(o){return o.cancelled;});
    if(canO.length===hit.length){
      addFlag(r,"Matches Amazon order"+(hit.length>1?"s ":" ")+idsOf(hit)+" that "+(hit.length>1?"were":"was")+" cancelled ("+when+" · "+fmtMoney(total)+") — this charge probably never went through; delete it unless your card shows it");
      return;
    }
    if(liveO.length){
      addFlag(r,(how==="id"?"Same Amazon order as ":"Same purchase as Amazon order"+(liveO.length>1?"s ":" "))+idsOf(liveO)+" in the Amazon Business report ("+when+" · "+fmtMoney(total)+(hit.length>1?" for "+hit.length+" orders":"")+") — the report lists it item by item; delete this copy");
      var other="("+[r.date,r.vendor,fmtMoney(r.amount)].filter(Boolean).join(" · ")+(hit.length>1?", covering "+hit.length+" orders":"")+")";
      liveO.forEach(function(o){o.rows.forEach(function(or){addFlag(or,"Also in a "+r.badge+" "+other+" — delete that copy, keep this one");});});
      var dc=dominantCat([].concat.apply([],liveO.map(function(o){return o.rows;})));
      if(dc)r.cat=dc;
      // The receipt goes on the rows to KEEP too (every part of a split
      // order shares the one upload) — deleting "this copy" must not leave
      // the file pointed at by nothing.
      if(r.fileRef)liveO.forEach(function(o){o.rows.forEach(function(or){if(!or.fileRef){or.fileRef=r.fileRef;or.fileName=r.fileName;}});});
    }
    if(expO.length){
      addFlag(r,"Same "+(how==="id"?"Amazon order":"purchase")+" as "+expIdsOf(expO)+" ("+expO[0].date+" · Amazon Business · "+fmtMoney(sumOf(expO))+"), already in Expenses — delete this copy");
      expO.forEach(function(o){o.exp.forEach(function(e){usedExisting.add(e.id);});});
      var dce=dominantCat([].concat.apply([],expO.map(function(o){return o.exp;})));
      if(dce&&!liveO.length)r.cat=dce;
      // …and on the expense already in the sheet, when it has none stored.
      if(r.fileRef){
        var need=[];expO.forEach(function(o){o.exp.forEach(function(e){if(!e.receiptPath&&need.indexOf(e.id)<0)need.push(e.id);});});
        if(need.length)r.attachExisting=uniq((r.attachExisting||[]).concat(need));
      }
    }
    var perO=hit.filter(personalOnly);
    if(perO.length)addFlag(r,(perO.length===hit.length?"Same purchase as ":"Includes ")+"Amazon order"+(perO.length>1?"s ":" ")+idsOf(perO)+" ("+when+" · "+fmtMoney(sumOf(perO))+"), left out as personal items — delete this unless it was for the business");
    if(perO.length&&perO.length===hit.length)r.cat="Other";   // like the orders it is
    if(canO.length&&canO.length<hit.length)addFlag(r,"Includes cancelled Amazon order"+(canO.length>1?"s ":" ")+idsOf(canO)+" — check the amount");
  };
  // An Amazon email or receipt that IS the report's order(s) — to the cent,
  // or by order id — is MERGED: left out, its source key + file on the
  // order's rows (orders already in Expenses: its file on those expenses
  // when they have none). Anything short of certain (a cent off, live and
  // cancelled orders mixed, a card line, a quote) is flagged as before —
  // and so is a row Reece chose to include anyway.
  var mergeAmazon=function(r,hit,how){
    var canO=hit.filter(function(o){return o.cancelled;});
    var liveO=hit.filter(function(o){return!o.cancelled&&o.rows.length;});
    var expO=hit.filter(function(o){return!o.cancelled&&!o.rows.length&&o.exp.length;});
    var perO=hit.filter(personalOnly);
    var mergeable=(r.source==="tracker"||(r.source==="receipt"&&MERGE_KINDS[r.kind]))&&(Number(r.amount)||0)>0&&(canO.length===0||canO.length===hit.length);
    if(noMerge||!mergeable){flagMatch(r,hit,how);return;}
    var total=sumOf(hit);var when=hit[0].date;var isRec=r.source==="receipt";
    var need=[];expO.forEach(function(o){o.exp.forEach(function(e){if(!e.receiptPath&&need.indexOf(e.id)<0)need.push(e.id);});});
    var liveRows=[].concat.apply([],liveO.map(function(o){return o.rows;}));
    var fileTail=!isRec?"":liveRows.length?(r.fileRef&&liveRows.every(function(x){return x.fileRef;})?" — that order already has a file":" — attached to it"):need.length?" — attached to it":expO.length?" — it already has its receipt":"";
    var perTail=perO.length&&perO.length<hit.length?"; order"+(perO.length>1?"s ":" ")+idsOf(perO)+" left out as personal items":"";
    var reason,why;
    if(canO.length){reason=isRec?"receipt":"inReport";why="Matches Amazon order"+(hit.length>1?"s ":" ")+idsOf(hit)+" that "+(hit.length>1?"were":"was")+" cancelled ("+when+" · "+fmtMoney(total)+") — the charge never went through";}
    else if(liveO.length){reason=isRec?"receipt":"inReport";why=(isRec?"Receipt for ":"Same purchase as ")+"Amazon order"+(liveO.length>1?"s ":" ")+idsOf(liveO)+" in the Amazon Business report ("+when+" · "+fmtMoney(total)+(hit.length>1?" for "+hit.length+" orders":"")+")"+(expO.length?" and "+expIdsOf(expO)+" already in Expenses":"")+perTail+fileTail;}
    else if(expO.length){reason=isRec?"receipt":"existing";why=(isRec?"Receipt for ":"Already in Expenses as ")+expIdsOf(expO)+" (Amazon order"+(expO.length>1?"s ":" ")+idsOf(expO)+" · "+when+" · "+fmtMoney(total)+")"+perTail+fileTail;}
    else{reason=isRec?"receipt":"inReport";why=(isRec?"Receipt for ":"Same purchase as ")+"Amazon order"+(hit.length>1?"s ":" ")+idsOf(hit)+" in the Amazon Business report ("+when+" · "+fmtMoney(total)+") — left out as personal items";}
    if(r.forced){included.push(leftOutEntry(r,reason,why,true));flagMatch(r,hit,how);return;}
    if(how==="sum")hit.forEach(function(o){o.claimed[r.source]=true;});
    r._amazonMatched=true;
    liveRows.forEach(function(or){mergeInto(or,r);});
    var exIds=[];expO.forEach(function(o){o.exp.forEach(function(e){usedExisting.add(e.id);if(exIds.indexOf(e.id)<0)exIds.push(e.id);});});
    if(r.source==="receipt")exIds.forEach(function(id){addBackfill(id,r);});   // its item lines, if its total is that expense's
    if(r.fileRef)addAttach(r.fileRef,r.fileName,need);
    dropRow(r,reason,why,liveRows.length?liveRows:null,exIds.length?exIds:null);
  };
  // (b) an order id written on another source (refund emails, receipts)
  // (Ready-file rows were decided when the file was made — (b), (b2), (c)
  // and the refund flag pass them by; the live-table rules still run.)
  act.forEach(function(r){
    if(r.ready||r.source==="amazon"||!r.orderIds||!r.orderIds.length)return;
    var hit=[];
    r.orderIds.forEach(function(id){var o=orders.get(id);if(o)hit.push(o);});
    if(!hit.length)return;
    if((Number(r.amount)||0)<0){
      r._amazonMatched=true;r._refundExplained=true;
      // A refund for what was left out as personal items (the whole order,
      // or exactly its personal share) is left out with it — never a
      // negative expense for a purchase that was never counted. Included
      // anyway (or at Add, noMerge) it is flagged instead, in Other.
      var refC=Math.abs(cents(r.amount));
      var perHit=hit.filter(function(o){return o.personal&&(personalOnly(o)||cents(o.personalAmt)===refC);});
      if(perHit.length===hit.length){
        var only=hit.every(personalOnly);
        var pwhy="Refund on Amazon order"+(hit.length>1?"s ":" ")+idsOf(hit)+" ("+hit[0].date+") — "+(only?"left out as personal items":"the amount of its personal items, which were left out")+", so the refund is left out too";
        r.cat="Other";
        if(!noMerge&&!r.forced){dropRow(r,"personal",pwhy);return;}
        if(!noMerge)included.push(leftOutEntry(r,"personal",pwhy,true));
        addFlag(r,"Refund for personal items that were left out (Amazon order"+(hit.length>1?"s ":" ")+idsOf(hit)+") — delete this unless they were for the business");
        return;
      }
      hit.forEach(function(o){addFlag(r,"Refund on Amazon order "+o.id+" ("+o.date+" · "+fmtMoney(o.total)+(o.cancelled?", cancelled":"")+(personalOnly(o)?"), which was left out as personal items":"), which is in here — keep both so the refund nets it out"));});
      // It nets out in the ORDER's category (paper bought → Marketing) — the
      // kept part of a split order, never a part left out: the category of
      // the line(s) it refunded when that's certain and one (refundLinesOf);
      // a refund that carries its own certain lines keeps its category when
      // the order has a part in it (a refund split by the parts it
      // returned); else the order's main part.
      var hitRows=[].concat.apply([],hit.map(function(o){return o.rows.length?o.rows:o.exp;}));
      var ownLines=r.itemsFrom==="lines";
      var rl=refundLinesOf(r,hit);
      var lc=rl?uniq(rl.cats):[];
      if(lc.length===1)r.cat=lc[0];
      else if(!(ownLines&&hitRows.some(function(x){return x.cat===r.cat;}))){var rc=dominantCat(hitRows);if(rc)r.cat=rc;}
      // What it refunded, when that's certain.
      if(rl){r.items=rl.items;r.itemsFrom="lines";}
      return;
    }
    mergeAmazon(r,hit,"id");
  });
  // (b) the same purchase without an order id: an Amazon email / receipt
  // whose total is the sum of the orders placed that day (±1) — to the cent
  // merges, a cent off is flagged. Emails first, then receipts, then sheet
  // lines; smallest amounts first so a single order is claimed before a
  // bigger combination.
  var srcRank=function(r){return r.source==="tracker"?0:r.source==="receipt"?1:2;};
  act.filter(function(r){return!r.ready&&r.source!=="amazon"&&!r._amazonMatched&&(Number(r.amount)||0)>0&&isAmazonish(r.vendor);})
    .sort(function(a,b){return srcRank(a)-srcRank(b)||a.amount-b.amount||a.at-b.at;})
    .forEach(function(r){
      var pool=[];
      [-1,0,1].forEach(function(n){(byDate.get(isoPlus(r.date,n))||[]).forEach(function(o){if(!o.claimed[r.source]&&!o.claimed.existing)pool.push(o);});});
      if(!pool.length)return;
      var open=pool.filter(function(o){return!o.cancelled;});
      var exact=subsetSum(open,r.amount,0)||subsetSum(pool,r.amount,0);
      if(exact){mergeAmazon(r,exact,"sum");return;}
      var near=subsetSum(open,r.amount,1)||subsetSum(pool,r.amount,1);
      if(near)flagMatch(r,near,"sum");
    });
  // Reverse: an Amazon expense already in the sheet (an earlier email /
  // receipt import, or typed by hand) whose total is this upload's orders.
  // To the cent, with "Amazon" in its vendor (a hand-entered row with no
  // vendor: in its description — matchVendorOf) → those orders are skipped
  // (already in Expenses); otherwise flagged. Never a hand-entered postage
  // row the receipts replace. A HAND-ENTERED one may also be within a
  // dollar of them (whole-dollar typing) — flagged, never certain. An order
  // an Amazon email / receipt of this upload was merged into is still open
  // to a HAND entry (only another expense's match closes it): his typed
  // entry is a record of its own, not one more copy of that email — and Add
  // (finalizeImport, where the merged email isn't among the ticked rows)
  // must find the same pairs the preview showed.
  existing.filter(function(e){return!usedExisting.has(e.id)&&!replacing.has(e.id)&&isAmazonish(e.vendor||e.desc)&&(Number(e.amount)||0)>0&&!importKeysOf(e.notes).some(function(k){return/^amazon:/.test(k);});})
    .sort(function(a,b){return(Number(a.amount)||0)-(Number(b.amount)||0);})
    .forEach(function(e){
      var pool=[];var byHand=isHandEntered(e);
      [-1,0,1].forEach(function(n){(byDate.get(isoPlus(e.date,n))||[]).forEach(function(o){if((byHand?!o.claimed.existing:!Object.keys(o.claimed).length)&&!o.cancelled&&o.rows.length)pool.push(o);});});
      if(!pool.length)return;
      var exact=subsetSum(pool,Number(e.amount),0);
      var hit=exact||subsetSum(pool,Number(e.amount),1);
      // A HAND entry is typed in whole dollars — one "Amazon $391" for three
      // orders that day of $391.99 — so within a dollar is a doubt too (never
      // certain): the orders come in, flagged against it.
      var off=0;
      if(!hit&&isHandEntered(e)){hit=subsetSum(pool,Number(e.amount),99);if(hit)off=Math.abs(hit.reduce(function(s,o){return s+cents(o.total);},0)-cents(e.amount));}
      if(!hit)return;
      usedExisting.add(e.id);
      var certain=!noMerge&&!!exact&&isAmazonish(matchVendorOf(e));
      var why="Already in Expenses as "+e.id+" "+describeExp(e)+(hit.length>1?" — one charge covering "+hit.length+" orders":"");
      var cover=hit.length>1?" covering "+hit.length+" orders":"";
      hit.forEach(function(o){o.claimed.existing=true;o.rows.forEach(function(or){
        if(certain&&!or.forced){dropRow(or,"existing",why,null,[e.id]);addBackfill(e.id,or);return;}
        if(certain)included.push(leftOutEntry(or,"existing",why,true));
        or._dupExisting=true;
        addFlag(or,off?"Same purchase as your hand entry "+e.id+" "+describeExp(e)+cover+" — "+centsWords(off)+" apart (typed rounded?); if so, keep the order"+(hit.length>1?"s":"")+" and delete the hand entry"
          :"Same purchase as "+e.id+" "+describeExp(e)+cover+", already in Expenses — keep one, delete the other");
      });});
    });

  // A one-item Amazon email for something personal (a controller, dog food)
  // that no order in the report explains: left out as a personal item —
  // listed, with Include anyway (then, and at Add, flagged instead).
  act.forEach(function(r){
    if(!r.personalHint||dropped(r)||r._amazonMatched)return;
    var why="Doesn't look like a business supply ("+r.personalHint+")";
    r.cat="Other";
    if(!noMerge&&!r.forced){dropRow(r,"personal",why);return;}
    if(!noMerge)included.push(leftOutEntry(r,"personal",why,true));
    addFlag(r,why+" — keep it or delete it");
  });

  // (b2) a receipt that exactly matches a row of this upload — the same
  // vendor (vendorFamily: Staples = "STAPLES 00123" = "Staples Inc", Amazon =
  // Amazon Business), to the cent, ±2 days or the same order / transaction
  // number — IS that row's receipt: merged into it (its file and source key
  // go on the row). An Amazon order is matched as a whole (all its parts).
  // Best pair first (same number, then fewest days apart), one-to-one.
  // A receipt split by category (splitReceipt) is matched WHOLE — its total,
  // all its lines, the category the whole receipt has — and all its parts
  // merge together (each part's key stamped on the row), or none does.
  // Ready-file rows (decided before) are never merged here, either way.
  if(!noMerge){
    var recs0=act.filter(function(r){return r.source==="receipt"&&!r.ready&&!dropped(r)&&!r._amazonMatched&&MERGE_KINDS[r.kind]&&cents(r.amount)>0&&vendorFamily(r.vendor);});
    var recBy=new Map();var recs=[];
    recs0.forEach(function(r){
      // (the same file dropped twice is two receipts — each its own unit)
      var id=r.splitOf?r.splitOf+"|"+(r.fileName||""):null;var u=id?recBy.get(id):null;
      if(!u){u={key:r.key,parts:[],amount:r.splitOf?r.splitTotal:r.amount,date:r.date,vendor:r.vendor,transactionId:r.transactionId,orderIds:r.orderIds,
        fileRef:r.fileRef,items:r.splitOf?(r.allItems||r.items):r.items,itemsFrom:r.itemsFrom,cat:r.splitOf?(r.wholeCat||r.cat):r.cat,personalItemsFlag:r.personalItemsFlag,count:r.split?r.split.count:1};if(id)recBy.set(id,u);recs.push(u);}
      u.parts.push(r);if(r.forced)u.forced=true;
    });
    recs=recs.filter(function(u){return u.parts.length===u.count;});   // every part here, or it isn't the whole receipt
    if(recs.length){
      var tUnits=new Map();var seenOrder=new Set();
      // A tracker email split by its item lines (joinItemDetails) is matched
      // WHOLE, like an Amazon order: its total, every part that's coming in.
      var splitParts=new Map();
      act.forEach(function(t){if(t.source!=="receipt"&&t.splitOf&&!dropped(t)){var l=splitParts.get(t.splitOf);if(!l){l=[];splitParts.set(t.splitOf,l);}l.push(t);}});
      act.forEach(function(t){
        // A bill / notice / support reply is not the charge: a payment
        // receipt merged into it would hide the proof of payment under a
        // "this is a bill" flag. The receipt comes in as its own row and the
        // pair is flagged (rule c below).
        if(t.ready||t.source==="receipt"||dropped(t)||t._amazonMatched||t.notice||t.support)return;
        var u;
        if(t.source==="amazon"){
          if(seenOrder.has(t.orderId))return;seenOrder.add(t.orderId);
          u={rows:orders.get(t.orderId).rows.filter(function(p){return!dropped(p);}),amount:t.orderTotal,date:t.date,ids:[t.orderId],txn:null,lead:t};
        }else if(t.splitOf&&t.splitTotal!=null){
          if(seenOrder.has("split:"+t.splitOf))return;seenOrder.add("split:"+t.splitOf);
          u={rows:splitParts.get(t.splitOf)||[t],amount:t.splitTotal,date:t.date,ids:t.orderIds||[],txn:t.transactionId||null,lead:t,whole:true};
        }else u={rows:[t],amount:t.amount,date:t.date,ids:t.orderIds||[],txn:t.transactionId||null,lead:t};
        u.fam=vendorFamily(t.vendor);
        var c=cents(u.amount);if(c<=0||!u.fam)return;
        var l=tUnits.get(c);if(!l){l=[];tUnits.set(c,l);}l.push(u);
      });
      var rp=[];
      recs.forEach(function(r){
        var fam=vendorFamily(r.vendor);
        (tUnits.get(cents(r.amount))||[]).forEach(function(u){
          if(u.fam!==fam)return;
          var idHit=!!(r.transactionId&&u.txn&&r.transactionId===u.txn)||(r.orderIds||[]).some(function(id){return u.ids.indexOf(id)>=0;});
          var days=dayDiff(r.date,u.date);
          if(!(days<=2||idHit))return;
          rp.push({r:r,u:u,score:(idHit?-100:0)+Math.min(days,99)});
        });
      });
      rp.sort(function(a,b){return a.score-b.score||String(a.r.key).localeCompare(String(b.r.key))||String(a.u.lead.key).localeCompare(String(b.u.lead.key));});
      var recDone=new Set();var unitTaken=new Set();
      rp.forEach(function(p){
        if(recDone.has(p.r)||unitTaken.has(p.u))return;
        recDone.add(p.r);
        var t=p.u.lead;
        var what=t.source==="amazon"?"Amazon order "+t.orderId+" ("+[t.date,fmtMoney(p.u.amount)].join(" · ")+")":"the "+rowWords(t);
        var why="Receipt for "+what+(p.r.fileRef&&p.u.rows.every(function(x){return x.fileRef;})?" — that row already has its own file":" — attached to it");
        if(p.r.forced){var lp=p.r.parts[0];included.push(leftOutEntry(lp.splitOf?Object.assign({},lp,{key:lp.splitOf,amount:lp.splitTotal,desc:lp.splitDesc||lp.desc}):lp,"receipt",why,true));return;}
        unitTaken.add(p.u);
        p.u.rows.forEach(function(x){p.r.parts.forEach(function(part){mergeInto(x,part);});});
        // The receipt's item lines say what was bought ("ENVELOPE SECURITY"
        // → Marketing); an email subject or a card line doesn't. An Amazon
        // order keeps its own, item-by-item category (and its own personal
        // split). Personal lines on the receipt stay a doubt on the row it
        // attaches to — in its base flags too, so Add keeps it.
        // (A row that has its own item lines already — the invoice export's
        // Itemized Details, 10/10/26 — keeps them, its category and its
        // split: they are the same receipt's lines.)
        var own=t.source==="amazon"||t.itemized||p.u.whole;
        if(!own&&p.r.items&&p.r.items.some(function(it){return it&&it.desc;})&&p.r.cat)t.cat=p.r.cat;
        // …and they ARE what this row bought (the same purchase, to the
        // cent): its Items line comes from them — "Here's your Staples
        // receipt" says nothing, the receipt lists the envelopes.
        if(!own&&p.r.itemsFrom==="lines"&&t.itemsFrom!=="lines"){t.items=p.r.items.slice();t.itemsFrom="lines";}
        if(!own&&p.r.personalItemsFlag){
          addFlag(t,p.r.personalItemsFlag);
          if(t.baseFlags&&t.baseFlags.indexOf(p.r.personalItemsFlag)<0)t.baseFlags.push(p.r.personalItemsFlag);
        }
        p.r.parts.forEach(function(part){dropRow(part,"receipt",why,p.u.rows.slice());});
      });
    }
  }

  // (a) duplicates of what's already in Expenses, one-to-one: same amount
  // within a cent within 5 days, or same vendor + amount within 15 days.
  // Matched GLOBALLY, best pair first (certain beats possible, same vendor
  // beats any vendor, then fewer days apart) — an earlier unrelated row with
  // the same amount must not take the real duplicate's match. A split
  // Amazon order is compared as the whole order as well as part by part.
  // CERTAIN (same vendorFamily on both sides, to the cent, ≤ 3 days) → the
  // row is skipped ("Already in Expenses as EXP-0123"); anything less (no
  // vendor on the expense, another vendor, a cent off, 4–15 days) is
  // imported AND flagged "Possible duplicate of …".
  // Cumulative sheets: an expense that is a row of this upload by its key
  // (keyMatched) is never the copy of another row of that same kind; an
  // expense stamped from ANOTHER Amazon order is never a copy of this one
  // (order ids are unique); one stamped from another tracker email /
  // receipt file / sheet row of the same kind is at most a POSSIBLE copy
  // (the file was renamed, the receipt scanned twice) — never certain.
  // A hand-entered row with no vendor is matched by the vendor its
  // description names (matchVendorOf). A postage row of the upload is never
  // matched to a hand-entered postage row, and a hand-entered postage row the
  // receipts replace is matched to nothing (both above). A HAND-ENTERED row
  // is also matched within a dollar (he types whole dollars — $60 for a
  // $60.63 receipt): ±5 days, same vendor or (no vendor on a side) same item
  // type → "Possible duplicate of your hand entry …", never certain.
  // Hand entries are typed in whole dollars ("Envelopes $60" for a $60.63
  // receipt): a HAND-ENTERED row within a dollar (but not ±1¢ — that's the
  // match above), ±5 days, the same vendor — or, when either has no vendor
  // to go on, the same item type — is a possible duplicate too (NEAR_DAYS,
  // above). Never certain: the upload row comes in, flagged, and the score
  // keeps every ±1¢ pair ahead of it.
  // (10/9/26, later — his $450.00 "business cards" for the $447.91 Vistaprint
  // order: the window is now max($5, 1.5%) and ±14 days — nearTolCents.)
  // (handByDay: day → vendor family → hand rows sorted by cents — a unit
  // looks at its own family and the rows with none, ±14 days nearest first,
  // a binary search each, and stops once no later day can beat its best 3.)
  var byCents=new Map();var handRows=[];
  existing.forEach(function(e){
    if(usedExisting.has(e.id)||replacing.has(e.id))return;var k=cents(e.amount);if(!k)return;var l=byCents.get(k);if(!l){l=[];byCents.set(k,l);}l.push(e);
    if(dayNum(e.date)!=null&&isHandEntered(e))handRows.push(e);
  });
  var mvOf=new Map();
  var mv=function(e){var v=mvOf.get(e);if(v===undefined){v=matchVendorOf(e);mvOf.set(e,v);}return v;};
  var feOf=new Map();
  var famOfExp=function(e){var f=feOf.get(e);if(f===undefined){f=vendorFamily(mv(e));feOf.set(e,f);}return f;};
  var handByDay=new Map();
  handRows.forEach(function(e){
    var n=dayNum(e.date);var dm=handByDay.get(n);if(!dm){dm=new Map();handByDay.set(n,dm);}
    var f=famOfExp(e);var l=dm.get(f);if(!l){l=[];dm.set(f,l);}l.push(e);
  });
  handByDay.forEach(function(dm){dm.forEach(function(l){l.sort(function(a,b){return cents(a.amount)-cents(b.amount);});});});
  var firstAtLeast=function(list,v){var lo=0,hi=list.length;while(lo<hi){var mid=(lo+hi)>>1;if(cents(list[mid].amount)<v)lo=mid+1;else hi=mid;}return lo;};
  var DAY_ORDER=[0];for(var dk=1;dk<=NEAR_DAYS;dk++)DAY_ORDER.push(-dk,dk);
  var typesOfExp=new Map();
  var expTypes=function(e){var t=typesOfExp.get(e);if(!t){t=expenseItemTypes(e);typesOfExp.set(e,t);}return t;};
  var unitTypes=function(u){if(!u.types)u.types=uniq([].concat.apply([],u.rows.map(rowItemTypes)));return u.types;};
  var dupText=function(e){return"Possible duplicate of "+e.id+" "+describeExp(e)+" — keep or delete";};
  var nearText=function(e,diff,whole){return"Possible duplicate of your hand entry "+e.id+" "+describeExp(e)+(whole?" (the whole order)":"")+" — "+centsWords(diff)+" apart (typed rounded?); if it's the same purchase, keep this one and delete the hand entry";};
  // A split Amazon order — or a receipt split by category — is compared
  // WHOLE as well as part by part (splitIdOf: its order / its receipt).
  var splitIdOf=function(r){return r.split?(r.source==="amazon"?"amazon:"+r.orderId:"split:"+(r.splitOf||r.key)):null;};
  var splitRows=new Map();
  act.forEach(function(r){var s=splitIdOf(r);if(!s)return;var l=splitRows.get(s);if(!l){l=[];splitRows.set(s,l);}l.push(r);});
  var units=[];var wholeOf=new Map();
  act.slice().sort(function(a,b){return(a.date||"").localeCompare(b.date||"")||a.at-b.at;}).forEach(function(r){
    if(dropped(r)||r._dupExisting||r._amazonMatched)return;
    var split=splitIdOf(r);
    if(split&&!wholeOf.has(split)){
      var parts=splitRows.get(split).filter(function(p){return!dropped(p);});
      var wholeAmt=r.source==="amazon"?r.orderTotal:r.splitTotal;
      if(wholeAmt!=null&&parts.every(function(p){return!p._dupExisting&&!p._amazonMatched;})){var w={rows:parts,amount:wholeAmt,vendor:r.vendor,date:r.date,whole:true,ix:units.length};wholeOf.set(split,w);units.push(w);}
      else wholeOf.set(split,null);
    }
    units.push({rows:[r],amount:r.amount,vendor:r.vendor,date:r.date,whole:false,orderId:split,ix:units.length});
  });
  var pairs=[];
  units.forEach(function(u){
    var c=cents(u.amount);if(!c||!u.date)return;
    var famU=vendorFamily(u.vendor);
    var src=u.rows[0].source,gk=groupKeyOf(u.rows[0].key);
    // …except a bill / notice email: the tracker's own rule — a bill and its
    // payment receipt a few days apart ARE one charge. Its payment already
    // in Expenses (that email imported before) makes the bill the copy.
    var bill=src==="tracker"&&!!u.rows[0].notice;
    var post=u.rows.some(function(r){return recordRow.has(r);});
    [c-1,c,c+1].forEach(function(k){(byCents.get(k)||[]).forEach(function(e){
      if(post&&handRecord.has(e.id))return;
      var km=keyMatched.get(e.id);
      if(km&&km.has(src)&&!bill)return;
      var sg=stampGroups.get(e.id);var sameKind=sg&&sg.get(src);
      var otherKey=!!sameKind&&!sameKind.has(gk)&&!bill;
      if(otherKey&&src==="amazon")return;
      var days=dayDiff(e.date,u.date);
      if(days>15)return;
      var ev=mv(e);
      var sv=sameVendor(ev||e.desc,u.vendor);
      if(!(days<=5||(sv&&days<=15)))return;
      var certain=!noMerge&&!otherKey&&k===c&&days<=3&&!!famU&&vendorFamily(ev)===famU;
      pairs.push({u:u,e:e,certain:certain,score:days-(sv?100:0)+(k===c?0:0.5)-(certain?1000:0)});
    });});
    // …a hand entry he typed rounded (within max($5, 1.5%), ±14 days): after
    // every ±1¢ pair; the best 3 per row are enough for the one-to-one pick.
    var n0=dayNum(u.date);var near=[];var tol=nearTolCents(c);
    if(handByDay.size&&n0!=null)for(var di=0;di<DAY_ORDER.length;di++){
      var dd=DAY_ORDER[di];
      // nothing on a later day can beat the best 3 found so far (score ≥ 100 + days)
      if(dd<0&&near.length>=3){near.sort(byNearScore);if(near[2].score<=100+Math.abs(dd))break;}
      var dm=handByDay.get(n0+dd);if(!dm)continue;
      var lists=famU?[dm.get(famU),dm.get("")]:Array.from(dm.values());
      for(var li=0;li<lists.length;li++){
        var list=lists[li];if(!list)continue;
        for(var ix=firstAtLeast(list,c-tol);ix<list.length;ix++){
          var e=list[ix];var k=cents(e.amount);if(k>c+tol)break;
          var diff=Math.abs(k-c);
          if(diff<=1||(k>0)!==(c>0))continue;
          if(post&&handRecord.has(e.id))continue;
          var fe=famOfExp(e);
          var both=!!fe&&!!famU;
          if(both?fe!==famU:!shareType(unitTypes(u),expTypes(e)))continue;
          near.push({u:u,e:e,certain:false,near:diff,score:200+Math.abs(dd)-(both?100:0)+diff/100});
        }
      }
    }
    near.sort(byNearScore).slice(0,3).forEach(function(p){pairs.push(p);});
  });
  pairs.sort(function(a,b){return a.score-b.score||(b.u.whole?1:0)-(a.u.whole?1:0)||a.u.ix-b.u.ix||String(a.e.id).localeCompare(String(b.e.id));});
  var takenRow=new Set();
  pairs.forEach(function(p){
    var u=p.u,e=p.e;
    if(u.done||usedExisting.has(e.id))return;
    if(u.rows.some(function(r){return takenRow.has(r);}))return;
    if(u.orderId){var w=wholeOf.get(u.orderId);if(w&&w.done)return;}
    usedExisting.add(e.id);u.done=true;
    var why="Already in Expenses as "+e.id+" "+describeExp(e)+(u.whole?" — the whole order":"");
    u.rows.forEach(function(r){
      takenRow.add(r);
      if(p.certain&&!r.forced){dropRow(r,"existing",why,null,[e.id]);addBackfill(e.id,r);return;}
      if(p.certain)included.push(leftOutEntry(r,"existing",why,true));
      r._dupExisting=true;addFlag(r,p.near?nearText(e,p.near,u.whole):u.whole?"Possible duplicate of "+e.id+" "+describeExp(e)+" (the whole order) — keep or delete":dupText(e));
    });
  });

  // ── Software he already logs by hand (Reece, 10/9/26: "Keep my recurring
  // entries") ── the vendors of his HAND-ENTERED Software/Subscriptions rows
  // (the vendor field, or the one the description names): a Software /
  // Subscriptions row of the upload from one of them is LEFT OUT (listed,
  // with Include anyway — then flagged). After rule (a), so an exact copy is
  // still "already in Expenses" and hands that expense its receipt file.
  // Software vendors he doesn't log yet come in as usual. Never at Add
  // (noMerge): exactly the ticked rows come in.
  // A left-out charge's receipt file (its tracker PDF) isn't thrown away: it
  // goes on his hand entry for that charge — the nearest one of that vendor
  // (±15 days, a monthly series' own month) with no receipt stored, one file
  // each — and the left-out entry says which (`existIds` → attachExisting,
  // like an exact copy's). None that close → the receipts panel says the
  // file isn't attached (Include anyway brings the charge in with it).
  // (handSoft — vendor family → his hand-entered Software/Subscriptions rows —
  // is handSoftwareFamilies, above; a vendor whose invoices he chose
  // (replacesHand) isn't left out.)
  if(!noMerge){
    var softFiles=[];
    if(handSoft.size)act.forEach(function(r){
      if(dropped(r)||r.replacesHand||r.cat!=="Software/Subscriptions")return;
      var hl=handSoft.get(vendorFamily(r.vendor));if(!hl)return;
      var n=hl.length;
      var by="You log "+(oneLine(r.vendor)||"this vendor")+" by hand ("+n+" "+(n===1?"entry":"entries")+" in Software/Subscriptions)";
      var why=by+" — left out so it isn't counted twice";
      if(!r.forced){dropRow(r,"handLogged",why);if(r.fileRef)softFiles.push({r:r,hand:hl});return;}
      included.push(leftOutEntry(r,"handLogged",why,true));
      var t=by+" — keep this only if it isn't one of those entries";
      addFlag(r,t);
      if(r.baseFlags&&r.baseFlags.indexOf(t)<0)r.baseFlags.push(t);
    });
    var fp=[];
    softFiles.forEach(function(x){x.hand.forEach(function(e){
      if(e.receiptPath||usedExisting.has(e.id))return;
      var days=dayDiff(e.date,x.r.date);if(days>15)return;
      fp.push({r:x.r,e:e,score:days*1e6+Math.min(999999,Math.abs(cents(e.amount)-cents(x.r.amount)))});
    });});
    fp.sort(function(a,b){return a.score-b.score||String(a.e.id).localeCompare(String(b.e.id))||String(a.r.key).localeCompare(String(b.r.key));});
    var fileDone=new Set(),handGot=new Set();
    fp.forEach(function(p){
      if(fileDone.has(p.r)||handGot.has(p.e.id))return;
      fileDone.add(p.r);handGot.add(p.e.id);
      p.r._drop.existIds=[p.e.id];
      p.r._drop.why+="; its receipt goes on your entry "+p.e.id+" ("+[p.e.date,fmtMoney(p.e.amount)].filter(Boolean).join(" · ")+")";
    });
  }
  // The choice (10/10/26, later): one offer per vendor he logs by hand whose
  // charges this upload brings — "Use these invoices instead of my hand
  // entries for <vendor> (<first> to <last>)" — with what each answer does:
  // its invoices (copies kept once — worked out here too while it isn't
  // chosen) and the hand rows they'd replace (invoiceFamilyReplaced).
  // Rows the plan settled otherwise (an exact copy of an expense already
  // there, a receipt attached to its email) aren't counted.
  var invoiceOffers=[];
  if(!noMerge&&handSoft.size){
    var offerBy=new Map();
    act.forEach(function(r){
      if(r.cat!=="Software/Subscriptions")return;
      var fam=r.replacesHand||vendorFamily(r.vendor);
      if(!fam||!handSoft.has(fam))return;
      var d=r._drop;
      if(d&&d.reason!=="handLogged"&&d.reason!=="sameInvoice")return;
      var o=offerBy.get(fam);if(!o){o={family:fam,vendor:oneLine(r.vendor)||fam,on:false,rows:[],copies:0};offerBy.set(fam,o);}
      if(r.replacesHand)o.on=true;
      if(d&&d.reason==="sameInvoice"){o.copies++;return;}
      o.rows.push(r);
    });
    offerBy.forEach(function(o){
      if(!o.on){var gone=new Set(invoiceCopies(o.rows).map(function(p){return p.copy;}));o.copies+=gone.size;o.rows=o.rows.filter(function(r){return!gone.has(r);});}
      if(!o.rows.length)return;
      var res=invoiceFamilyReplaced(o.family,o.vendor,o.rows,existing);
      var dates=o.rows.map(function(r){return r.date;}).filter(Boolean).sort();
      invoiceOffers.push({family:o.family,vendor:o.vendor,on:o.on,count:o.rows.length,total:round2(o.rows.reduce(function(s,r){return s+(Number(r.amount)||0);},0)),
        from:dates[0]||null,to:dates[dates.length-1]||null,copies:o.copies,
        hand:{count:res.replaced.length,total:round2(res.replaced.reduce(function(s,x){return s+(Number(x.amount)||0);},0)),ids:res.replaced.map(function(x){return x.id;})},
        handLogged:(handSoft.get(o.family)||[]).length,keys:o.rows.map(function(r){return r.key;})});
    });
    invoiceOffers.sort(function(a,b){return String(a.vendor).localeCompare(String(b.vendor))||String(a.family).localeCompare(String(b.family));});
  }

  // (c) duplicates inside this upload — both rows flagged.
  //   • same origin (one sheet, one receipt file): same vendor + amount on
  //     the same day;
  //   • different origins (a receipt and a card export, a tracker email and
  //     a store history…): the rule (a) windows — same vendor (Amazon and
  //     Amazon Business are one vendor) + amount within 15 days, or the same
  //     amount within 5 days when a vendor is missing or neither is one the
  //     VENDORS table knows (two known, different vendors with the same
  //     amount are two purchases). One match per row per other origin,
  //     best first, so a run of same-amount charges pairs up instead of
  //     every row being flagged against every other.
  var knownV=new Map();
  var isKnown=function(r){var k=knownV.get(r);if(k===undefined){k=!blankVendor(r.vendor)&&vendorOf(r.vendor).known;knownV.set(r,k);}return k;};
  var pairText=function(x){return"Possible duplicate of another row in this upload "+describeRow(x)+" — keep or delete";};
  var byAmt=new Map();
  act.forEach(function(r){if(dropped(r))return;var c=cents(r.amount);if(!c)return;var g=byAmt.get(c);if(!g){g=[];byAmt.set(c,g);}g.push(r);});
  var cross=[];
  byAmt.forEach(function(g){
    if(g.length<2)return;
    g.sort(function(a,b){return(dayNum(a.date)||0)-(dayNum(b.date)||0);});
    for(var x=0;x<g.length;x++){
      var a=g[x];
      for(var y=x+1,looked=0;y<g.length&&looked<400;y++,looked++){
        var b=g[y];var d=(dayNum(b.date)||0)-(dayNum(a.date)||0);
        if(d>15)break;
        if(distinctRows(a,b))continue;
        var svRaw=sameVendor(a.vendor,b.vendor);
        if(originOf(a)===originOf(b)){
          if(d===0&&svRaw){addFlag(a,pairText(b));addFlag(b,pairText(a));}
          continue;
        }
        var sv=svRaw&&!blankVendor(a.vendor)&&!blankVendor(b.vendor);
        var anyV=d<=5&&(blankVendor(a.vendor)||blankVendor(b.vendor)||(!isKnown(a)&&!isKnown(b)));
        if((sv&&d<=15)||anyV)cross.push({a:a,b:b,score:d-(sv?100:0)});
      }
    }
  });
  cross.sort(function(p,q){return p.score-q.score;});
  var pairedTo=new Map();
  var hasPair=function(r,o){var s=pairedTo.get(r);return!!(s&&s.has(o));};
  var setPair=function(r,o){var s=pairedTo.get(r);if(!s){s=new Set();pairedTo.set(r,s);}s.add(o);};
  cross.forEach(function(p){
    var oa=originOf(p.a),ob=originOf(p.b);
    if(hasPair(p.a,ob)||hasPair(p.b,oa))return;
    setPair(p.a,ob);setPair(p.b,oa);
    addFlag(p.a,pairText(p.b));addFlag(p.b,pairText(p.a));
  });

  // (h) two different emails / uploaded receipts whose files are byte for
  // byte the same image under different names (one SHA-256): one of them may
  // be the wrong picture — both flagged. Not when the two rows are the same
  // store, the same day, different totals: that is ONE photo of the two
  // receipts of one store visit (10/10/26 — the invoice export's Fry's 4/17
  // photo shows the $43.75 receipt and the $140.40 one side by side, a
  // minute apart; both rows are right). (An Amazon email's file copied onto
  // its order's rows, or a split row's parts, share one file legitimately:
  // same name / same email.)
  var byFile=new Map();
  act.forEach(function(r){if(dropped(r)||!r.fileRef||r.source!=="tracker"||!r.fileName)return;var l=byFile.get(r.fileRef);if(!l){l=[];byFile.set(r.fileRef,l);}l.push(r);});
  var wholeC=function(r){return cents(r.splitOf&&r.splitTotal!=null?r.splitTotal:r.amount);};
  var oneVisit=function(a,b){var f=vendorFamily(a.vendor);return!!f&&f===vendorFamily(b.vendor)&&!!a.date&&a.date===b.date&&wholeC(a)!==wholeC(b);};
  byFile.forEach(function(l){
    if(l.length<2)return;
    l.forEach(function(a){l.forEach(function(b){
      if(a===b||groupKeyOf(a.key)===groupKeyOf(b.key)||String(a.fileName).toLowerCase()===String(b.fileName).toLowerCase()||oneVisit(a,b))return;
      addFlag(a,FLAG.sameImagePrefix+" "+describeRow(b)+" ("+b.fileName+") — check it shows this purchase");
    });});
  });

  // (g) refunds / credits — not one that says what it refunds (`refundOf`:
  // a store return naming its original receipt, read off the return itself
  // — it carries its own "keep both" flag; 10/10/26, the Staples 7/05
  // return of the 6/20 ink)
  live.forEach(function(r){if(!dropped(r)&&!r.ready&&(Number(r.amount)||0)<0&&!r._refundExplained&&!r.refundPair&&!r.refundOf)addFlag(r,FLAG.refund);});

  // The rows dropped above → `excluded` (counted, listed with Include
  // anyway). A copy of an expense already in the sheet hands its file to
  // that expense when it has none; a row merged into rows that turned out
  // to be in Expenses already says so.
  var existById=new Map();existing.forEach(function(e){existById.set(e.id,e);});
  var wholeListed=new Set();
  live.forEach(function(r){
    var d=r._drop;if(!d)return;
    // A receipt split by category and attached whole is ONE receipt in the
    // list (its key, its total); Include anyway brings every part back.
    if(r.splitOf&&d.reason==="receipt"){
      if(wholeListed.has(r.splitOf))return;wholeListed.add(r.splitOf);
      r=Object.assign({},r,{key:r.splitOf,amount:r.splitTotal,desc:r.splitDesc||r.desc});
    }
    skipped[d.reason]=(skipped[d.reason]||0)+1;
    var why=d.why;
    if(d.into&&d.into.length&&d.into.every(function(t){return t._drop&&t._drop.reason==="existing";})){
      why+=" (that row is already in Expenses as "+uniq([].concat.apply([],d.into.map(function(t){return t._drop.existIds||[];}))).join(", ")+")";
    }else if(d.into&&d.into.length&&d.into.every(function(t){return t._drop&&t._drop.reason==="handLogged";})){
      why+=" (that row is left out too — you log it by hand)";
    }
    if((d.reason==="existing"||d.reason==="handLogged")&&r.fileRef&&d.existIds)addAttach(r.fileRef,r.fileName,d.existIds.filter(function(id){var e=existById.get(id);return e&&!e.receiptPath;}));
    excluded.push(leftOutEntry(r,d.reason,why));
  });
  live=live.filter(function(r){return!r._drop;});
  // Left out by an earlier import (an Amazon email whose order was left out
  // or cancelled — importDecisions) and not explained by this drop (the
  // tracker on its own): held back, unticked and flagged — never re-added.
  live.forEach(function(r){
    var tv=r._leftBefore;if(!tv||r.on===false)return;
    r.on=false;leftOutBefore++;
    addFlag(r,FLAG.leftBeforePrefix+" ("+[tv.day,tv.leftOut].filter(Boolean).join(" — ")+") — it's unticked; tick it only if it belongs in Expenses");
  });

  // ── Item detail for expenses already in the sheet (plan.backfill) ──
  // An expense this upload matches — imported before (by each of its stamps
  // in turn, its own first: the candidates with exactly its keys, else, when
  // it is the group's only expense, the whole group), an exact copy skipped
  // above, an Amazon charge these orders make up, a receipt for it — and
  // that has no Items line gets one from the upload's LINE items (an Amazon
  // order's lines, a receipt's): the first of those that totals its amount
  // to the cent. A one-item subject / description says nothing the row
  // doesn't already say (an Amazon order's merged email has none — its
  // receipt or order lines do), and a total that doesn't match (an amount he
  // edited, a split he changed) is left alone. → [{id, line}], applied by
  // applyItemBackfill at import.
  var backfill=[];
  if(!noMerge){
    var waysOf=new Map();   // id → candidate lists that could itemize it, best first ({list, byKey: matched by its import key})
    var addWay=function(id,list,byKey){if(!list||!list.length)return;var l=waysOf.get(id);if(!l){l=[];waysOf.set(id,l);}l.push({list:list,byKey:!!byKey});};
    if(haveCands.size)existing.forEach(function(e){
      var keys=importKeysOf(e.notes);var doneG=new Set();
      keys.forEach(function(k){
        var gk=groupKeyOf(k);if(doneG.has(gk))return;doneG.add(gk);
        var cs=haveCands.get(gk);if(!cs)return;
        cs=cs.map(function(c){return haveResolved.get(c)||c;});   // a refund: its order's lines
        var own=cs.filter(function(c){return keys.indexOf(c.key)>=0;});
        addWay(e.id,own,true);
        if(own.length<cs.length&&(byGroup.get(gk)||[]).length===1)addWay(e.id,cs,true);
      });
    });
    bfSrc.forEach(function(l,id){addWay(id,l,false);});
    waysOf.forEach(function(ways,id){
      var e=existById.get(id);
      if(!e)return;
      // A one-part line with no count — the subcategory he picked in the
      // Add / Edit form ("Items: Envelopes $60.63") — takes the receipt's
      // detail (its qty / count) when the receipt says the SAME type. An
      // imported row's line (matched by its import key) takes the upload's
      // product detail — the kind of paper / envelope, a count — only when
      // the upload has exactly its types at exactly its per-type amounts
      // (detailUpgrade). Any other line is left exactly as it is.
      var has=hasItemsLine(e.notes);
      var mine=has?pickedLineType(e):null;
      if(has&&!mine&&!ways.some(function(w){return w.byKey;}))return;
      for(var w=0;w<ways.length;w++){
        var srcs=ways[w].list;
        if(srcs.some(function(s){return!(s.itemsFrom==="lines"&&s.items&&s.items.length)&&!(s.itemsFrom==="ready"&&s.readyParts&&s.readyParts.length);}))continue;
        var sum=srcs.reduce(function(s,r){return s+cents(r.amount);},0);
        if(Math.abs(sum-cents(e.amount))>1)continue;
        var lists=srcs.map(function(r){return r.itemsFrom==="ready"?r.readyParts:itemParts(r.items,r.amount);});
        if(lists.some(function(p){return!p.length;}))continue;
        var parts=mergeItemParts(lists,e.amount);
        var line=formatItemsLine(parts);
        if(!line)continue;
        if(!has){backfill.push({id:id,line:line});return;}
        if(mine&&parts.every(function(p){return p.type===mine;})&&parts.some(function(p){return p.qty!=null||p.units!=null;})){backfill.push({id:id,line:line,replace:mine});return;}
        if(ways[w].byKey){var up=detailUpgrade(e,parts);if(up){backfill.push({id:id,line:up.line,replace:DETAIL_REPLACE,was:up.was});return;}}
      }
    });
    backfill.sort(function(a,b){return String(a.id).localeCompare(String(b.id));});
  }

  live.forEach(function(r){Object.keys(r).forEach(function(k){if(k.charAt(0)==="_")delete r[k];});});
  live.sort(function(a,b){return(b.date||"").localeCompare(a.date||"")||(b.at||0)-(a.at||0)||String(a.key).localeCompare(String(b.key));});
  var stats=summarizeRows(live);
  stats.candidates=(candidates||[]).length;
  // The Amazon orders that never come in (cancelled, left out as personal):
  // finalizeImport plans the ticked rows again WITH them as context, so an
  // email included anyway still reads "…that was cancelled" / "…left out as
  // personal items" at Add (they're never imported — exclude stays set).
  var amazonContext=cancelled.concat(personalLeft);
  return{rows:live,skipped:skipped,excluded:excluded,included:included,attachExisting:attachExisting,stats:stats,stale:stale,deletedBefore:deletedBefore,leftOutBefore:leftOutBefore,amazonContext:amazonContext,backfill:backfill,
    replaced:replaced,postageSpan:spanOut(pr.span),mailhouseSpan:spanOut(pr.mailSpan),invoiceSpans:pr.invoiceSpans||[],invoiceOffers:invoiceOffers};
}

// The rows Reece left ticked → the rows to build, with every pairing flag
// worked out again among THOSE rows only: a "delete this copy" (or "possible
// duplicate of another row in this upload") whose counterpart he unticked
// would otherwise point at a row that never arrives. Category and tick stay
// as he left them in the preview. Nothing is left out or merged here
// (noMerge): exactly the ticked rows are built, and a would-be merge among
// them is flagged instead. → {rows, stale, attach, backfill, replaced} — stale = the
// plan's Amazon changes (they don't depend on ticks) + the receipt re-read
// placeholders of the files actually being imported; attach = uploads of
// the left-out rows' receipt files onto expenses ALREADY in the sheet
// (shaped like existingAttachUploads); backfill = the plan's Items lines
// for expenses already there (they don't depend on ticks either); replaced
// = the hand-entered postage rows the TICKED receipts replace — and the
// software hand rows the ticked invoices he chose replace (their rows carry
// `replacesHand`) (→ applyReplaced).
function finalizeImport(plan,picked,existingExpenses,opts){
  var list=(picked||[]).filter(function(r){return r&&r.on!==false;});
  var keys=new Set(list.map(function(r){return r.key;}));
  // A candidate's own pairing flags (a bill vs its receipt) go when none of
  // the rows they're about is coming in.
  // (A flag with a `flagAlt` says what's true without them — a bill whose
  // payment email he unticked is the only record of that charge.)
  var stillPaired=function(r){return function(f){var p=r.flagPartners&&r.flagPartners[f];return!p||p.some(function(k){return keys.has(k);});};};
  var cands=list.map(function(r){
    var ok=stillPaired(r);
    var flags=uniq((r.baseFlags||r.flags||[]).map(function(f){return ok(f)?f:(r.flagAlt&&r.flagAlt[f])||null;}));
    return Object.assign({},r,{flags:flags,exclude:null,pickedCat:r.cat});
  });
  // The cancelled / personal Amazon orders the plan left out ride along as
  // context only (still excluded — never built): without them an email
  // included anyway for orders that were cancelled, or left out as personal
  // items, would lose that flag at Add and come in clean.
  var ctx=((plan&&plan.amazonContext)||[]).filter(function(c){return c&&c.exclude&&!keys.has(c.key);});
  var p2=planImport(cands.concat(ctx),existingExpenses,Object.assign({},opts||{},{deletedKeys:null,include:null,noMerge:true}));
  var rows=p2.rows.map(function(r){var o=Object.assign({},r,{cat:r.pickedCat||r.cat,on:true});delete o.pickedCat;return o;});
  // The plan's changes to rows ALREADY in the sheet (a ⚑ for a changed
  // Amazon order, a receipt, an Items line) were worked out on the list the
  // preview had. Add passes the list as the SERVER holds it now: a row
  // deleted on another device since is gone from it, and nothing may write
  // to it (the autosave's upsert would bring it back).
  var still=Array.isArray(existingExpenses)?new Set(existingExpenses.map(function(e){return e&&e.id;})):null;
  var there=function(id){return!still||still.has(id);};
  var stale=((plan&&plan.stale)||[]).filter(function(s){return s.kind!=="reread";}).concat((p2.stale||[]).filter(function(s){return s.kind==="reread";}))
    .map(function(s){var ids=(s.ids||[]).filter(there);return ids.length===(s.ids||[]).length?s:Object.assign({},s,{ids:ids});}).filter(function(s){return(s.ids||[]).length;});
  var attach=((plan&&plan.attachExisting)||[]).map(function(a){return a&&a.ids?Object.assign({},a,{ids:a.ids.filter(there)}):a;}).filter(function(a){return a&&a.fileRef&&a.ids&&a.ids.length;}).map(function(a){
    var name=receiptDisplayName(a.name);
    return{fileRef:a.fileRef,name:name,path:expenseReceiptPath(a.ids[0],safeFileName(a.name)),ids:[],existingIds:a.ids.slice()};
  });
  var backfill=((plan&&plan.backfill)||[]).filter(function(b){return b&&b.id&&b.line&&there(b.id);});
  // The hand-entered postage rows to flag: worked out again from the TICKED
  // rows (untick every USPS row and nothing is replaced).
  return{rows:rows,stale:stale,attach:attach,backfill:backfill,replaced:p2.replaced||[]};
}
// Apply plan.replaced to the expenses already in the sheet: each named row
// gets its "⚑ Review: Replaced by the imported USPS/Stamps.com receipts …"
// line (one targeted notes change; a row already carrying one is left as
// it is). Every other row is returned as it was (same object).
function applyReplaced(expenses,replaced){
  var by=new Map();
  (replaced||[]).forEach(function(x){if(x&&x.id&&x.flag&&!by.has(x.id))by.set(x.id,x.flag);});
  if(!by.size)return expenses;
  return(expenses||[]).map(function(e){
    var f=e&&by.get(e.id);
    if(!f||reviewFlagsOf(e).some(isReplacedFlag))return e;
    return Object.assign({},e,{notes:withReviewFlags(e.notes,[f])});
  });
}
// Apply plan.backfill to the expenses already in the sheet: each named row
// that STILL has no Items line gets it (under its ⚑ lines); nothing else on
// it changes, and every other row is returned as it was (same object) — one
// targeted row update each, like applyStale / attachExistingReceipts.
function applyItemBackfill(expenses,backfill){
  var by=new Map();
  (backfill||[]).forEach(function(b){if(b&&b.id&&b.line&&!by.has(b.id))by.set(b.id,b);});
  if(!by.size)return expenses;
  return(expenses||[]).map(function(e){
    var b=e&&by.get(e.id);
    if(!b)return e;
    // DETAIL_REPLACE (product detail, 10/10/26): the line the plan read is
    // replaced only while it is still exactly that line (he may have
    // edited it since the preview).
    if(b.replace===DETAIL_REPLACE)return itemsLineText(e.notes)===b.was?Object.assign({},e,{notes:replaceItemsLine(e.notes,b.line)}):e;
    // `replace` (a type): his one-part line of that type gets the detail —
    // only while it is still exactly that (he may have changed it since).
    if(hasItemsLine(e.notes))return b.replace&&pickedLineType(e)===b.replace?Object.assign({},e,{notes:replaceItemsLine(e.notes,b.line)}):e;
    return Object.assign({},e,{notes:withItemsLine(e.notes,b.line)});
  });
}
// ── Product detail for rows already imported (10/10/26) ──
// Reece imported the ready file before Items lines said WHICH paper /
// envelopes ("Paper $53.98 (2)"); the same upload again (the ready file v3,
// or the original files) knows ("Paper [Cotton (25–100%)] $53.98 (2 · 200
// sheets)"). A row matched by its import key gets the new line ONLY when it
// is the same purchase split the same way: exactly the same item types at
// exactly the same per-type amounts (to the cent), every kind / count its
// line already names unchanged, and the upload adds a kind or a count. A
// line that differs in types or amounts (an edit, an amount change) is
// never touched. → {line, was} or null.
// (10/10/26, later) The brand — Southworth or not — is product detail too:
// a line whose only news is "[… · Southworth]" / "[… · Non-Southworth]" is
// upgraded (each kind's dollars the same to the cent, however the upload
// splits a kind between brands); brands it already names stay as they are.
var DETAIL_REPLACE="product detail";
function itemsLineText(notes){
  var l=null;String(notes||"").split("\n").some(function(x){if(/^\s*items?\s*:/i.test(x)){l=x.trim();return true;}return false;});
  return l;
}
function detailUpgrade(e,parts){
  var was=itemsLineText(e&&e.notes);if(!was)return null;
  var old=parseItemsLine(was);
  if(!old.length||!parts||!parts.length||old.some(function(p){return p.amount==null;}))return null;
  var amtC=cents(e.amount);
  var sumOld=old.reduce(function(s,p){return s+cents(p.amount);},0);
  var sumNew=parts.reduce(function(s,p){return s+p.cents;},0);
  if(sumOld!==amtC||sumNew!==amtC)return null;
  var byType=function(list,cOf){var m=new Map();list.forEach(function(p){var t=m.get(p.type);if(!t){t={cents:0,parts:[]};m.set(p.type,t);}t.cents+=cOf(p);t.parts.push(p);});return m;};
  var O=byType(old,function(p){return cents(p.amount);}),N=byType(parts,function(p){return p.cents;});
  if(O.size!==N.size)return null;
  var added=0;var ok=true;
  O.forEach(function(o,type){
    if(!ok)return;
    var n=N.get(type);
    if(!n||n.cents!==o.cents){ok=false;return;}
    // kinds he already has stay exactly as they are (each kind's dollars to
    // the cent — the upload may split a kind between Southworth and not)…
    var key=function(list,kOf,cOf){var m={};list.forEach(function(p){var k=kOf(p);m[k]=(m[k]||0)+cOf(p);});return Object.keys(m).sort().map(function(k){return k+"="+m[k];}).join("|");};
    var oc=function(p){return cents(p.amount);},nc=function(p){return p.cents;};
    var kind=function(p){return p.variety||"";},brand=function(p){return p.brand||"";},kindBrand=function(p){return(p.variety||"")+" · "+(p.brand||"");};
    var hadKinds=o.parts.some(function(p){return!!p.variety;});
    if(hadKinds){
      if(key(o.parts,kind,oc)!==key(n.parts,kind,nc)){ok=false;return;}
    }else if(n.parts.some(function(p){return!!p.variety;}))added++;
    // …and so do the brands he already has (per kind when it named kinds);
    // a brand is news on its own (10/10/26, later: "[Cotton (25–100%)]" →
    // "[Cotton (25–100%) · Southworth]")
    if(o.parts.some(function(p){return!!p.brand;})){
      var bOf=hadKinds?kindBrand:brand;
      if(key(o.parts,bOf,oc)!==key(n.parts,bOf,nc)){ok=false;return;}
    }else if(n.parts.some(function(p){return!!p.brand;}))added++;
    // a count / qty he already has must be the same in total
    var tot=function(list,f){var s=0;for(var i=0;i<list.length;i++){if(list[i][f]==null)return null;s+=list[i][f];}return s;};
    ["qty","units"].forEach(function(f){
      var a=tot(o.parts,f),b=tot(n.parts,f);
      if(a!=null&&b!==a)ok=false;
      else if(a==null&&b!=null)added++;
    });
  });
  if(!ok||!added)return null;
  var line=formatItemsLine(parts);
  return line&&line!==was?{line:line,was:was}:null;
}
// The type of a one-part Items line for the whole amount with no count —
// what the Add / Edit form writes for a picked subcategory ("Items:
// Envelopes $60.63") — else null.
function pickedLineType(e){
  var p=parseItemsLine(e&&e.notes);
  if(p.length!==1||p[0].amount==null||p[0].qty!=null||p[0].units!=null)return null;
  return cents(p[0].amount)===cents(e.amount)?p[0].type:null;
}
// Apply planImport's `stale` to the expenses already in the sheet: each
// named row gets its ⚑ line, and its stamp is updated so the next run
// doesn't flag it again (an Amazon stamp records the new total /
// "cancelled"; a re-read placeholder's key becomes "…:unread-replaced").
// Every other row is returned as it was (same object).
function applyStale(expenses,stale){
  var by=new Map();
  (stale||[]).forEach(function(s){(s.ids||[]).forEach(function(id){var l=by.get(id);if(!l){l=[];by.set(id,l);}l.push(s);});});
  if(!by.size)return expenses;
  return(expenses||[]).map(function(e){
    var list=e&&by.get(e.id);if(!list)return e;
    var notes=e.notes||"";
    list.forEach(function(s){
      if(s.kind==="reread")notes=rewriteStamps(notes,function(p){return/^receipt:[^:]+:unread$/.test(p.key);},function(p){return{label:p.label,key:p.key+"-replaced"};});
      else if(s.orderId)notes=rewriteStamps(notes,function(p){return isAmazonKeyOf(p.key,s.orderId);},function(p){return{label:SOURCE_LABELS.amazon+(s.detail?" ("+s.detail+")":""),key:p.key};});
      notes=withReviewFlags(notes,[s.flag]);
    });
    return notes===(e.notes||"")?e:Object.assign({},e,{notes:notes});
  });
}

// ── Rows → expenses ──────────────────────────────────────────

// A receipt file's name as he sees it (the row's receiptName, the flash).
function receiptDisplayName(name){return String(name||"receipt").replace(/[\\/]+/g,"_").replace(/\s+/g," ").trim().slice(0,150)||"receipt";}
// …and the name its Storage object gets (receipts/<id>/<this>): lib.js
// safeStorageName — only characters Supabase Storage takes in a key, so a
// "Your eFax® invoice.pdf" or "Here’s your Staples receipt.pdf" is stored
// (it used to fail with "Invalid key" every time, re-attach included).
function safeFileName(name){return safeStorageName(name);}
// The ticked rows → {expenses, uploads}. Ids come from nextExpenseId like
// the rest of Expenses (EXP- Reece, EXM- Max); owner = who is signed in.
// notes = ⚑ flags + Items line + split note + the source stamp. uploads lists each
// receipt file once — receipts/<first expense id>/<name> — with every
// expense that should point at it (several receipts in one PDF share it).
// Set receiptPath on those ids only after the upload succeeds. An upload's
// existingIds are expenses ALREADY in the sheet that should point at it too
// (the kept copy of an Amazon purchase that has no receipt stored) — see
// attachExistingReceipts.
// A row that absorbed merged ones (mergedFrom) gets a stamp line for each
// — "Imported from invoice tracker — merged into this expense · tracker:…"
// — so a re-run knows those emails / receipt files are already in.
function mergedStampLine(m){
  var label=(SOURCE_LABELS[m.source]||String(m.source||"import"))+(m.detail&&(m.source==="receipt"||m.source==="sheet")?" "+m.detail:"")+" — merged into this expense";
  return STAMP_START+cleanLabel(label)+STAMP_SEP+oneLine(m.key);
}
// A candidate's Items line ("" for none): only from items it can vouch for
// (itemsFrom — an Amazon confirmation's cut-short titles are items, but
// not a line). The preview shows the same line Add writes.
function candidateItemsLine(r){
  if(r&&r.readyParts)return formatItemsLine(r.readyParts);   // a ready file's row: its Items column, as he left it
  return r&&r.itemsFrom&&r.items&&r.items.length?itemsLine(r.items,r.amount):"";
}
function buildExpenses(rows,existingExpenses,staff){
  var who=staff==="max"?"max":"reece";
  var list=(rows||[]).filter(function(r){return r&&r.on!==false;});
  var out=[];var uploads=[];var upBy=new Map();
  var builtKeys=new Set(list.map(function(r){return r.key;}));
  list.forEach(function(r,i){
    var id=nextExpenseId(existingExpenses||[],i,who);
    var detail=r.source==="receipt"?r.fileName:r.source==="sheet"?r.sheetName:r.source==="amazon"&&r.orderTotal!=null?"order total "+fmtMoney(r.orderTotal):null;
    // A split order's note says the personal part was left out — unless
    // that part is being imported too.
    var note=r.personalKey&&r.splitNoteKept&&!builtKeys.has(r.personalKey)?r.splitNoteKept:r.splitNote;
    // What it went on: "Items: Paper $53.98 (2 · 200 sheets) · …" (summing
    // exactly to the amount) — the top of the comment, under any ⚑ lines.
    var items=candidateItemsLine(r);
    var body=[items,note,stampLine(r.source,r.key,detail)].concat((r.mergedFrom||[]).map(mergedStampLine)).filter(Boolean).join("\n");
    var exp={id:id,date:r.date,desc:r.desc,amount:round2(r.amount),cat:CATEGORIES.indexOf(r.cat)>=0?r.cat:"Other",vendor:r.vendor||"",
      notes:withReviewFlags(body,r.flags),receipt:null,receiptName:null,receiptPath:null,owner:who};
    if(r.fileRef){
      var name=receiptDisplayName(r.fileName);
      exp.receiptName=name;
      var u=upBy.get(r.fileRef);
      if(!u){u={fileRef:r.fileRef,name:name,path:expenseReceiptPath(id,safeFileName(r.fileName)),ids:[],existingIds:[]};upBy.set(r.fileRef,u);uploads.push(u);}
      u.ids.push(id);
      (r.attachExisting||[]).forEach(function(x){if(u.existingIds.indexOf(x)<0)u.existingIds.push(x);});
    }
    out.push(exp);
  });
  return{expenses:out,uploads:uploads};
}
// Point these expenses at an uploaded receipt (after the upload worked).
function withReceiptPath(expenses,ids,path){
  var s=new Set(ids||[]);
  return(expenses||[]).map(function(e){return e&&s.has(e.id)?Object.assign({},e,{receiptPath:path}):e;});
}
// Add imported expenses to the list. Ids were minted from the list as it
// was when the import started; any taken meanwhile are renumbered (the
// receipt keeps its stored path either way).
function mergeImported(prev,added,staff){
  var who=staff==="max"?"max":"reece";
  var p=prev||[];var all=p.concat(added||[]);
  var taken=new Set(p.map(function(e){return e&&e.id;}));var off=0;
  return p.concat((added||[]).map(function(a){
    if(!taken.has(a.id)){taken.add(a.id);return a;}
    var id;do{id=nextExpenseId(all,off++,who);}while(taken.has(id));
    taken.add(id);return Object.assign({},a,{id:id});
  }));
}
function importSummary(expenses){
  var list=expenses||[];
  var total=round2(list.reduce(function(s,e){return s+(Number(e.amount)||0);},0));
  var flagged=list.filter(hasReviewFlags).length;
  return"Imported "+list.length+" expense"+(list.length===1?"":"s")+" ("+fmtMoney(total)+")"+(flagged?" — "+flagged+" flagged for review":"");
}
// plan.excluded / plan.included → the preview's "What was left out /
// merged" groups, in LEFT_OUT order: [{reason, label, canInclude, count,
// total, rows (left out, newest first), included (brought in anyway)}].
// Only groups with something in them.
function leftOutGroups(plan){
  var by={},inc={};
  var push=function(m,x){(m[x.reason]||(m[x.reason]=[])).push(x);};
  ((plan&&plan.excluded)||[]).forEach(function(x){push(by,x);});
  ((plan&&plan.included)||[]).forEach(function(x){push(inc,x);});
  var order=LEFT_OUT.map(function(d){return d.reason;});
  Object.keys(by).concat(Object.keys(inc)).forEach(function(k){if(order.indexOf(k)<0)order.push(k);});
  var newest=function(a,b){return String(b.date||"").localeCompare(String(a.date||""))||String(a.key).localeCompare(String(b.key));};
  var out=[];
  order.forEach(function(k){
    var rows=(by[k]||[]).slice().sort(newest);var ii=(inc[k]||[]).slice().sort(newest);
    if(!rows.length&&!ii.length)return;
    var d=LEFT_OUT_BY[k]||{reason:k,label:k,one:k,many:k};
    out.push({reason:k,label:d.label,one:d.one,many:d.many,money:!!d.money,canInclude:!!d.auto,count:rows.length,
      total:round2(rows.reduce(function(s,x){return s+(Number(x.amount)||0);},0)),rows:rows,included:ii});
  });
  return out;
}
// What an import does, in plain words — the preview's summary and the
// flash after it. o: {count, total, byCategory | marketing, flagged,
// groups (leftOutGroups), inReady (rows of the original files a ready file
// in the same upload decides), alreadyImported, deletedBefore,
// leftOutBefore, backfill (how
// many expenses already there get an Items line), replaced ({count, total}
// of hand-entered postage rows the receipts replace), done (true = after
// the import: "Added …")} → {added, review, replaced, leftOut, itemDetail,
// text}.
function importOutcome(o){
  o=o||{};
  var n=o.count||0;var mk=o.marketing!=null?o.marketing:((o.byCategory||{}).Marketing||0);
  var ex=n+" expense"+(n===1?"":"s");
  var money=" ("+fmtMoney(o.total||0)+(Math.abs(mk)>=0.005?"; Marketing "+fmtMoney(mk):"")+")";
  var added=o.done?"Added "+ex+(n?money:"")+".":n?ex+" will be added"+money+".":"Nothing new will be added.";
  var f=o.flagged||0;
  var review=f?f+" need"+(f===1?"s":"")+" your review"+(o.done?" — "+(f===1?"it's":"they're")+" in the list below":" after import")+".":n?"Nothing needs your review.":"";
  var parts=[];
  (o.groups||[]).forEach(function(g){
    if(!g.count)return;
    parts.push(g.count+" "+(g.count===1?g.one:g.many)+(g.money?" ("+fmtMoney(g.total)+")":""));
  });
  if(o.inReady)parts.push(o.inReady+" already in the ready file (it decides them)");
  if(o.alreadyImported)parts.push(o.alreadyImported+" imported before");
  if(o.deletedBefore)parts.push(o.deletedBefore+" you deleted before (unticked)");
  if(o.leftOutBefore)parts.push(o.leftOutBefore+" left out at an earlier import (unticked)");
  var leftOut=parts.length?"Left out automatically: "+parts.join(", ")+".":"";
  var b=o.backfill||0;
  var itemDetail=b?(o.done?"Added item detail to "+b+" existing expense"+(b===1?"":"s")+".":b+" existing expense"+(b===1?"":"s")+" get"+(b===1?"s":"")+" item detail."):"";
  // o.upgrade: rows already imported whose Items line gets the kind of
  // paper / envelope (and counts) — DETAIL_REPLACE entries of the backfill.
  var u=o.upgrade||0;
  var productDetail=u?(o.done?"Added product detail (paper / envelope types) to "+u+" existing expense"+(u===1?"":"s")+".":u+" existing expense"+(u===1?"":"s")+" get"+(u===1?"s":"")+" product detail (paper / envelope types)."):"";
  // o.replaced: {count, total, mailhouse?: {count, total}, invoices?: {count,
  // total, vendors}} — hand-entered postage (and mailhouse) rows the receipts
  // replace, and software hand rows the invoices he chose replace
  // (replacedSummary).
  var rp=o.replaced||{};var rn=rp.count||0;
  var mh=rp.mailhouse||{};var mn=mh.count||0;
  var iv=rp.invoices||{};var vn=iv.count||0;var pn=rn-mn-vn;
  var pt=round2((rp.total||0)-(mh.total||0)-(iv.total||0));
  var who,by="the receipts";
  if(!vn)who=!mn?rn+" of your hand-entered postage entries ("+fmtMoney(rp.total||0)+")"
    :!pn?mn+" of your hand-entered mailhouse entries ("+fmtMoney(mh.total||0)+")"
    :pn+" of your hand-entered postage entries ("+fmtMoney(pt)+") and "+mn+" mailhouse entr"+(mn===1?"y":"ies")+" ("+fmtMoney(mh.total||0)+")";
  else{
    var vName=iv.vendors&&iv.vendors.length?iv.vendors.join(" / "):"software";
    if(!pn&&!mn){who=vn+" of your hand-entered "+vName+" entries ("+fmtMoney(iv.total||0)+")";by="the invoices";}
    else{
      var bits=[];
      if(pn)bits.push(pn+" of your hand-entered postage entries ("+fmtMoney(pt)+")");
      if(mn)bits.push((pn?mn+" mailhouse entr"+(mn===1?"y":"ies"):mn+" of your hand-entered mailhouse entries")+" ("+fmtMoney(mh.total||0)+")");
      bits.push(vn+" "+vName+" entr"+(vn===1?"y":"ies")+" ("+fmtMoney(iv.total||0)+")");
      who=bits.slice(0,-1).join(", ")+" and "+bits[bits.length-1];by="the receipts and invoices";
    }
  }
  var replaced=!rn?"":o.done?"Flagged "+who+" as replaced by "+by+" — use Select the replaced rows → Delete selected.":
    who+" will be flagged as replaced by "+by+" — after import, use Select the replaced rows → Delete selected.";
  return{added:added,review:review,replaced:replaced,leftOut:leftOut,itemDetail:itemDetail,productDetail:productDetail,text:[added,review,replaced,leftOut,itemDetail,productDetail].filter(Boolean).join(" ")};
}
// ── What's behind one purchase (Expenses' detail panel, 10/10/26) ──
// Reece: "click into each purchase that is listed and then be able to see
// more information on it, such as: date purchased, each transaction, the
// link to the invoices that are included". Read-only — from the row's own
// notes (stamp lines, split note, ⚑ lines) and the other rows of `list`.
// → {handEntered, sources:[{kind, key, merged, label, photo?, …}], orderIds:
// [{id, url}], splitNote, linkNotes (plain "Refund on Amazon order …" /
// "Return of …" lines), siblings:[{ex, why}], refunds:[{ex, why, how}],
// refundOf:[{ex, why, how}], files:[{path, name, ex, own}], flags}
// (10/10/26, review) A refund is tied to its purchase by `refundTargets` —
// the same rule from either side: the same Amazon order; its notes naming
// the row (id, or "(date · … $amount"), or naming the TOTAL of the receipt
// the row was split from (the Staples return names "$347.33", the 6/20
// receipt is split into $252.36 + $94.97 rows); its description's "(bought
// 6/20/26)" at the same store; the purchase's notes naming it. A receipt- or
// day-level tie keeps only the rows holding an item type the refund returns.
// A row that is both a sibling and a refund shows once, under the refunds.
// Keep leaves a "Refund on … / Return of …" flag behind as a plain line.
var AMAZON_ORDER_URL="https://www.amazon.com/gp/your-account/order-details?orderID=";
function amazonOrderUrl(id){return AMAZON_ORDER_URL+encodeURIComponent(String(id||""));}
// A tracker file "invoices/2026-09-29_staplesreceipt_here-s-your-staples-
// receipt_6835eafba3.pdf" → {date, sender, subject} (the subject as the
// file name spells it: dashes for spaces, punctuation dropped). A receipt
// PHOTO he uploaded is named date_what ("2026-09-28_target-copy-paper-
// receipt-photo.jpg" — no sender): its date + what it says, `photo` true
// (an image file, or a name that says receipt-photo / uploaded) — it is a
// receipt photo, never an "invoice email".
var PHOTO_FILE_RE=/\.(jpe?g|png|heic|heif|webp|gif)$/i;
function trackerFileInfo(path){
  var f=baseName(String(path||"").replace(/#\d+$/,""));
  var photo=PHOTO_FILE_RE.test(f)||/receipt[-_ ]?photo|uploaded/i.test(f);
  var m=/^(\d{4}-\d{2}-\d{2})_(?:([^_]+)_)?(.+?)(?:_[0-9a-f]{6,})?\.[a-z0-9]+$/i.exec(f);
  if(!m)return{file:f,date:null,sender:null,subject:null,photo:photo};
  var subj=m[3].replace(/-/g," ").replace(/(\w) s\b/g,"$1's").replace(/\s+/g," ").trim();
  return{file:f,date:m[1],sender:m[2]?m[2].replace(/-/g," "):null,subject:subj?subj.charAt(0).toUpperCase()+subj.slice(1):null,photo:photo};
}
function describeStamp(p){
  var merged=/merged into this expense/i.test(p.label||"");
  var key=p.key||"";var out={kind:p.source,key:key,merged:merged,group:key?groupKeyOf(key):null};
  if(p.source==="amazon"){
    var m=/^amazon:([^:#]+)(?::([^#]+))?/.exec(key);
    var tot=/order total (-?\$[\d,]+\.\d{2})/i.exec(p.label||"");
    out.orderId=m?m[1]:null;out.part=m&&m[2]?m[2]:null;out.orderTotal=tot?parseMoney(tot[1]):null;out.cancelled=/order cancelled/i.test(p.label||"");
    out.url=out.orderId?amazonOrderUrl(out.orderId):null;
    out.label="Amazon order "+(out.orderId||"")+(out.part?" — its "+out.part+" part":"");
  }else if(p.source==="tracker"){
    var path=key.replace(/^tracker:/,"");var part=null;var i=path.indexOf(TRACKER_PART_SEP);
    if(i>0){part=path.slice(i+TRACKER_PART_SEP.length);path=path.slice(0,i);}
    var t=trackerFileInfo(path);
    Object.assign(out,{file:t.file,date:t.date,sender:t.sender,subject:t.subject,part:part,photo:t.photo});
    out.label=(t.photo?"Receipt photo":"Invoice email")+(t.date?" "+t.date:"")+(t.subject?(t.photo?" — "+t.subject:" — “"+t.subject+"”"):" — "+t.file)+(part?" (its "+part+" part)":"");
  }else if(p.source==="receipt"){
    var rn=String(p.label||"").replace(/\s*—\s*merged into this expense\s*$/i,"").replace(/^receipt\s*/i,"").trim();
    out.file=rn||null;out.unread=/:unread/.test(key);
    out.label="Receipt file"+(rn?" "+rn:"")+(out.unread?" (pages that couldn't be read)":"");
  }else if(p.source==="sheet"){
    var sn=String(p.label||"").replace(/\s*—\s*merged into this expense\s*$/i,"").replace(/^spreadsheet\s*/i,"").trim();
    out.file=sn||null;out.label="Spreadsheet row"+(sn?" — "+sn:"");
  }else out.label=p.label||key;
  return out;
}
function textOf(e){return[e&&e.desc,e&&e.notes].filter(Boolean).join("\n");}
function orderIdsOf(e){
  var ids=[];
  importKeysOf(e).forEach(function(k){var m=/^amazon:([^:#]+)/.exec(k);if(m&&ids.indexOf(m[1])<0)ids.push(m[1]);});
  (textOf(e).match(AMAZON_ORDER_RE)||[]).forEach(function(id){if(ids.indexOf(id)<0)ids.push(id);});
  return ids;
}
// One part of a split order / receipt: the WHOLE's total its split note
// names ("Split from Staples receipt … ($347.33) by category: …") → "$347.33"
// (sign dropped), else null.
function splitTotalTextOf(e){
  var m=/^\s*Split from .*?\((-?\$[\d,]+\.\d{2})\)/m.exec(String((e&&e.notes)||""));
  return m?fmtMoney(Math.abs(parseMoney(m[1])||0)):null;
}
// Does `a`'s text point at expense `b`? → "id" (names b's id), "amount"
// ("(<b's date> · … $<b's amount>"), "receipt" (the same, with the TOTAL of
// the order / receipt b was split from — a store return names the receipt it
// returns, "Return of Staples receipt 65031 (2026-06-20 · $347.33 …", and
// that receipt is split by category into rows of other amounts), else null.
function pointsAt(a,b){
  var t=textOf(a);if(!t||!b)return null;
  if(b.id&&new RegExp("\\b"+String(b.id).replace(/[.*+?^${}()|[\]\\]/g,"\\$&")+"\\b").test(t))return"id";
  if(!b.date)return null;
  var amt=fmtMoney(Math.abs(Number(b.amount)||0));var tot=splitTotalTextOf(b);
  var hit=null;
  t.split("\n").some(function(l){
    // never a split note or a stamp: a file name's date followed by the
    // parts' amounts ("Split from Amazon refund 2026-09-09_….pdf (-$53.18)
    // by category: Marketing -$46.10 + …") names the split's own parts
    if(/^\s*Split from /.test(l)||isStampLine(l))return false;
    var i=l.indexOf(b.date);if(i<0)return false;
    if(l.indexOf(amt,i)>i){hit="amount";return true;}
    if(tot&&l.indexOf(tot,i)>i){hit="receipt";return true;}
    return false;
  });
  return hit;
}
// The purchase date a return / refund's DESCRIPTION names ("Staples —
// Return: CANON GI25 … (bought 6/20/26)") → "2026-06-20", else null. It is
// what's left to tie a return to its purchase once Keep has taken the ⚑ line
// that named the receipt (rows kept before 10/10/26, later).
function boughtDateOf(e){
  var d=String((e&&e.desc)||"");
  if(!/\b(return|refund|credit)/i.test(d))return null;
  var m=/\bbought (?:on )?(\d{4}-\d{2}-\d{2})\b/i.exec(d);if(m)return m[1];
  m=/\bbought (?:on )?(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})\b/i.exec(d);
  if(!m)return null;
  var y=m[3].length===2?"20"+m[3]:m[3];
  return y+"-"+("0"+m[1]).slice(-2)+"-"+("0"+m[2]).slice(-2);
}
// The purchases refund `r` refunds → [{ex, how, id?, total?}] (how: "order"
// — the same Amazon order; "id" / "amount" — r's notes name it; "receipt" —
// r's notes name the receipt it was split from; "bought" — r's description
// names the day it was bought, at the same store; "named" — its own notes
// name r). Order / named ties are the whole order / that row; a receipt- or
// day-level tie keeps only the rows holding an item type r returns, when
// any does (the Staples return of ink → the 6/20 receipt's Marketing part
// with the ink, not its office-supplies part or another receipt that day).
function refundTargets(r,all){
  if(!r||!((Number(r.amount)||0)<0))return[];
  var mine=orderIdsOf(r);var bought=boughtDateOf(r);var vg=bought?expenseVendorGroup(r):null;
  var direct=[],loose=[];
  (all||[]).forEach(function(o){
    if(!o||o===r||(r.id&&o.id===r.id)||!((Number(o.amount)||0)>0))return;
    var oo=orderIdsOf(o);var shared=mine.filter(function(id){return oo.indexOf(id)>=0;});
    if(shared.length){direct.push({ex:o,how:"order",id:shared[0]});return;}
    var p=pointsAt(r,o);
    if(p==="id"||p==="amount"){direct.push({ex:o,how:p});return;}
    if(p==="receipt"){loose.push({ex:o,how:"receipt",total:splitTotalTextOf(o)});return;}
    if(pointsAt(o,r)){direct.push({ex:o,how:"named"});return;}
    if(bought&&o.date===bought&&expenseVendorGroup(o)===vg)loose.push({ex:o,how:"bought"});
  });
  if(loose.length){
    var types={};expenseItemParts(r).forEach(function(p){if(p.type&&p.type!==NO_DETAIL)types[p.type]=true;});
    var holds=function(x){return expenseItemParts(x.ex).some(function(p){return!!types[p.type];});};
    var narrowed=Object.keys(types).length?loose.filter(holds):[];
    if(narrowed.length)loose=narrowed;
  }
  return direct.concat(loose);
}
// Why a tie holds, said from the panel it's shown on (side "purchase": the
// refund listed under its purchase; "refund": the purchase under the refund).
function tieWhy(t,side){
  var p=side==="purchase";
  switch(t.how){
    case"order":return"Amazon order "+t.id;
    case"id":case"amount":return p?"its notes name this purchase":"this refund's notes name it";
    case"receipt":return p?"its notes name the receipt this is part of ("+t.total+")":"part of the receipt this refund's notes name ("+t.total+")";
    case"bought":return p?"returns items bought here that day ("+t.ex.date+")":"bought "+t.ex.date+" at "+expenseVendorGroup(t.ex)+" — the day its description names";
    case"named":return p?"this purchase's notes name it":"its notes name this refund";
    default:return"";
  }
}
// The plain (non-⚑) lines that say what a refund / return refunds —
// "Refund on Amazon order …", "Return of Staples receipt …". The import
// writes them as ⚑ flags; Keep leaves them behind as plain lines
// (keepExpense), so the tie survives the review.
var LINK_LINE_RE=/^(Refund on Amazon orders? \d{3}-\d{7}-\d{7}|Return of )/;
function isRefundLinkLine(l){var s=String(l||"").trim();return!isReviewLine(s)&&LINK_LINE_RE.test(s);}
function purchaseDetail(ex,list){
  var all=(list||[]).filter(function(e){return e&&e.id&&e!==ex&&e.id!==(ex&&ex.id);});
  var lines=String((ex&&ex.notes)||"").split("\n");
  var sources=[];
  lines.forEach(function(l){var p=parseStampLine(l);if(p)sources.push(describeStamp(p));});
  var orders=orderIdsOf(ex).map(function(id){return{id:id,url:amazonOrderUrl(id)};});
  var splitNote=null;lines.some(function(l){var t=l.trim();if(/^Split from /.test(t)){splitNote=t;return true;}return false;});
  var linkNotes=lines.map(function(l){return l.trim();}).filter(isRefundLinkLine);
  // Rows sharing an order / email / receipt file with it: a split's other
  // parts, or the other orders one emailed charge was merged into.
  var groups=new Set();sources.forEach(function(s){if(s.group)groups.add(s.group);});
  var siblings=[];
  if(groups.size)all.forEach(function(o){
    var hit=null;
    importKeysOf(o).some(function(k){var g=groupKeyOf(k);if(groups.has(g)){hit=g;return true;}return false;});
    if(!hit)return;
    var why=/^amazon:/.test(hit)?"same Amazon order":/^tracker:/.test(hit)?"same invoice email":/^receipt:/.test(hit)?"same receipt file":"same spreadsheet row";
    siblings.push({ex:o,why:why});
  });
  // Refunds tied to it (for a refund: what it refunds) — refundTargets, the
  // same rule both ways. A row that is both (a refund on the same email /
  // order) shows ONCE, under the refunds, with both reasons.
  var amt=Number(ex&&ex.amount)||0;
  var refunds=[],refundOf=[];
  if(amt<0)refundTargets(ex,all).forEach(function(t){refundOf.push({ex:t.ex,why:tieWhy(t,"refund"),how:t.how});});
  else if(amt>0)all.forEach(function(o){
    if(!((Number(o.amount)||0)<0))return;
    var t=null;
    refundTargets(o,[ex].concat(all)).some(function(x){if(x.ex===ex||(ex.id&&x.ex.id===ex.id)){t=x;return true;}return false;});
    if(t)refunds.push({ex:o,why:tieWhy(t,"purchase"),how:t.how});
  });
  var tied=refunds.concat(refundOf);
  siblings=siblings.filter(function(s){
    var x=tied.filter(function(y){return y.ex===s.ex;})[0];
    if(!x)return true;
    if(x.why.indexOf(s.why)!==0)x.why=s.why+" · "+x.why;
    return false;
  });
  // The files stored for it (this row's), and the ones the same upload
  // stored for its split parts / merged copies.
  var files=[];var seenP=new Set();
  var addFile=function(e,own){
    if(!e)return;
    var p=e.receiptPath||null;
    if(p&&seenP.has(p))return;if(p)seenP.add(p);
    if(!p&&!own)return;
    if(!p&&!e.receiptName&&!(e.receipt&&e.receipt!=="[stored]"))return;
    files.push({path:p,name:e.receiptName||baseName(p)||"receipt",ex:e,own:!!own,stored:!!p,local:!p&&!!(e.receipt&&e.receipt!=="[stored]")});
  };
  addFile(ex,true);
  siblings.forEach(function(s){addFile(s.ex,false);});
  return{handEntered:!sources.length,sources:sources,orderIds:orders,splitNote:splitNote,linkNotes:linkNotes,siblings:siblings,refunds:refunds,refundOf:refundOf,files:files,flags:reviewFlagsOf(ex)};
}
// plan.backfill / fin.backfill → {items: rows getting an Items line (or a
// count on a picked one), product: rows getting product detail}.
function backfillCounts(backfill){
  var items=0,product=0;
  (backfill||[]).forEach(function(b){if(!b)return;if(b.replace===DETAIL_REPLACE)product++;else items++;});
  return{items:items,product:product};
}

export {
  CATEGORIES, REVIEW_PREFIX, IMPORT_TAG, SOURCE_LABELS, SOURCE_BADGES, VENDORS, FAILED, NOTICE_SOURCE, NOTICE_SUBJECT, SHEET_ACCEPT, RECEIPT_KINDS, LEFT_OUT, REPLACED_PREFIX, REPLACED_KEPT, REPLACED_MAILHOUSE_PREFIX, REPLACED_MAILHOUSE_KEPT, REPLACED_INVOICES_PREFIX, REPLACED_INVOICES_KEPT, READY_HEADER, READY_FLAG, READY_REPLACES, NEAR_DAYS, INVOICE_NEAR_DAYS,
  // notes
  stampLine, parseStampLine, isStampLine, isReviewLine, importKeysOf, groupKeyOf, reviewFlagsOf, hasReviewFlags, withReviewFlags, clearReviewFlags, splitImportNotes, relatedExpenseIds, keepExpense, flaggedExpenses, rewriteStamps, isReplacedFlag, replacedKindOf,
  // hand-entered rows: postage / mailhouse source of truth, hand-logged software, typed rounded
  isHandEntered, matchVendorOf, isPostageRow, isMailhouseRow, isHandPostage, isHandMailhouse, applyReplaced, replacedFor, replacedSummary, nearTolCents,
  // software he logs by hand — or its invoices instead (10/10/26, later)
  handSoftwareFamilies, invoiceFamilyReplaced, invoiceCopies, invoiceVendorOf,
  // the LoanFlow ready file
  readyFileRows, readyManifestRows, readyFileCsv, readyCandidates, attachReadyFiles, matchImportedReceipts, decodeSheetBytes,
  // deleted imports + what Add remembers (this browser, per person)
  readImportTombstones, recordDeletedImports, importDecisions, recordImportDecisions,
  // values + files
  cellValue, parseMoney, parseDate, parseCSV, parseCSVObjects, readXlsxRows, readXlsxSheets, workbookSheetsOf, sheetRowsOf, sharedStringsOf, readSheetFile, sheetKindOf,
  // vendors / categories
  vendorOf, vendorFamily, categorize, looksPersonal, personalService,
  // sheets → candidates
  detectSheet, guessMapping, amazonCandidates, trackerCandidates, genericCandidates, sheetCandidates, workbookCandidates, receiptCandidates, matchTrackerFiles, existingAttachUploads, attachExistingReceipts,
  // item lines from a sheet (the invoice export's Itemized Details)
  itemDetailReceipts, joinItemDetails, applyItemLines, personalLine, TRACKER_PART_SEP, NOT_VISIBLE_FLAG,
  // plan + build
  planImport, finalizeImport, applyStale, applyItemBackfill, candidateItemsLine, summarizeRows, buildExpenses, withReceiptPath, mergeImported, importSummary, leftOutGroups, importOutcome, safeFileName, receiptDisplayName,
  // product detail for rows already imported (10/10/26)
  DETAIL_REPLACE, detailUpgrade, backfillCounts, itemsLineText,
  // one purchase, everything behind it (Expenses' detail panel)
  purchaseDetail, amazonOrderUrl, trackerFileInfo, refundTargets, refundLinkOf, boughtDateOf,
  // small helpers the UI may want
  fmtMoney, hashStr, oneLine, clipWords
};
